import { apiHeaders } from './helpers.js';
import { savePref, applyServerPrefs } from './preferences.js';
import { TILES, HYBRID_LABELS, TILE_ATTRIBUTION, PIN_COLORS, DISC_BADGE, DISC_LETTER, ACC_LABELS } from './constants.js';
import { LayerRegistry } from './layer-registry.js';
import { TextView } from './text-view.js';

const App = {
    // Config from server
    config: window.__APP || { planSlug: 'gratis', planFeatures: [], isAuth: false, isAdmin: false, userName: '' },

    // Data
    incidents: new Map(),
    markers: new Map(),
    iconCache: new Map(),
    cards: new Map(),
    regionData: [],

    // UI state
    selected: null,
    activeTab: 'info',
    lastUpdate: new Date(),
    theme: 'dark',
    tileMode: 'auto',

    // Merge mode (admin only)
    mergeMode: false,
    mergeSelected: new Set(),

    // Filter state
    fDisc: new Set(),
    fEsc: new Set(),
    fPrio: new Set(),
    fRegion: '',
    fLocLat: null,
    fLocLng: null,
    fRadiusKm: 25,
    fHideTest: localStorage.getItem('hideTest') === '1',
    fMajorPin: localStorage.getItem('majorPin') === '1',
    fSound: localStorage.getItem('sound') === '1',
    _soundCtx: null,
    // TTS (voorlezen van nieuwe meldingen) — instelbaar per dienst + prioriteit
    fTts: localStorage.getItem('ttsEnabled') === '1',
    fTtsFull: localStorage.getItem('ttsFull') === '1',
    fTtsDisc: new Set(),
    fTtsPrio: new Set(),
    _ttsQueueCount: 0,
    _ttsVoiceCache: undefined,
    filtersApplied: false,
    mapBoundsFilter: false,

    // Map
    map: null,
    tileLayer: null,
    attribution: null,
    _tileAttribution: null,
    labelsLayer: null,
    vrLayer: null,
    vrVisible: false,
    gemLayer: null,
    gemVisible: false,
    radiusCircle: null,
    radiusCenter: null,

    // Helicopter tracking
    _heliLayer: null,
    _heliMarkers: new Map(),
    _heliVisible: false,

    // Feeds
    feeds: [],
    activeFeedId: null,
    feedIncidents: [],
    feedFollows: [],
    wizStep: 0,
    wizData: {},

    // Timeline
    tlActive: false,
    tlPlaying: false,
    tlInterval: null,
    tlMaxHours: 6,

    // WS
    ws: null,
    reconnectDelay: 1000,
    _lastHeartbeat: 0,
    _heartbeatTimer: null,
    _wsOpenTimeout: null,
    _hasConnectedOnce: false,
    _reconnectTimeout: null,

    // ========================================
    // Init
    // ========================================

    init() {
        applyServerPrefs();
        this._pinIcons = localStorage.getItem('pinIcons') === '1';
        this._pinGrip = localStorage.getItem('pinGrip') === '1';
        this.loadTheme();
        this.initMap();
        this._syncLegendIcons();
        this.initMapControls();
        this.initThemeToggle();
        this.initFilterPanel();
        this.initDetailPanel();
        this.initMobileTabs();
        this.initMergeMode();
        this.initTimeline();
        this.initBoundsFilter();
        this.initTextView();
        this.initFeeds();
        this.loadRegions();
        this.loadInitialIncidents();
        this.connectWs();

        // Update timestamp every second
        setInterval(() => {
            const el = document.getElementById('tsBox');
            if (el) {
                el.textContent = 'Bijgewerkt ' + this.lastUpdate.toLocaleTimeString('nl-NL', {
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                });
            }
        }, 1000);

        // Reconnect immediately when tab becomes visible again (but not after session expiry)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && !this._sessionExpired) {
                const wsAlive = this.ws && this.ws.readyState === WebSocket.OPEN;
                if (!wsAlive) {
                    clearTimeout(this._reconnectTimeout);
                    this.reconnectDelay = 1000;
                    this.connectWs();
                }
            }
        });

        // Refresh na registratie-popup dismiss
        window.addEventListener('app:refresh', () => {
            this.refreshAllIncidents();
        });
    },

    // ========================================
    // Theme
    // ========================================

    loadTheme() {
        this.themeMode = localStorage.getItem('themeMode') || 'auto';
        if (this.themeMode === 'auto') {
            this.theme = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
        } else {
            this.theme = this.themeMode;
        }
        const savedTile = localStorage.getItem('tileMode');
        if (savedTile && ['auto', 'simple', 'satellite', 'hybrid'].includes(savedTile)) {
            this.tileMode = savedTile;
        }
        this.vrVisible = localStorage.getItem('vrVisible') === '1';
        this.applyTheme();
        // Reageer op OS-themawijziging in auto-modus
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (this.themeMode === 'auto') {
                this.theme = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
                this.applyTheme();
            }
        });
    },

    applyTheme() {
        document.documentElement.setAttribute('data-theme', this.theme);
        const btn = document.getElementById('themeToggle');
        if (btn) {
            btn.textContent = this.themeMode === 'dark' ? '\u2600' : this.themeMode === 'light' ? '\u263E' : '\u25D1';
            btn.title = this.themeMode === 'dark' ? 'Thema: donker' : this.themeMode === 'light' ? 'Thema: licht' : 'Thema: automatisch';
        }
        this.applyTileLayer();
    },

    initThemeToggle() {
        const btn = document.getElementById('themeToggle');
        if (btn) {
            btn.addEventListener('click', () => {
                this.themeMode = this.themeMode === 'dark' ? 'light' : this.themeMode === 'light' ? 'auto' : 'dark';
                this.theme = this.themeMode === 'auto'
                    ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
                    : this.themeMode;
                localStorage.setItem('themeMode', this.themeMode);
                savePref('theme', this.themeMode);
                this.applyTheme();
            });
        }
    },

    // ========================================
    // Map
    // ========================================

    initMap() {
        const saved = this.loadMapView();
        this.map = L.map('map', {
            center: saved ? [saved.lat, saved.lng] : [52.15, 5.38],
            zoom: saved ? saved.zoom : 8,
            zoomControl: false,
            attributionControl: false,
        });
        // Zoom controls handled by custom buttons in .mc-br
        this.attribution = L.control.attribution({ prefix: false, position: 'bottomright' }).addTo(this.map);

        let tileKey;
        if (this.tileMode === 'auto') tileKey = this.theme;
        else if (this.tileMode === 'simple') tileKey = this.theme + '-simple';
        else tileKey = this.tileMode;
        this.tileLayer = L.tileLayer(TILES[tileKey], {
            maxZoom: 19, subdomains: 'abcd',
        }).addTo(this.map);
        this.setTileAttribution(tileKey);

        if (tileKey === 'hybrid') {
            this.labelsLayer = L.tileLayer(HYBRID_LABELS, {
                maxZoom: 19, subdomains: 'abcd', pane: 'overlayPane',
            }).addTo(this.map);
        }

        // Persist map view on move/zoom + update bounds filter (debounced)
        let _moveTimer = null;
        this.map.on('moveend', () => {
            this.saveMapView();
            if (this.mapBoundsFilter) {
                clearTimeout(_moveTimer);
                _moveTimer = setTimeout(() => {
                    this.renderAll();
                    this.applyMarkerVisibility();
                }, 150);
            }
        });
    },

    setTileAttribution(tileKey) {
        if (this._tileAttribution) this.attribution.removeAttribution(this._tileAttribution);
        this._tileAttribution = TILE_ATTRIBUTION[tileKey];
        this.attribution.addAttribution(this._tileAttribution);
    },

    saveMapView() {
        if (!this.map) return;
        const c = this.map.getCenter();
        savePref('mapView', JSON.stringify({ lat: c.lat, lng: c.lng, zoom: this.map.getZoom() }));
    },

    loadMapView() {
        try {
            const raw = localStorage.getItem('mapView');
            if (!raw) return null;
            const d = JSON.parse(raw);
            if (typeof d.lat === 'number' && typeof d.lng === 'number' && typeof d.zoom === 'number') return d;
        } catch (e) { /* ignore */ }
        return null;
    },

    applyTileLayer() {
        if (!this.map || !this.tileLayer) return;
        let tileKey;
        if (this.tileMode === 'auto') tileKey = this.theme;
        else if (this.tileMode === 'simple') tileKey = this.theme + '-simple';
        else tileKey = this.tileMode;
        this.tileLayer.setUrl(TILES[tileKey]);
        this.setTileAttribution(tileKey);
        if (tileKey === 'hybrid') {
            if (!this.labelsLayer) {
                this.labelsLayer = L.tileLayer(HYBRID_LABELS, {
                    maxZoom: 19, subdomains: 'abcd', pane: 'overlayPane',
                });
            }
            this.labelsLayer.addTo(this.map);
        } else if (this.labelsLayer) {
            this.labelsLayer.remove();
        }
        document.querySelectorAll('.mc-tile-thumb').forEach(b => {
            b.classList.toggle('active', b.dataset.tile === this.tileMode);
        });
    },

    initMapControls() {
        // --- Tile thumbnail strip (collapsed by default) ---
        this._initTileThumbs();
        const tileStrip = document.getElementById('mcTileStrip');
        document.querySelectorAll('.mc-tile-thumb').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tile === this.tileMode);
            btn.addEventListener('click', () => {
                if (!tileStrip.classList.contains('expanded')) {
                    tileStrip.classList.add('expanded');
                    return;
                }
                this.tileMode = btn.dataset.tile;
                savePref('tileMode', this.tileMode);
                this.applyTileLayer();
                tileStrip.classList.remove('expanded');
            });
        });
        // Close strip when clicking outside
        document.addEventListener('click', e => {
            if (tileStrip && !tileStrip.contains(e.target)) {
                tileStrip.classList.remove('expanded');
            }
        });

        // --- Zoom buttons ---
        const zoomIn = document.getElementById('zoomIn');
        const zoomOut = document.getElementById('zoomOut');
        if (zoomIn) zoomIn.addEventListener('click', () => this.map.zoomIn());
        if (zoomOut) zoomOut.addEventListener('click', () => this.map.zoomOut());

        // --- Zoom to NL button ---
        const nlBtn = document.getElementById('zoomNL');
        if (nlBtn) nlBtn.addEventListener('click', () => this.zoomToNL());

        // --- Locate me button ---
        const locBtn = document.getElementById('locateBtn');
        if (locBtn) {
            locBtn.addEventListener('click', () => {
                if (!navigator.geolocation) { alert('GPS niet beschikbaar'); return; }
                locBtn.style.opacity = '.5';
                navigator.geolocation.getCurrentPosition(pos => {
                    const { latitude, longitude } = pos.coords;
                    this.map.setView([latitude, longitude], 13);
                    if (this._locMarker) {
                        this.map.removeLayer(this._locMarker);
                        this._locMarker = null;
                    }
                    const avatar = this.config.avatarUrl || null;
                    if (avatar) {
                        const safeUrl = String(avatar).replace(/'/g, '%27');
                        const icon = L.divIcon({
                            className: 'self-loc-marker',
                            html: "<span class=\"self-loc-avatar\" style=\"background-image:url('" + safeUrl + "')\" role=\"img\" aria-label=\"Jouw locatie\"></span>",
                            iconSize: [28, 28],
                            iconAnchor: [14, 14],
                        });
                        this._locMarker = L.marker([latitude, longitude], { icon, interactive: false, keyboard: false }).addTo(this.map);
                    } else {
                        this._locMarker = L.circleMarker([latitude, longitude], {
                            radius: 7, color: '#fff', weight: 2,
                            fillColor: '#185FA5', fillOpacity: 1, interactive: false,
                        }).addTo(this.map);
                    }
                    locBtn.style.opacity = '';
                    locBtn.classList.add('active');
                }, () => {
                    alert('Locatie niet beschikbaar');
                    locBtn.style.opacity = '';
                });
            });
        }

        // --- Layers dropdown (bottom-left) ---
        const layersToggle = document.getElementById('layersToggle');
        const layersMenu = document.getElementById('layersMenu');
        if (layersToggle && layersMenu) {
            layersToggle.addEventListener('click', () => layersMenu.classList.toggle('open'));
            document.addEventListener('click', (e) => {
                if (!e.target.closest('.mc-layers')) layersMenu.classList.remove('open');
            });
        }

        // Initialize the layer registry — definitions come from server (window.__APP.mapLayers).
        this._initLayerRegistry();
    },

    /**
     * Build the LayerRegistry from server-provided definitions and wire up type-handlers.
     * Falls back to an empty registry if no definitions are available (UI shows no layers).
     */
    _initLayerRegistry() {
        const container = document.getElementById('layersMenu');
        if (!container) return;

        const definitions = Array.isArray(this.config.mapLayers) ? this.config.mapLayers : [];

        // Per-type render handlers. Each handler receives (def, map, entry) where entry.layerInstance
        // is mutable storage for the loaded Leaflet layer/group.
        const handlers = {
            geojson_url: {
                show: async (def, map, entry) => {
                    if (entry.layerInstance) {
                        entry.layerInstance.addTo(map);
                        return;
                    }
                    if (!def.source_url) return;
                    try {
                        const res = await fetch(def.source_url);
                        const geojson = await res.json();
                        const style = def.style || {};
                        const tooltipField = def.meta?.tooltip_field;
                        entry.layerInstance = L.geoJSON(geojson, {
                            style: () => style,
                            onEachFeature: tooltipField
                                ? (feature, layer) => {
                                    const name = feature.properties?.[tooltipField] || feature.properties?.name || '';
                                    if (name) layer.bindTooltip(name, { sticky: true, className: 'vr-tooltip' });
                                }
                                : undefined,
                        }).addTo(map);
                    } catch (e) {
                        console.error('Failed to load GeoJSON layer', def.slug, e);
                    }
                },
                hide: (_def, _map, entry) => {
                    if (entry.layerInstance) entry.layerInstance.remove();
                },
            },
            wms: {
                show: (def, map, entry) => {
                    if (entry.layerInstance) {
                        entry.layerInstance.addTo(map);
                        return;
                    }
                    if (!def.source_url) return;
                    const opts = Object.assign({
                        format: 'image/png',
                        transparent: true,
                        attribution: def.attribution || '',
                    }, def.style || {});
                    if (def.min_zoom !== null && def.min_zoom !== undefined) opts.minZoom = def.min_zoom;
                    if (def.max_zoom !== null && def.max_zoom !== undefined) opts.maxZoom = def.max_zoom;
                    entry.layerInstance = L.tileLayer.wms(def.source_url, opts).addTo(map);
                },
                hide: (_def, _map, entry) => {
                    if (entry.layerInstance) entry.layerInstance.remove();
                },
            },
            tile_xyz: {
                show: (def, map, entry) => {
                    if (entry.layerInstance) {
                        entry.layerInstance.addTo(map);
                        return;
                    }
                    if (!def.source_url) return;
                    const opts = Object.assign({
                        attribution: def.attribution || '',
                    }, def.style || {});
                    if (def.min_zoom !== null && def.min_zoom !== undefined) opts.minZoom = def.min_zoom;
                    if (def.max_zoom !== null && def.max_zoom !== undefined) opts.maxZoom = def.max_zoom;
                    entry.layerInstance = L.tileLayer(def.source_url, opts).addTo(map);
                },
                hide: (_def, _map, entry) => {
                    if (entry.layerInstance) entry.layerInstance.remove();
                },
            },
            stations: {
                show: (def, _map, _entry) => {
                    const meta = def.meta || {};
                    if (!meta.discipline) return;
                    const legacyKey = meta.legacy_layers_key || meta.discipline.toLowerCase().slice(0, 3);
                    this.loadStationLayer(meta.discipline, legacyKey, meta.marker_icon || '');
                },
                hide: (def, _map, _entry) => {
                    const meta = def.meta || {};
                    const legacyKey = meta.legacy_layers_key || (meta.discipline || '').toLowerCase().slice(0, 3);
                    if (this._stationLayers && this._stationLayers[legacyKey]) {
                        this._stationLayers[legacyKey].remove();
                    }
                },
            },
            nl_alert: {
                show: (def, _map, _entry) => {
                    const meta = def.meta || {};
                    if (!this._nlAlertLoaderStarted) {
                        this._nlAlertLoaderStarted = true;
                        const refresh = (typeof meta.refresh_ms === 'number' && meta.refresh_ms > 10000)
                            ? meta.refresh_ms : 300000;
                        setTimeout(() => {
                            this.loadNlAlertData();
                            setInterval(() => this.loadNlAlertData(), refresh);
                        }, 3000);
                    } else {
                        this.renderNlAlertLayer();
                    }
                },
                hide: (_def, _map, _entry) => {
                    if (this._nlAlertLayer) {
                        this._nlAlertLayer.remove();
                        this._nlAlertLayer = null;
                    }
                },
            },
            helicopters: {
                show: (_def, _map, _entry) => {
                    this._heliVisible = true;
                    this.loadHelicopterLayer();
                },
                hide: (_def, _map, _entry) => {
                    this._heliVisible = false;
                    this.removeHelicopterLayer();
                },
            },
            ndw_situations: {
                show: (def, map, entry) => {
                    if (!entry.layerInstance) {
                        entry.layerInstance = L.layerGroup();
                    }
                    entry.layerInstance.addTo(map);
                    this._loadNdwSituations(def, entry);
                    if (!entry.refreshTimer) {
                        const interval = (def.meta?.refresh_ms && def.meta.refresh_ms > 30000)
                            ? def.meta.refresh_ms : 60000;
                        entry.refreshTimer = setInterval(() => {
                            if (entry.isVisible) this._loadNdwSituations(def, entry);
                        }, interval);
                    }
                },
                hide: (_def, _map, entry) => {
                    if (entry.layerInstance) entry.layerInstance.remove();
                    if (entry.refreshTimer) {
                        clearInterval(entry.refreshTimer);
                        entry.refreshTimer = null;
                    }
                },
            },
            weather_labels: {
                show: (def, map, entry) => {
                    if (!entry.layerInstance) {
                        entry.layerInstance = L.layerGroup();
                    }
                    entry.layerInstance.addTo(map);
                    this._loadWeatherLabels(def, entry);
                    // Andere weather-labels lagen herrenderen zodat de stadsnaam-cascade klopt
                    // (wie de "primary" / bovenste zichtbare laag is bepaalt waar de naam staat).
                    this._refreshOtherWeatherLabels(def.slug);
                    if (!entry.refreshTimer) {
                        const interval = (def.meta?.refresh_ms && def.meta.refresh_ms >= 60000)
                            ? def.meta.refresh_ms : 900000;
                        entry.refreshTimer = setInterval(() => {
                            if (entry.isVisible) this._loadWeatherLabels(def, entry);
                        }, interval);
                    }
                },
                hide: (def, _map, entry) => {
                    if (entry.layerInstance) entry.layerInstance.remove();
                    if (entry.refreshTimer) {
                        clearInterval(entry.refreshTimer);
                        entry.refreshTimer = null;
                    }
                    // Cascade naam naar volgende zichtbare laag.
                    this._refreshOtherWeatherLabels(def.slug);
                },
            },
        };

        this.layerRegistry = new LayerRegistry({
            map: this.map,
            definitions,
            handlers,
        });
        this.layerRegistry.bootstrap(container);

        // Preserve external references used elsewhere in app-core.
        const vrEntry = this.layerRegistry.getEntry('vr');
        this.vrVisible = !!(vrEntry && vrEntry.isVisible);
        const gemEntry = this.layerRegistry.getEntry('gem');
        this.gemVisible = !!(gemEntry && gemEntry.isVisible);
    },

    zoomPadding() {
        const dp = document.getElementById('detailPanel');
        const dpOpen = dp && dp.classList.contains('open');
        const dpW = dpOpen ? (dp.offsetWidth || 380) : 0;
        return { paddingTopLeft: [30, 30], paddingBottomRight: [dpW + 30, 30] };
    },

    zoomToNL() {
        if (this._mapUsable()) this.map.fitBounds([[50.75, 3.35], [53.55, 7.25]], this.zoomPadding());
    },

    _tileName(mode) {
        return { auto: 'Kaart', simple: 'Simpel', satellite: 'Satelliet', hybrid: 'Hybride' }[mode] || 'Kaart';
    },

    _initTileThumbs() {
        // Set tile thumbnail backgrounds (Amsterdam z=10)
        const thumbs = { auto: this.theme, simple: this.theme + '-simple', satellite: 'satellite', hybrid: 'hybrid' };
        Object.entries(thumbs).forEach(([mode, key]) => {
            const img = document.querySelector(`.mc-tile-thumb[data-tile="${mode}"] .mc-tile-img`);
            if (!img) return;
            const url = TILES[key].replace('{z}', '10').replace('{x}', '527').replace('{y}', '340').replace('{r}', '').replace('{s}', 'a');
            img.style.backgroundImage = `url(${url})`;
        });
    },

    async loadVrLayer() {
        if (this.vrLayer) {
            this.vrLayer.addTo(this.map);
            return;
        }
        try {
            const res = await fetch('/data/veiligheidsregios.geojson');
            const geojson = await res.json();
            this.vrLayer = L.geoJSON(geojson, {
                style: {
                    color: '#27ae60',
                    weight: 1.5,
                    opacity: 0.6,
                    fillOpacity: 0.05,
                    fillColor: '#27ae60',
                },
                onEachFeature: (feature, layer) => {
                    const name = feature.properties?.statnaam || feature.properties?.name || '';
                    if (name) layer.bindTooltip(name, { sticky: true, className: 'vr-tooltip' });
                },
            }).addTo(this.map);
        } catch (e) {
            console.error('Failed to load VR geojson:', e);
        }
    },

    async loadGemLayer() {
        if (this.gemLayer) {
            this.gemLayer.addTo(this.map);
            return;
        }
        try {
            const res = await fetch('/data/gemeenten.geojson');
            const geojson = await res.json();
            this.gemLayer = L.geoJSON(geojson, {
                style: {
                    color: '#7f8c8d',
                    weight: 1,
                    opacity: 0.4,
                    fillOpacity: 0.02,
                    fillColor: '#7f8c8d',
                },
                onEachFeature: (feature, layer) => {
                    const name = feature.properties?.statnaam || '';
                    if (name) layer.bindTooltip(name, { sticky: true, className: 'vr-tooltip' });
                },
            }).addTo(this.map);
        } catch (e) {
            console.error('Failed to load gemeente geojson:', e);
        }
    },

    async loadNlAlertData() {
        try {
            const res = await fetch('/api/v1/nl-alert');
            const json = await res.json();
            this._nlAlertData = json.data || [];
            this.renderNlAlertLayer();
        } catch (e) {
            console.error('NL-Alert laden mislukt:', e);
        }
    },

    renderNlAlertLayer() {
        if (this._nlAlertLayer) { this._nlAlertLayer.remove(); this._nlAlertLayer = null; }

        const data = this._nlAlertData || [];
        const now = new Date();
        const active = data.filter(a => {
            const start = new Date(a.start_at);
            const stop = a.stop_at ? new Date(a.stop_at) : null;
            return start <= now && (!stop || stop >= now);
        });

        // Update count badge
        const badge = document.getElementById('nlAlertCount');
        if (badge) {
            if (active.length > 0) { badge.textContent = active.length; badge.style.display = ''; }
            else { badge.style.display = 'none'; }
        }

        const isOn = this.layerRegistry ? this.layerRegistry.isVisible('nl-alert') : false;
        if (!isOn || active.length === 0) return;

        const group = L.layerGroup();
        const NL_ICON_URL = '/img/nl-alert.png';

        active.forEach(alert => {
            const fmtTime = (iso) => {
                const d = new Date(iso);
                return ('0'+d.getDate()).slice(-2)+'-'+('0'+(d.getMonth()+1)).slice(-2)+' '+('0'+d.getHours()).slice(-2)+':'+('0'+d.getMinutes()).slice(-2);
            };
            const popup = `<div style="max-width:300px"><div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;"><img src="${NL_ICON_URL}" width="24" height="24" style="border-radius:4px;"><strong>NL-Alert</strong></div><div style="font-size:11px;color:#666;margin-bottom:6px;">${fmtTime(alert.start_at)}${alert.stop_at ? ' — ' + fmtTime(alert.stop_at) : ''}</div><div style="font-size:12px;white-space:pre-line;">${this.esc(alert.message)}</div></div>`;

            (alert.area || []).forEach(areaStr => {
                // Format: "lat,lng lat,lng lat,lng ..."
                const coords = areaStr.split(' ').map(p => { const c = p.split(','); return [parseFloat(c[0]), parseFloat(c[1])]; });
                if (coords.length < 3) return;

                const polygon = L.polygon(coords, {
                    color: '#dc2626', weight: 2.5, opacity: 0.8,
                    fillColor: '#dc2626', fillOpacity: 0.12,
                    className: 'nlalert-area',
                });
                polygon.bindPopup(popup, { maxWidth: 350 });
                group.addLayer(polygon);

                // Marker in center
                const center = polygon.getBounds().getCenter();
                const icon = L.divIcon({
                    html: `<div class="nlalert-pin"><img src="${NL_ICON_URL}" width="32" height="32"></div>`,
                    iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -22],
                    className: '',
                });
                L.marker(center, { icon, zIndexOffset: 3000 })
                    .bindPopup(popup, { maxWidth: 350 })
                    .addTo(group);
            });
        });

        this._nlAlertLayer = group;
        group.addTo(this.map);
    },

    async loadStationLayer(discipline, key, iconUrl) {
        if (!this._stationLayers) this._stationLayers = {};
        if (this._stationLayers[key]) {
            this._stationLayers[key].addTo(this.map);
            return;
        }
        // Lazy-load stations data (shared across all disciplines)
        if (!this._stationsData) {
            try {
                const res = await fetch('/data/stations.json');
                this._stationsData = await res.json();
            } catch (e) {
                console.error('Failed to load stations:', e);
                return;
            }
        }
        const icon = L.divIcon({
            html: `<img src="${iconUrl}" width="18" height="18" style="display:block">`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            className: 'station-icon',
        });
        const filtered = this._stationsData.filter(s => s.d === discipline);
        const group = L.layerGroup();
        filtered.forEach(s => {
            if (!s.lat || !s.lng) return;
            L.marker([s.lat, s.lng], { icon, interactive: true })
                .bindTooltip(s.name, { className: 'vr-tooltip' })
                .addTo(group);
        });
        this._stationLayers[key] = group;
        group.addTo(this.map);
    },

    // ========================================
    // WebSocket
    // ========================================

    async connectWs() {
        this._sessionExpired = false;
        this.setWsStatus('conn');

        let ticket;
        try {
            const res = await fetch('/api/v1/ws/ticket', { method: 'POST' });
            if (!res.ok) {
                this.setWsStatus('disc');
                // Bij 429: langzaam retry (30s), niet eindeloos snel
                const delay = res.status === 429 ? 30000 : this.reconnectDelay;
                const jitter = Math.random() * 1000;
                this._reconnectTimeout = setTimeout(() => this.connectWs(), delay + jitter);
                this.reconnectDelay = Math.min(this.reconnectDelay * 2, 60000);
                return;
            }
            const json = await res.json();
            ticket = json.data?.ticket;
            if (!ticket) throw new Error('No ticket in response');
        } catch (e) {
            console.error('WS ticket fetch failed:', e);
            this.setWsStatus('disc');
            const jitter = Math.random() * 500;
            this._reconnectTimeout = setTimeout(() => this.connectWs(), this.reconnectDelay + jitter);
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
            return;
        }

        const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
        this.ws = new WebSocket(`${proto}//${location.host}/ws?token=${ticket}`);

        this._wsOpenTimeout = setTimeout(() => {
            if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
                this.ws.close();
            }
        }, 5000);

        this.ws.onopen = () => {
            clearTimeout(this._wsOpenTimeout);
            this.reconnectDelay = 1000;
            this._lastHeartbeat = Date.now();
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = setInterval(() => {
                if (Date.now() - this._lastHeartbeat > 45000 && this.ws && this.ws.readyState === WebSocket.OPEN) {
                    console.warn('Heartbeat timeout, reconnecting...');
                    this.ws.close();
                }
            }, 5000);

            // Verse connectie: de server staat standaard op 'geen ruwe berichten',
            // dus dat is ook onze uitgangspositie. Alleen als we ze nodig hebben
            // gaat er een bericht heen.
            this._rawSubscribed = false;
            this._syncRawSubscription();

            if (this._hasConnectedOnce) {
                // Reconnect: full state refresh
                this.resetRawMessages();
                this.refreshAllIncidents();
            } else {
                this._hasConnectedOnce = true;
                this.setWsStatus('live');
            }
        };

        this.ws.onmessage = (e) => {
            try {
                const msg = JSON.parse(e.data);
                if (msg.type === 'heartbeat') {
                    this._lastHeartbeat = Date.now();
                } else if (msg.type === 'shutdown') {
                    this.reconnectDelay = 1000;
                    this.ws.close();
                    return;
                } else if (msg.type === 'session_expired') {
                    this._sessionExpired = true;
                    clearInterval(this._heartbeatTimer);
                    this.ws.close();
                    this.setWsStatus('expired');
                    return;
                } else if (msg.type && msg.type.startsWith('media.') && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleMediaEvent(msg.data);
                } else if (msg.type && msg.type.startsWith('heli.') && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleHeliEvent(msg.data, msg.type);
                } else if (msg.type === 'message.new' && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleMessageEvent(msg.data);
                } else if (msg.type === 'note.new' && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleNoteEvent(msg.data);
                } else if (msg.type === 'weather.update' && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleWeatherEvent(msg.data);
                } else if (msg.type && msg.type.startsWith('incident.') && msg.data) {
                    this._lastHeartbeat = Date.now();
                    this.handleIncidentEvent(msg.data, msg.type);
                }
            } catch (err) {
                console.error('WS parse error:', err);
            }
        };

        this.ws.onerror = () => this.setWsStatus('disc');

        this.ws.onclose = () => {
            clearInterval(this._heartbeatTimer);
            clearTimeout(this._wsOpenTimeout);
            if (this._sessionExpired) {
                this.setWsStatus('expired');
                return; // Niet reconnecten na session_expired
            }
            this.setWsStatus('disc');
            const jitter = Math.random() * 500;
            this._reconnectTimeout = setTimeout(() => this.connectWs(), this.reconnectDelay + jitter);
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
        };
    },

    setWsStatus(state) {
        const pill = document.getElementById('wsPill');
        const lbl = document.getElementById('wsLbl');
        if (!pill || !lbl) return;
        pill.className = 'ws-pill ws-' + state;
        const labels = { conn: 'Verbinden...', live: 'Live', disc: 'Verbroken', reload: 'Herladen...', expired: 'Verbroken' };
        lbl.textContent = labels[state] || state;
    },

    // ========================================
    // Initial load
    // ========================================

    async loadInitialIncidents() {
        try {
            const since = this.sinceParam();
            const perPage = this.tlActive ? 1000 : 200;
            const res = await fetch(`/api/v1/incidents?status=&is_administrative=all&since=${encodeURIComponent(since)}&per_page=${perPage}`);
            const json = await res.json();
            if (json.data) {
                json.data.reverse().forEach(inc => this.addOrUpdateIncident(inc, false));
            }
        } catch (e) {
            console.error('Failed to load incidents:', e);
        }
        this.renderAll();
    },

    async refreshAllIncidents() {
        this.setWsStatus('reload');
        try {
            const since = this.sinceParam();
            const perPage = this.tlActive ? 1000 : 200;
            const res = await fetch(`/api/v1/incidents?status=&is_administrative=all&since=${encodeURIComponent(since)}&per_page=${perPage}`);
            const json = await res.json();
            if (json.data) {
                const freshIds = new Set(json.data.map(inc => inc.id));
                const feedIds = new Set(this.feedIncidents || []);

                // Remove stale incidents + markers (preserve feed incidents)
                this.incidents.forEach((inc, id) => {
                    if (!freshIds.has(id) && !feedIds.has(id)) {
                        this.incidents.delete(id);
                        const marker = this.markers.get(id);
                        if (marker) { marker.remove(); this.markers.delete(id); }
                        const card = this.cards.get(id);
                        if (card) this.cards.delete(id);
                    }
                });

                // Update/add fresh incidents
                json.data.forEach(inc => this.addOrUpdateIncident(inc, false));

                this.lastUpdate = new Date();
                this.renderAll();
                this.applyMarkerVisibility();

                // Re-fetch detail if one is selected
                if (this.selected) {
                    const selectedInc = this.incidents.get(this.selected);
                    if (selectedInc) {
                        this.renderDetailHeader(selectedInc);
                        this.renderTab(selectedInc, this.activeTab);
                        this.fetchIncidentDetail(this.selected);
                    } else {
                        this.closeDetail();
                    }
                }
            }
        } catch (e) {
            console.error('Failed to refresh incidents:', e);
        }
        this.setWsStatus('live');
    },

    async loadRegions() {
        try {
            const res = await fetch('/api/v1/regions');
            const json = await res.json();
            const select = document.getElementById('regionSelect');
            if (json.data && select) {
                this.regionData = json.data;
                json.data.forEach(r => {
                    const opt = document.createElement('option');
                    opt.value = r.region_id;
                    opt.textContent = r.name;
                    select.appendChild(opt);
                });
            }
        } catch (e) {
            console.error('Failed to load regions:', e);
        }
    },

    // ========================================
    // Incident handling
    // ========================================

    handleIncidentEvent(data, eventType) {
        const isNew = eventType === 'incident.new';
        const isClosed = eventType === 'incident.closed' || data.status === 'closed';

        this.addOrUpdateIncident(data, true, eventType);

        // Instantly add matching incidents to active feed
        let inFeed = !this.activeFeedId || this.feedIncidents.includes(data.id);
        if (this.activeFeedId && !inFeed) {
            const inc = this.incidents.get(data.id);
            if (inc && this.incidentMatchesFeed(inc)) {
                this.feedIncidents.push(data.id);
                inFeed = true;
            }
        }

        // Show event toast (only for feed incidents when a feed is active)
        if (!this.activeFeedId || inFeed) {
            const type = data.incident_type || (data.disciplines || ['Melding'])[0];
            const city = data.city || data.region_name || '';
            if (isNew) {
                this.showEventToast(`\u25B2 Nieuw: ${type} \u2014 ${city}`);
                if (this.fSound) this._playAlertSound();
                if (this.fTts) this._maybeSpeakIncident(data);
            } else if (isClosed) {
                this.showEventToast(`\u2713 Gesloten: ${type} \u2014 ${city}`);
            } else if (data.grip) {
                this.showEventToast(`\u2B06 GRIP ${data.grip}: ${type} \u2014 ${city}`);
            }
        }

        this.lastUpdate = new Date();

        // Debounced API sync to reconcile client-side matching with server
        if (this.activeFeedId && isNew) {
            this.scheduleFeedRefresh();
        }

        this.scheduleRender(isNew ? data.id : null, (!isNew && !isClosed) ? data.id : null);

        // Update detail panel if selected
        if (this.selected === data.id) {
            const inc = this.incidents.get(data.id);
            if (inc) {
                this.renderDetailHeader(inc);
                this.renderTab(inc, this.activeTab);
                this.fetchIncidentDetail(data.id);
            }
        }
    },

    addOrUpdateIncident(data, isLive, eventType = '') {
        const id = data.id;
        const existing = this.incidents.get(id);
        const isClosed = eventType === 'incident.closed' || data.status === 'closed';

        // Don't add unknown closed incidents (unless timeline is active or it's a feed incident)
        if (!existing && isClosed && !this.tlActive && !this.feedIncidents.includes(id)) return;

        // Merge: preserve existing geocoded fields
        if (existing) {
            data = { ...existing, ...Object.fromEntries(
                Object.entries(data).filter(([, v]) => v != null)
            )};
        }

        data._dirty = true;
        this.incidents.set(id, data);

        // Map marker
        if (data.lat && data.lng) {
            this.addOrUpdateMarker(data, isLive && eventType === 'incident.new');
        }

        // Dim closed incident marker
        if (isClosed) {
            const marker = this.markers.get(id);
            if (marker) marker.setOpacity(0.3);
        }
    },

    addOrUpdateMarker(data, isNew) {
        const id = data.id;
        const lat = data.lat;
        const lng = data.lng;
        const color = this.pinColor(data);
        const size = this.pinSize(data);
        const disc = (data.disciplines || [])[0] || '';
        const discs = data.disciplines || [];
        const grip = data.grip || 0;

        let marker = this.markers.get(id);
        if (marker) {
            marker.setLatLng([lat, lng]);
            const iconKey = this._iconKey(color, size, disc, grip, discs);
            if (marker._iconKey !== iconKey) {
                marker.setIcon(this.createIcon(color, size, disc, grip, discs));
                marker._iconKey = iconKey;
            }
        } else {
            const icon = this.createIcon(color, size, disc, grip, discs);
            marker = L.marker([lat, lng], { icon, interactive: true })
                .on('click', () => this.selectIncident(id));
            marker._iconKey = this._iconKey(color, size, disc, grip, discs);
            marker._incId = id;
            this.markers.set(id, marker);

            if (isNew) {
                const pulseIcon = L.divIcon({
                    className: '',
                    html: `<div class="marker-pulse" style="color:${color}"></div>`,
                    iconSize: [12, 12],
                });
                const pulse = L.marker([lat, lng], { icon: pulseIcon, interactive: false }).addTo(this.map);
                setTimeout(() => pulse.remove(), 3000);
            }
        }

        // Visibility based on filters
        if (this.passesFilter(data)) {
            marker.addTo(this.map);
        } else {
            marker.remove();
        }
    },

    _iconKey(color, size, disc, grip, discs) {
        const showIcon = this._pinIcons ? disc : '';
        const showGrip = this._pinGrip ? grip : 0;
        const multiKey = (discs && discs.length > 1) ? discs.join(',') : '';
        return `${color}:${size}:${showIcon}:${showGrip}:${multiKey}`;
    },

    createIcon(color, size, disc, grip, discs) {
        const key = this._iconKey(color, size, disc, grip, discs);
        let icon = this.iconCache.get(key);
        if (!icon) {
            let inner = '';
            // Optional discipline emoji icon
            if (this._pinIcons && disc) {
                const emoji = this._discEmoji(disc, size);
                if (emoji) inner = emoji;
            }
            // Optional GRIP pulse ring
            const isGrip = this._pinGrip && grip > 0;
            const gripClass = isGrip ? ' pin-grip' : '';
            const shadow = isGrip ? '' : `box-shadow:0 0 10px ${color}cc, 0 0 3px rgba(0,0,0,.5);`;

            // Multi-discipline striped border
            const isMulti = discs && discs.length > 1;
            const borderWidth = isMulti ? 3 : 2;
            const outerSize = size + borderWidth * 2;

            let html;
            if (isMulti) {
                // Build conic-gradient segments for each discipline
                const colors = discs.map(d => PIN_COLORS[d] || '#8b8fa3');
                const segSize = 360 / colors.length;
                const stops = colors.map((c, i) => `${c} ${i * segSize}deg ${(i + 1) * segSize}deg`).join(', ');
                html = `<div class="pin-dot${gripClass}" style="width:${outerSize}px;height:${outerSize}px;background:conic-gradient(${stops});border-radius:50%;${shadow}display:flex;align-items:center;justify-content:center;color:${color}"><div style="width:${size}px;height:${size}px;background:#e8e8e8;border-radius:50%;position:relative">${inner}</div></div>`;
            } else {
                html = `<div class="pin-dot${gripClass}" style="width:${size}px;height:${size}px;background:${color};color:${color};border-radius:50%;border:${borderWidth}px solid rgba(255,255,255,.9);${shadow}">${inner}</div>`;
            }

            icon = L.divIcon({
                className: '',
                html,
                iconSize: [outerSize, outerSize],
                iconAnchor: [outerSize / 2, outerSize / 2],
            });
            this.iconCache.set(key, icon);
        }
        return icon;
    },

    _discEmoji(disc, size) {
        const emojis = { Brandweer: '🔥', Ambulance: '🚑', Politie: '👮', Lifeliner: '🚁', KNRM: '⚓', Reddingsbrigade: '🛟', Veiligheidsregio: '🛡️', Meldkamer: '📟' };
        const e = emojis[disc];
        if (!e) return '';
        const fs = Math.round(size * 0.5);
        return `<span style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:${fs}px;line-height:1">${e}</span>`;
    },

    refreshAllIcons() {
        this.iconCache.clear();
        this.markers.forEach((marker, id) => {
            const inc = this.incidents.get(id);
            if (!inc) return;
            const color = this.pinColor(inc);
            const size = this.pinSize(inc);
            const disc = (inc.disciplines || [])[0] || '';
            const discs = inc.disciplines || [];
            const grip = inc.grip || 0;
            const newKey = this._iconKey(color, size, disc, grip, discs);
            marker.setIcon(this.createIcon(color, size, disc, grip, discs));
            marker._iconKey = newKey;
        });
    },

    _syncLegendIcons() {
        const legend = document.getElementById('mapLegend');
        if (legend) legend.classList.toggle('legend-icons', this._pinIcons);
    },

    highlightMarker(id) {
        this._removeRing();
        const marker = this.markers.get(id);
        if (!marker) return;

        const el = marker.getElement();
        if (el) {
            const dot = el.querySelector('.pin-dot');
            if (dot) dot.classList.add('pin-highlight');
        }
        this._highlightId = id;

        const card = this.cards.get(id);
        if (card) card.classList.add('hover');
    },

    _removeRing() {
        if (this._highlightId) {
            const m = this.markers.get(this._highlightId);
            if (m) {
                const el = m.getElement();
                if (el) {
                    const dot = el.querySelector('.pin-dot');
                    if (dot) dot.classList.remove('pin-highlight');
                }
            }
            this._highlightId = null;
        }
        document.querySelectorAll('.ac.hover').forEach(el => el.classList.remove('hover'));
    },

    clearHighlight() {
        this._removeRing();
        // Restore selected incident highlight if detail panel is open
        if (this.selected) {
            this.highlightMarker(this.selected);
        }
    },

    // ========================================
    // Rendering
    // ========================================

    renderAll(newIds) {
        this.updateCounts();
        this.renderList(newIds);
        this.renderTextView();
        this.updateRadiusCircle();
    },

    scheduleRender(newId, updatedId) {
        if (newId) {
            this._pendingNewIds = this._pendingNewIds || new Set();
            this._pendingNewIds.add(newId);
        }
        if (updatedId) {
            this._pendingUpdatedIds = this._pendingUpdatedIds || new Set();
            this._pendingUpdatedIds.add(updatedId);
        }
        if (this._renderRAF) return;
        this._renderRAF = requestAnimationFrame(() => {
            this._renderRAF = null;
            const newIds = this._pendingNewIds || new Set();
            const updatedIds = this._pendingUpdatedIds || new Set();
            this._pendingNewIds = null;
            this._pendingUpdatedIds = null;
            this.renderAll(newIds);
            this.applyMarkerVisibility();
            // Flash updated cards after DOM is rebuilt
            updatedIds.forEach(id => {
                const card = this.cards.get(id);
                if (card) {
                    card.classList.add('flash');
                }
            });
        });
    },

    updateCounts() {
        const disc = { B: 0, A: 0, P: 0, L: 0, K: 0 };
        const esc = { klein: 0, middel: 0, groot: 0, 'zeer-groot': 0, grip1: 0, grip2: 0, grip3: 0, grip4: 0, grip5: 0 };
        // Track priority counts per discipline: { Brandweer: { P1: 3, P2: 5 }, ... }
        const discPrio = {};
        const feedActive = this.activeFeedId && this.feedIncidents.length > 0;
        this.incidents.forEach(inc => {
            if (feedActive) {
                if (!this.feedIncidents.includes(inc.id)) return;
            } else {
                if (inc.status === 'closed') return;
            }
            const level = this.escLevel(inc);
            if (esc[level] !== undefined) esc[level]++;
            const discs = inc.disciplines || [];
            const prio = (inc.priority_code || '').toUpperCase() || null;
            if (discs.includes('Brandweer')) { disc.B++; this._countPrio(discPrio, 'Brandweer', prio); }
            if (discs.includes('Ambulance')) { disc.A++; this._countPrio(discPrio, 'Ambulance', prio); }
            if (discs.includes('Politie')) { disc.P++; this._countPrio(discPrio, 'Politie', prio); }
            if (discs.includes('Lifeliner')) { disc.L++; this._countPrio(discPrio, 'Lifeliner', prio); }
            if (discs.includes('KNRM')) { disc.K++; this._countPrio(discPrio, 'KNRM', prio); }
        });
        this._discPrio = discPrio;
        const container = document.getElementById('tbCounts');
        if (!container) return;
        const quickDisc = this._quickDiscFilter || '';
        const quickEsc = this._quickEscFilter || '';
        const expandedDisc = this._expandedDisc || '';
        const pill = (label, count, bg, tx, discName, escLevel) => {
            const activeDisc = discName && quickDisc === discName;
            const activeEsc = escLevel && quickEsc === escLevel;
            const active = (activeDisc || activeEsc) ? ' tbc-active' : '';
            const data = discName ? ` data-disc="${discName}"` : (escLevel ? ` data-esc="${escLevel}"` : '');
            return `<span class="tbc${active}" style="background:var(${bg});color:var(${tx})"${data}>${label}: ${count}</span>`;
        };
        const pills = [];
        if (esc.grip5 > 0) pills.push(pill('GRIP 5', esc.grip5, '--c-grip3-bg', '--c-grip3-tx', null, 'grip5'));
        if (esc.grip4 > 0) pills.push(pill('GRIP 4', esc.grip4, '--c-grip3-bg', '--c-grip3-tx', null, 'grip4'));
        if (esc.grip3 > 0) pills.push(pill('GRIP 3', esc.grip3, '--c-grip3-bg', '--c-grip3-tx', null, 'grip3'));
        if (esc.grip2 > 0) pills.push(pill('GRIP 2', esc.grip2, '--c-grip2-bg', '--c-grip2-tx', null, 'grip2'));
        if (esc.grip1 > 0) pills.push(pill('GRIP 1', esc.grip1, '--c-grip1-bg', '--c-grip1-tx', null, 'grip1'));
        if (esc['zeer-groot'] > 0) pills.push(pill('Zeer grote brand', esc['zeer-groot'], '--c-esc-zg-bg', '--c-esc-zg-tx', null, 'zeer-groot'));
        if (esc.groot > 0) pills.push(pill('Grote brand', esc.groot, '--c-esc-g-bg', '--c-esc-g-tx', null, 'groot'));
        if (esc.middel > 0) pills.push(pill('Middelbrand', esc.middel, '--c-esc-m-bg', '--c-esc-m-tx', null, 'middel'));

        // Discipline pills with expandable priority sub-pills
        const discEntries = [
            ['B', 'Brandweer', '--c-bw-bg', '--c-bw-tx', true],
            ['A', 'Ambulance', '--c-amb-bg', '--c-amb-tx', true],
            ['P', 'Politie', '--c-pol-bg', '--c-pol-tx', true],
            ['L', 'Lifeliner', '--c-life-bg', '--c-life-tx', disc.L > 0],
            ['K', 'KNRM', '--c-knrm-bg', '--c-knrm-tx', disc.K > 0],
        ];
        discEntries.forEach(([label, name, bg, tx, show]) => {
            if (!show) return;
            pills.push(pill(label, disc[label], bg, tx, name));
            // If this discipline is expanded, add priority sub-pills
            if (expandedDisc === name && discPrio[name]) {
                const prios = Object.entries(discPrio[name]).sort((a, b) => a[0].localeCompare(b[0]));
                prios.forEach(([code, count]) => {
                    const activePrio = this.fPrio.has(code) ? ' tbc-active' : '';
                    pills.push(`<span class="tbc tbc-sub${activePrio}" style="background:var(${bg});color:var(${tx});opacity:.75" data-prio="${this.esc(code)}" data-prio-disc="${this.esc(name)}">${this.esc(code)}: ${count}</span>`);
                });
            }
        });
        container.innerHTML = pills.join('');

        // Bind click handlers for discipline quick-filter
        // Click: select + expand sub-pills. Re-click expanded: collapse + deselect.
        // Re-click collapsed (already filtered): expand sub-pills.
        container.querySelectorAll('.tbc[data-disc]').forEach(el => {
            el.addEventListener('click', () => {
                const d = el.dataset.disc;
                if (this._quickDiscFilter === d) {
                    if (this._expandedDisc === d) {
                        // Collapse and deselect
                        this._quickDiscFilter = '';
                        this._expandedDisc = '';
                        this.fDisc.clear();
                        this.fPrio.clear();
                    } else {
                        // Already filtered, expand sub-pills
                        this._expandedDisc = d;
                        this.fPrio.clear();
                    }
                } else {
                    this._quickDiscFilter = d;
                    this.fDisc.clear();
                    this.fDisc.add(d);
                    this._expandedDisc = d;
                    this.fPrio.clear();
                    this._quickEscFilter = '';
                    this.fEsc.clear();
                }
                this.updateFilterState();
                this.applyFilters();
            });
        });

        // Bind click handlers for priority sub-pills
        container.querySelectorAll('.tbc-sub[data-prio]').forEach(el => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                const code = el.dataset.prio;
                if (this.fPrio.has(code)) {
                    this.fPrio.delete(code);
                } else {
                    this.fPrio.add(code);
                }
                this.updateFilterState();
                this.applyFilters();
            });
        });

        // Bind click handlers for escalation quick-filter
        container.querySelectorAll('.tbc[data-esc]').forEach(el => {
            el.addEventListener('click', () => {
                const e = el.dataset.esc;
                if (this._quickEscFilter === e) {
                    this._quickEscFilter = '';
                    this.fEsc.clear();
                } else {
                    this._quickEscFilter = e;
                    this.fEsc.clear();
                    this.fEsc.add(e);
                }
                // Clear discipline quick filter when toggling escalation
                this._quickDiscFilter = '';
                this._expandedDisc = '';
                this.fDisc.clear();
                this.fPrio.clear();
                this.updateFilterState();
                this.applyFilters();
            });
        });
    },

    _countPrio(discPrio, discName, prio) {
        if (!prio) return;
        if (!discPrio[discName]) discPrio[discName] = {};
        discPrio[discName][prio] = (discPrio[discName][prio] || 0) + 1;
    },

    renderList(newIds) {
        const list = document.getElementById('alertList');
        const empty = document.getElementById('emptyState');
        if (!list) return;

        // Collect visible incidents sorted by last_message_at desc
        const visible = [];
        this.incidents.forEach(inc => {
            if (this.passesFilter(inc)) visible.push(inc);
        });
        visible.sort((a, b) => {
            const tA = a.last_message_at || a.first_message_at || '';
            const tB = b.last_message_at || b.first_message_at || '';
            return tB.localeCompare(tA);
        });

        // Update sidebar header label based on timeline state
        const sbLabel = document.querySelector('.sb-hd-lbl');
        if (sbLabel) sbLabel.textContent = this.tlActive ? 'Incidenten' : 'Actieve incidenten';

        // Update total badge with yesterday comparison
        const badge = document.getElementById('totBadge');
        if (badge) {
            const count = visible.length;
            const total = this.incidents.size;
            const yesterdayTotal = window.__APP?.yesterdayActive ?? 0;
            let trend = '';
            if (yesterdayTotal > 0 && total > 0 && !this.tlActive) {
                // Proportioneel schalen: als user filters 60% tonen, toon 60% van yesterday
                const yesterdayEstimate = Math.round(yesterdayTotal * count / total);
                const diff = count - yesterdayEstimate;
                if (diff > 0) trend = ' <span style="color:#27ae60;font-size:10px;" title="Gisteren om deze tijd: ~' + yesterdayEstimate + '">&#9650;</span>';
                else if (diff < 0) trend = ' <span style="color:#e74c3c;font-size:10px;" title="Gisteren om deze tijd: ~' + yesterdayEstimate + '">&#9660;</span>';
                else trend = ' <span style="color:#95a5a6;font-size:10px;" title="Gisteren om deze tijd: ~' + yesterdayEstimate + '">=</span>';
            }
            badge.innerHTML = String(count) + trend;
        }

        if (visible.length === 0) {
            list.innerHTML = '';
            if (empty) {
                empty.style.display = '';
                empty.textContent = this.filtersApplied ? 'Geen incidenten voor deze filters' : 'Wachten op meldingen...';
            }
            return;
        }

        if (empty) empty.style.display = 'none';

        // Split major incidents (escalation_level >= 2, active) from regular
        const majorSection = document.getElementById('majorSection');
        const majorList = document.getElementById('majorList');
        const majorCount = document.getElementById('majorCount');
        let majors = [];
        let regulars = visible;

        if (this.fMajorPin && majorSection && majorList) {
            majors = visible.filter(inc => inc.escalation_level >= 2 && inc.status !== 'closed');
            regulars = visible.filter(inc => !(inc.escalation_level >= 2 && inc.status !== 'closed'));

            if (majors.length > 0) {
                majorSection.style.display = '';
                if (majorCount) majorCount.textContent = String(majors.length);

                // Max 4 visible without scroll, scroll if more
                const cardH = 96; // approximate card height including 2-line body
                const maxVisible = Math.min(majors.length, 4);
                majorList.style.maxHeight = (maxVisible * cardH + (maxVisible - 1) * 4 + 8) + 'px';

                const majorFrag = document.createDocumentFragment();
                majors.forEach(inc => {
                    const card = this._getOrCreateCard(inc, newIds);
                    majorFrag.appendChild(card);
                });
                majorList.innerHTML = '';
                majorList.appendChild(majorFrag);
            } else {
                majorSection.style.display = 'none';
                majorList.innerHTML = '';
            }
        } else {
            if (majorSection) majorSection.style.display = 'none';
        }

        // Build regular card elements
        const fragment = document.createDocumentFragment();

        // Show "Andere incidenten" header when major section is visible
        if (this.fMajorPin && majors.length > 0 && regulars.length > 0) {
            let otherHd = document.getElementById('otherIncidentsHd');
            if (!otherHd) {
                otherHd = document.createElement('div');
                otherHd.id = 'otherIncidentsHd';
                otherHd.className = 'other-hd';
                otherHd.textContent = 'Andere incidenten';
            }
            fragment.appendChild(otherHd);
        }

        regulars.forEach(inc => {
            const card = this._getOrCreateCard(inc, newIds);
            fragment.appendChild(card);
        });

        list.innerHTML = '';
        list.appendChild(fragment);
    },

    _getOrCreateCard(inc, newIds) {
        let card = this.cards.get(inc.id);
        if (card) {
            if (inc._dirty) {
                card.innerHTML = this.buildCardHtml(inc);
                card.className = this.buildCardClass(inc, newIds);
                inc._dirty = false;
            }
        } else {
            card = document.createElement('div');
            card.className = this.buildCardClass(inc, newIds);
            card.dataset.id = inc.id;
            card.innerHTML = this.buildCardHtml(inc);
            card.addEventListener('click', () => this.onCardClick(inc.id));
            card.addEventListener('mouseenter', () => this.highlightMarker(inc.id));
            card.addEventListener('mouseleave', () => this.clearHighlight());
            this.cards.set(inc.id, card);
        }
        return card;
    },

    buildCardClass(inc, newIds) {
        let cls = 'ac ' + this.cardClass(inc);
        if (this.selected === inc.id) cls += ' sel';
        if (newIds && newIds.has(inc.id)) cls += ' new-in';
        if (inc.status === 'closed') cls += ' closed';
        if (inc.is_test) cls += ' test';
        if (inc.is_administrative) cls += ' administrative';
        if (this.mergeMode && this.mergeSelected.has(inc.id)) cls += ' merge-sel';
        return cls;
    },

    buildCardHtml(inc) {
        const discs = inc.disciplines || [];
        const primary = discs[0] || '';

        // Discipline letter badge(s)
        let badges = '';
        discs.forEach(d => {
            const letter = DISC_LETTER[d] || d.substring(0, 1);
            badges += `<span class="disc-letter ${DISC_BADGE[d] || 'b-over'}">${letter}</span>`;
        });

        // Priority / escalation badge (same size as disc-letter)
        if (inc.grip) {
            badges += `<span class="prio-badge b-grip2">GRIP${inc.grip}</span>`;
        } else if (inc.priority_code) {
            badges += `<span class="prio-badge ${this.prioBadgeClass(inc.priority_code)}">${this.esc(inc.priority_code)}</span>`;
        }

        const lastTime = this.formatTime(inc.last_message_at || inc.first_message_at);
        const firstTime = this.formatTime(inc.first_message_at);
        const hasUpdate = inc.message_count > 1 && inc.first_message_at && inc.last_message_at && inc.first_message_at !== inc.last_message_at;
        const body = inc.body || '';
        const type = inc.incident_type || primary || '\u2013';
        const city = inc.city || inc.region_name || '';

        // Meta: units, messages, media, city
        let meta = '';
        if (inc.unit_count > 0) meta += `<span>${inc.unit_count} eenh.</span>`;
        if (inc.message_count > 1) meta += `<span>${inc.message_count} ber.</span>`;
        const mc = inc.media_counts;
        if (mc && mc.photos > 0) meta += `<span title="${mc.photos} foto${mc.photos !== 1 ? "'s" : ''}">📷${mc.photos}</span>`;
        if (mc && mc.links > 0) meta += `<span title="${mc.links} link${mc.links !== 1 ? 's' : ''}">🔗${mc.links}</span>`;
        if (city) meta += `<span class="ac-city">${this.esc(city)}</span>`;

        // Time display: show last update time, and start time when updated
        const timeHtml = hasUpdate
            ? `<span class="ac-time"><span class="ac-updated">\u21bb ${lastTime}</span>${firstTime}</span>`
            : `<span class="ac-time">${lastTime}</span>`;

        return `
            <div class="ac-top">
                <div class="badges">${badges}</div>
                <div class="ac-text">
                    <div class="ac-body">${this.esc(body || type)}</div>
                    ${body && type !== primary ? `<div class="ac-type">${this.esc(type)}</div>` : ''}
                </div>
                ${timeHtml}
            </div>
            ${meta ? `<div class="ac-meta">${meta}</div>` : ''}`;
    },

    // ========================================
    // Filters
    // ========================================

    initFilterPanel() {
        const toggle = document.getElementById('filterToggle');
        const panel = document.getElementById('filterPanel');
        const close = document.getElementById('fpClose');

        if (toggle && panel) {
            toggle.addEventListener('click', () => {
                panel.classList.toggle('open');
                toggle.classList.toggle('on');
            });
        }
        if (close && panel) {
            close.addEventListener('click', () => {
                panel.classList.remove('open');
                if (toggle) toggle.classList.remove('on');
            });
        }

        // Hide test messages toggle
        const hideTestToggle = document.getElementById('hideTestToggle');
        if (hideTestToggle) {
            hideTestToggle.checked = this.fHideTest;
            hideTestToggle.addEventListener('change', () => {
                this.fHideTest = hideTestToggle.checked;
                savePref('hideTest', this.fHideTest ? '1' : '0');
                this.applyFilters();
            });
        }

        // Major incidents pinned toggle
        const majorPinToggle = document.getElementById('majorPinToggle');
        if (majorPinToggle) {
            majorPinToggle.checked = this.fMajorPin;
            majorPinToggle.addEventListener('change', () => {
                this.fMajorPin = majorPinToggle.checked;
                savePref('majorPin', this.fMajorPin ? '1' : '0');
                this.renderAll();
            });
        }

        // Sound toggle
        const soundToggle = document.getElementById('soundToggle');
        if (soundToggle) {
            soundToggle.checked = this.fSound;
            soundToggle.addEventListener('change', () => {
                this.fSound = soundToggle.checked;
                savePref('sound', this.fSound ? '1' : '0');
                if (this.fSound) this._playAlertSound(); // Preview
            });
        }

        // TTS (voorlezen) toggles
        this._initTtsControls();

        // Pin settings toggles
        const pinIconsToggle = document.getElementById('pinIconsToggle');
        const pinGripToggle = document.getElementById('pinGripToggle');
        if (pinIconsToggle) {
            pinIconsToggle.checked = this._pinIcons;
            pinIconsToggle.addEventListener('change', () => {
                this._pinIcons = pinIconsToggle.checked;
                savePref('pinIcons', this._pinIcons ? '1' : '0');
                this._syncLegendIcons();
                this.refreshAllIcons();
            });
        }
        if (pinGripToggle) {
            pinGripToggle.checked = this._pinGrip;
            pinGripToggle.addEventListener('change', () => {
                this._pinGrip = pinGripToggle.checked;
                savePref('pinGrip', this._pinGrip ? '1' : '0');
                this.refreshAllIcons();
            });
        }

        // Region select
        const regionSel = document.getElementById('regionSelect');
        if (regionSel) {
            regionSel.addEventListener('change', () => { this.fRegion = regionSel.value; });
        }

        // Radius range
        const radiusRange = document.getElementById('radiusRange');
        const radiusVal = document.getElementById('radiusVal');
        if (radiusRange && radiusVal) {
            radiusRange.addEventListener('input', () => {
                this.fRadiusKm = parseInt(radiusRange.value, 10);
                radiusVal.textContent = this.fRadiusKm + ' km';
            });
        }

        // GPS button
        const gpsBtn = document.getElementById('locGpsBtn');
        if (gpsBtn) {
            gpsBtn.addEventListener('click', () => {
                if (!navigator.geolocation) { alert('GPS niet beschikbaar'); return; }
                navigator.geolocation.getCurrentPosition(pos => {
                    this.fLocLat = pos.coords.latitude;
                    this.fLocLng = pos.coords.longitude;
                    const coords = document.getElementById('locCoords');
                    if (coords) {
                        coords.style.display = 'block';
                        coords.textContent = this.formatCoords();
                    }
                    gpsBtn.classList.add('on');
                    const manBtn = document.getElementById('locManBtn');
                    if (manBtn) manBtn.classList.remove('on');
                    const mc = document.getElementById('manualCoords');
                    if (mc) mc.style.display = 'none';
                }, () => alert('GPS niet toegestaan'));
            });
        }

        // Manual location button
        const manBtn = document.getElementById('locManBtn');
        if (manBtn) {
            manBtn.addEventListener('click', () => {
                const mc = document.getElementById('manualCoords');
                if (!mc) return;
                mc.style.display = mc.style.display === 'none' ? 'block' : 'none';
                manBtn.classList.toggle('on', mc.style.display === 'block');
                if (gpsBtn) gpsBtn.classList.remove('on');
            });
        }

        // Apply button
        const applyBtn = document.getElementById('fpApply');
        if (applyBtn) {
            applyBtn.addEventListener('click', () => {
                // Read manual coordinates if entered
                const lat = parseFloat(document.getElementById('manLat')?.value);
                const lng = parseFloat(document.getElementById('manLng')?.value);
                if (!isNaN(lat) && !isNaN(lng)) {
                    this.fLocLat = lat;
                    this.fLocLng = lng;
                    const coords = document.getElementById('locCoords');
                    if (coords) {
                        coords.style.display = 'block';
                        coords.textContent = this.formatCoords();
                    }
                }
                if (panel) panel.classList.remove('open');
                if (toggle) toggle.classList.remove('on');
                this.applyFilters();
                this.zoomToFiltered();
            });
        }

        // Reset button
        const resetBtn = document.getElementById('fpReset');
        if (resetBtn) {
            resetBtn.addEventListener('click', () => {
                this.fDisc.clear();
                this.fEsc.clear();
                this.fRegion = '';
                this.fLocLat = null;
                this.fLocLng = null;
                this.fRadiusKm = 25;
                this.fHideTest = false;
                savePref('hideTest', '0');
                if (hideTestToggle) hideTestToggle.checked = false;
                if (regionSel) regionSel.value = '';
                if (radiusRange) radiusRange.value = '25';
                if (radiusVal) radiusVal.textContent = '25 km';
                const coords = document.getElementById('locCoords');
                if (coords) coords.style.display = 'none';
                const mc = document.getElementById('manualCoords');
                if (mc) mc.style.display = 'none';
                if (gpsBtn) gpsBtn.classList.remove('on');
                if (manBtn) manBtn.classList.remove('on');
                document.querySelectorAll('.chip').forEach(c => c.classList.remove('on'));

                this._quickDiscFilter = '';
                this._quickEscFilter = '';
                this.applyFilters();
                this.zoomToNL();
            });
        }

        // Restore saved filters
        this.restoreFilters();
    },

    _initTtsControls() {
        const ttsToggle = document.getElementById('ttsToggle');
        const ttsOptions = document.getElementById('ttsOptions');
        if (!ttsToggle) return;

        // Spraak niet ondersteund → toggle uitschakelen met uitleg
        if (!('speechSynthesis' in window)) {
            ttsToggle.disabled = true;
            const lbl = ttsToggle.closest('.fp-toggle');
            if (lbl) lbl.title = 'Spraak wordt niet ondersteund in deze browser';
            return;
        }

        // Stemmen laden asynchroon; reset de cache zodat een later beschikbare
        // (mooiere) cloud-stem alsnog opgepikt wordt.
        try { window.speechSynthesis.addEventListener('voiceschanged', () => { this._ttsVoiceCache = undefined; }); } catch (_) {}

        // Server-prefs zijn al toegepast op localStorage (applyServerPrefs vóór init)
        this.fTts = localStorage.getItem('ttsEnabled') === '1';
        this.fTtsFull = localStorage.getItem('ttsFull') === '1';
        const discStored = localStorage.getItem('ttsDisc');
        const prioStored = localStorage.getItem('ttsPrio');
        // Eerste gebruik (nooit ingesteld) → standaard alle diensten + prio 1/2/3 aan
        this.fTtsDisc = new Set((discStored === null ? 'Brandweer,Ambulance,Politie' : discStored).split(',').filter(Boolean));
        this.fTtsPrio = new Set((prioStored === null ? '1,2,3' : prioStored).split(',').filter(Boolean).map(Number));

        const syncVisible = () => { if (ttsOptions) ttsOptions.style.display = this.fTts ? 'block' : 'none'; };

        ttsToggle.checked = this.fTts;
        syncVisible();
        ttsToggle.addEventListener('change', () => {
            this.fTts = ttsToggle.checked;
            savePref('ttsEnabled', this.fTts ? '1' : '0');
            syncVisible();
            if (this.fTts) this._speak('Voorlezen ingeschakeld'); // preview + ontgrendelt spraak
            else { try { window.speechSynthesis.cancel(); } catch (_) {} }
        });

        const ttsFullToggle = document.getElementById('ttsFullToggle');
        if (ttsFullToggle) {
            ttsFullToggle.checked = this.fTtsFull;
            ttsFullToggle.addEventListener('change', () => {
                this.fTtsFull = ttsFullToggle.checked;
                savePref('ttsFull', this.fTtsFull ? '1' : '0');
            });
        }

        document.querySelectorAll('.tts-disc').forEach(cb => {
            cb.checked = this.fTtsDisc.has(cb.value);
            cb.addEventListener('change', () => {
                if (cb.checked) this.fTtsDisc.add(cb.value); else this.fTtsDisc.delete(cb.value);
                savePref('ttsDisc', Array.from(this.fTtsDisc).join(','));
            });
        });

        document.querySelectorAll('.tts-prio').forEach(cb => {
            const n = parseInt(cb.value, 10);
            cb.checked = this.fTtsPrio.has(n);
            cb.addEventListener('change', () => {
                if (cb.checked) this.fTtsPrio.add(n); else this.fTtsPrio.delete(n);
                savePref('ttsPrio', Array.from(this.fTtsPrio).join(','));
            });
        });
    },

    passesFilter(inc) {
        // If a feed is active, only show matching feed incidents
        if (this.activeFeedId && this.feedIncidents.length > 0) {
            if (!this.feedIncidents.includes(inc.id)) return false;
        }
        if (this.fHideTest && inc.is_test) return false;
        if (this.fDisc.size > 0) {
            const d = inc.disciplines || [];
            if (!d.some(x => this.fDisc.has(x))) return false;
        }
        if (this.fEsc.size > 0) {
            if (!this.fEsc.has(this.escLevel(inc))) return false;
        }
        if (this.fPrio.size > 0) {
            const p = (inc.priority_code || '').toUpperCase();
            if (!this.fPrio.has(p)) return false;
        }
        if (this.fRegion) {
            if (String(inc.region_id || '') !== String(this.fRegion)) return false;
        }
        if (this.fLocLat !== null) {
            if (!inc.lat || !inc.lng) return false;
            const dist = this.haversineKm(this.fLocLat, this.fLocLng, inc.lat, inc.lng);
            if (dist > this.fRadiusKm) return false;
        }
        // Map bounds filter: only show incidents visible in current viewport
        if (this.mapBoundsFilter && this.map) {
            if (!inc.lat || !inc.lng) return false;
            if (!this.map.getBounds().contains([inc.lat, inc.lng])) return false;
        }
        // Timeline filter: show incidents that existed at the cutoff time
        const cutoff = this.getTimelineCutoff();
        if (cutoff) {
            const raw = inc.first_message_at || inc.last_message_at;
            if (!raw) return true;
            // DB stores CET (local time), parse without Z suffix
            const startTime = new Date(raw.replace(' ', 'T'));
            if (isNaN(startTime.getTime())) return true;
            if (startTime > cutoff) return false;
        }
        return true;
    },

    applyMarkerVisibility() {
        this.incidents.forEach((inc, id) => {
            const marker = this.markers.get(id);
            if (!marker) return;
            if (this.passesFilter(inc)) marker.addTo(this.map);
            else marker.remove();
        });
    },

    zoomToFiltered() {
        // Filters zijn ook zonder kaart te bedienen (tekstmodus) — zoomen op een
        // kaart zonder afmetingen levert een NaN-viewport op.
        if (!this._mapUsable()) return;
        if (!this.filtersApplied) {
            this.zoomToNL();
            return;
        }

        // If region filter is set, zoom to VR GeoJSON bounds
        if (this.fRegion) {
            this.zoomToRegion(this.fRegion);
            return;
        }

        // Otherwise fit to visible incidents
        const bounds = [];
        this.incidents.forEach(inc => {
            if (this.passesFilter(inc) && inc.lat && inc.lng) {
                bounds.push([inc.lat, inc.lng]);
            }
        });
        if (bounds.length >= 2) {
            this.map.fitBounds(bounds, { ...this.zoomPadding(), maxZoom: 12 });
        } else if (bounds.length === 1) {
            this.map.setView(bounds[0], 11);
        }
    },

    async zoomToRegion(regionId) {
        if (!this._mapUsable()) return;
        const regionInfo = this.regionData.find(r => String(r.region_id) === String(regionId));
        if (!regionInfo) return;
        const regionName = regionInfo.name;

        // Load GeoJSON if not cached
        if (!this._vrGeoJson) {
            try {
                const res = await fetch('/data/veiligheidsregios.geojson');
                this._vrGeoJson = await res.json();
            } catch (e) { return; }
        }

        const feature = this._vrGeoJson.features.find(f => f.properties.statnaam === regionName);
        if (!feature) return;

        const layer = L.geoJSON(feature);
        this.map.fitBounds(layer.getBounds(), { ...this.zoomPadding(), maxZoom: 11 });
    },

    saveFilters() {
        const data = {
            disc: [...this.fDisc],
            esc: [...this.fEsc],
            prio: [...this.fPrio],
            region: this.fRegion,
            locLat: this.fLocLat,
            locLng: this.fLocLng,
            radiusKm: this.fRadiusKm,
        };
        savePref('filters', JSON.stringify(data));
    },

    restoreFilters() {
        const raw = localStorage.getItem('filters');
        if (!raw) return;
        try {
            const data = JSON.parse(raw);
            if (Array.isArray(data.disc)) data.disc.forEach(d => this.fDisc.add(d));
            if (Array.isArray(data.esc)) data.esc.forEach(e => this.fEsc.add(e));
            if (Array.isArray(data.prio)) data.prio.forEach(p => this.fPrio.add(p));
            if (data.region) this.fRegion = data.region;
            if (data.locLat !== null && data.locLat !== undefined) {
                this.fLocLat = data.locLat;
                this.fLocLng = data.locLng;
            }
            if (data.radiusKm) this.fRadiusKm = data.radiusKm;
            this.updateFilterState();
            // Restore quick-filter highlights
            if (this.fDisc.size === 1) {
                this._quickDiscFilter = [...this.fDisc][0];
                if (this.fPrio.size > 0) this._expandedDisc = this._quickDiscFilter;
            }
            if (this.fEsc.size === 1) this._quickEscFilter = [...this.fEsc][0];

            // Sync UI controls
            const regionSel = document.getElementById('regionSelect');
            if (regionSel && this.fRegion) regionSel.value = this.fRegion;
            const radiusRange = document.getElementById('radiusRange');
            const radiusVal = document.getElementById('radiusVal');
            if (radiusRange) radiusRange.value = String(this.fRadiusKm);
            if (radiusVal) radiusVal.textContent = this.fRadiusKm + ' km';
            if (this.fLocLat !== null) {
                const coords = document.getElementById('locCoords');
                if (coords) {
                    coords.style.display = 'block';
                    coords.textContent = this.formatCoords();
                }
            }

            this.renderActiveFilters();
        } catch (e) { /* ignore corrupt data */ }
    },

    renderActiveFilters() {
        const bar = document.getElementById('activeFilters');
        if (!bar) return;
        const chips = [];
        if (this.fDisc.size > 0) {
            let lbl = 'Disc: ' + [...this.fDisc].join(', ');
            if (this.fPrio.size > 0) lbl += ' (' + [...this.fPrio].sort().join(', ') + ')';
            chips.push({ label: lbl, key: 'disc' });
        }
        if (this.fEsc.size > 0) chips.push({ label: 'Niveau: ' + [...this.fEsc].join(', '), key: 'esc' });
        if (this.fRegion) {
            const r = this.regionData.find(x => String(x.region_id) === String(this.fRegion));
            chips.push({ label: 'Regio: ' + (r ? r.name : this.fRegion), key: 'region' });
        }
        if (this.fLocLat !== null) chips.push({ label: `${this.fRadiusKm}km radius`, key: 'loc' });
        if (this.mapBoundsFilter) chips.push({ label: 'Alleen meldingen zichtbaar op kaart', key: 'bounds' });

        if (chips.length === 0) {
            bar.classList.remove('show');
            return;
        }
        bar.classList.add('show');
        bar.innerHTML = chips.map(c =>
            `<span class="af-chip" data-key="${this.esc(c.key)}" title="Klik om te verwijderen">\u2715 ${this.esc(c.label)}</span>`
        ).join('');
        bar.querySelectorAll('.af-chip').forEach(el => {
            el.addEventListener('click', () => {
                const k = el.dataset.key;
                if (k === 'disc') { this.fDisc.clear(); this.fPrio.clear(); this._quickDiscFilter = ''; this._expandedDisc = ''; }
                if (k === 'esc') { this.fEsc.clear(); this._quickEscFilter = ''; }
                if (k === 'region') { this.fRegion = ''; this.zoomToNL(); }
                if (k === 'bounds') {
                    this.mapBoundsFilter = false;
                    savePref('mapBoundsFilter', '0');
                    const boundsBtn = document.getElementById('boundsToggle');
                    if (boundsBtn) boundsBtn.classList.remove('active');
                }
                if (k === 'loc') {
                    this.fLocLat = null;
                    this.fLocLng = null;
                    const gps = document.getElementById('locGpsBtn');
                    const man = document.getElementById('locManBtn');
                    const coords = document.getElementById('locCoords');
                    if (gps) gps.classList.remove('on');
                    if (man) man.classList.remove('on');
                    if (coords) coords.style.display = 'none';
                }
                this.applyFilters();
            });
        });
    },

    updateRadiusCircle() {
        // Remove existing
        if (this.radiusCircle) { this.radiusCircle.remove(); this.radiusCircle = null; }
        if (this.radiusCenter) { this.radiusCenter.remove(); this.radiusCenter = null; }

        if (this.fLocLat === null) return;

        this.radiusCircle = L.circle([this.fLocLat, this.fLocLng], {
            radius: this.fRadiusKm * 1000,
            color: '#185FA5',
            weight: 1.5,
            dashArray: '5,3',
            fillColor: 'rgba(24,95,165,.08)',
            fillOpacity: 1,
            interactive: false,
        }).addTo(this.map);

        this.radiusCenter = L.circleMarker([this.fLocLat, this.fLocLng], {
            radius: 5,
            color: '#fff',
            weight: 1.5,
            fillColor: '#185FA5',
            fillOpacity: 1,
            interactive: false,
        }).addTo(this.map);
    },

    // ========================================
    // Detail Panel
    // ========================================

    initDetailPanel() {
        const ccStored = localStorage.getItem('showCapcodes');
        this._ccMode = ccStored === 'names' || ccStored === '1' ? 'names' : ccStored === 'nums' ? 'nums' : 'off';
        this._showCapcodes = this._ccMode !== 'off'; // compat

        // Tabs
        const tabs = document.getElementById('dpTabs');
        if (tabs) {
            tabs.addEventListener('click', e => {
                const tab = e.target.dataset.tab;
                if (!tab) return;
                const inc = this.incidents.get(this.selected);
                if (inc) this.renderTab(inc, tab);
            });
        }

        // Capcode toggle (event delegation) — 3-way: off → names → nums → off
        const ccModes = ['off', 'names', 'nums'];
        const ccLabels = { off: 'Capcodes', names: 'Namen', nums: 'Nummers' };
        document.addEventListener('click', e => {
            if (e.target.id === 'tlCcToggle') {
                this._ccMode = ccModes[(ccModes.indexOf(this._ccMode) + 1) % ccModes.length];
                this._showCapcodes = this._ccMode !== 'off';
                localStorage.setItem('showCapcodes', this._ccMode === 'off' ? '0' : this._ccMode);
                e.target.classList.toggle('on', this._showCapcodes);
                e.target.textContent = ccLabels[this._ccMode];
                const wrap = document.getElementById('tlWrap') || document.getElementById('msgContainer');
                if (wrap) {
                    wrap.classList.remove('show-cc-names', 'show-cc-nums');
                    if (this._ccMode !== 'off') wrap.classList.add('show-cc-' + this._ccMode);
                }
            }
        });

        // Close
        const close = document.getElementById('dpClose');
        if (close) {
            close.addEventListener('click', () => this.closeDetail());
        }
    },

    /**
     * Is de kaart bruikbaar voor lat/lng-bewerkingen?
     *
     * In tekstmodus staat de kaart op display:none en levert getSize() 0x0.
     * flyTo() deelt daar door, wat een NaN-LatLng oplevert en elke animatieframe
     * een fout gooit. We meten de grootte in plaats van de weergavestand, zodat
     * dit ook klopt zolang de kaart nog niet is uitgemeten.
     */
    _mapUsable() {
        if (!this.map) return false;
        const size = this.map.getSize();
        return size.x > 0 && size.y > 0;
    },

    selectIncident(id) {
        // Toggle if same
        if (this.selected === id) {
            this.closeDetail();
            return;
        }

        this.selected = id;
        const inc = this.incidents.get(id);
        if (!inc) return;

        // Highlight in list
        this.cards.forEach((card, cid) => card.classList.toggle('sel', cid === id));
        this._markTextRowSelected(id);

        const mapUsable = this._mapUsable();

        // Bewaar huidige kaartpositie voor restore bij sluiten
        if (mapUsable && !this._preSelectView) {
            this._preSelectView = {
                center: this.map.getCenter(),
                zoom: this.map.getZoom(),
            };
        }

        // Fly to on map, offset for detail panel
        if (mapUsable && inc.lat && inc.lng) {
            const dp = document.getElementById('detailPanel');
            const dpW = dp ? (dp.offsetWidth || 380) : 380;
            const targetPoint = this.map.project([inc.lat, inc.lng], 14);
            targetPoint.x += dpW / 2;
            const offsetLatLng = this.map.unproject(targetPoint, 14);
            this.map.flyTo(offsetLatLng, 14, { duration: 0.8 });
            this.highlightMarker(id);
        }

        // On mobile, show feed panel — behalve in tekstmodus, daar zou de
        // zijbalk over de lijst schuiven die je net aanklikte
        if (window.innerWidth <= 768 && this.viewMode !== 'text') {
            const sb = document.querySelector('.sb');
            if (sb) sb.classList.add('mobile-visible');
        }

        // Show detail panel
        this.renderDetailHeader(inc);
        this.renderTab(inc, 'info');
        this.activeTab = 'info';
        document.querySelectorAll('.dp-tab').forEach(t => {
            t.classList.toggle('on', t.dataset.tab === 'info');
            if (t.dataset.tab === 'eenheden') {
                const count = inc._vehicles?.length || inc.unit_count || 0;
                t.innerHTML = count > 0 ? `Eenheden <span class="tab-badge">${count}</span>` : 'Eenheden';
            }
            if (t.dataset.tab === 'kladblok') {
                const noteCount = inc._notes?.length || 0;
                t.style.display = noteCount > 0 ? '' : 'none';
                if (noteCount > 0) t.innerHTML = `Kladblok <span class="tab-badge">${noteCount}</span>`;
            }
        });
        document.getElementById('detailPanel')?.classList.add('open');
        document.getElementById('mapWrap')?.classList.add('dp-open');
        // Ook op .dash, zodat de tekstweergave ruimte maakt voor het paneel
        document.querySelector('.dash')?.classList.add('dp-open');

        // Fetch full detail data + media
        this.fetchIncidentDetail(id);
        this.fetchIncidentMedia(id);
    },

    closeDetail() {
        this.selected = null;
        this.clearHighlight();
        this.clearIncidentStations();
        document.getElementById('detailPanel')?.classList.remove('open');
        document.getElementById('mapWrap')?.classList.remove('dp-open');
        document.querySelector('.dash')?.classList.remove('dp-open');
        this.cards.forEach(card => card.classList.remove('sel'));
        this._markTextRowSelected(null);

        // Herstel kaartpositie van voor selectie
        if (this._preSelectView) {
            if (this._mapUsable()) {
                this.map.flyTo(this._preSelectView.center, this._preSelectView.zoom, { duration: 0.6 });
            }
            this._preSelectView = null;
        }
    },

    async showIncidentStations(vehicles) {
        // Extract unique station_id + region_id pairs
        const stationKeys = new Map();
        vehicles.forEach(v => {
            if (!v.station_id || !v.region_id) return;
            const key = v.region_id + ':' + v.station_id;
            if (!stationKeys.has(key)) {
                stationKeys.set(key, { sid: v.station_id, rid: v.region_id, disc: v.discipline, name: v.station_name });
            }
        });
        if (stationKeys.size === 0) return;

        // Load stations data if not cached
        if (!this._stationsData) {
            try {
                const res = await fetch('/data/stations.json');
                this._stationsData = await res.json();
            } catch (e) { return; }
        }

        // Remove previous layer
        this.clearIncidentStations();

        const DISC_ICONS = { Brandweer: '/img/brandweer.svg', Ambulance: '/img/ambulance.svg', Politie: '/img/politie.svg', Lifeliner: '/img/lifeliner.svg' };
        const group = L.layerGroup();
        const points = [];

        // Include incident location
        const inc = this.selected ? this.incidents.get(this.selected) : null;
        if (inc && inc.lat && inc.lng) points.push([inc.lat, inc.lng]);

        stationKeys.forEach(({ sid, rid, disc, name }) => {
            const s = this._stationsData.find(st => st.id === sid && st.rid === rid);
            if (!s || !s.lat || !s.lng) return;
            points.push([s.lat, s.lng]);
            const iconUrl = DISC_ICONS[disc] || DISC_ICONS[s.d] || '/img/brandweer.svg';
            const icon = L.divIcon({
                html: `<div class="inc-station-pin"><img src="${iconUrl}" width="16" height="16"></div>`,
                iconSize: [28, 28],
                iconAnchor: [14, 14],
                className: '',
            });
            L.marker([s.lat, s.lng], { icon, interactive: true, zIndexOffset: 500 })
                .bindTooltip(name || s.name, { className: 'vr-tooltip' })
                .addTo(group);

            // Route lines from station to incident
            if (inc && inc.lat && inc.lng) {
                if (disc === 'Lifeliner' || s.d === 'Lifeliner') {
                    // Straight dashed line for helicopters (they fly direct)
                    L.polyline([[s.lat, s.lng], [inc.lat, inc.lng]], {
                        color: '#9b59b6',
                        weight: 2,
                        dashArray: '6, 8',
                        opacity: 0.7,
                    }).addTo(group);
                } else {
                    // OSRM road route for ground vehicles
                    const coords = `${s.lng},${s.lat};${inc.lng},${inc.lat}`;
                    const selId = this.selected;
                    fetch(`/api/v1/osrm/route?coords=${encodeURIComponent(coords)}`)
                        .then(r => r.json())
                        .then(data => {
                            if (this.selected !== selId) return;
                            if (data?.routes?.[0]?.geometry?.coordinates) {
                                const latLngs = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
                                const color = PIN_COLORS[disc] || PIN_COLORS[s.d] || '#718096';
                                L.polyline(latLngs, { color, weight: 3, dashArray: '8, 10', opacity: 0.6 }).addTo(group);
                            }
                        })
                        .catch(() => {});
                }
            }
        });

        this._incidentStationLayer = group;
        group.addTo(this.map);

        // Fit map to show incident + all stations
        if (points.length > 1 && this._mapUsable()) {
            const bounds = L.latLngBounds(points).pad(0.15);
            const dp = document.getElementById('detailPanel');
            const dpW = dp && dp.classList.contains('open') ? (dp.offsetWidth || 380) : 0;
            this.map.flyToBounds(bounds, {
                paddingTopLeft: [30, 30],
                paddingBottomRight: [dpW + 30, 30],
                maxZoom: 14,
                duration: 0.8,
            });
        }
    },

    clearIncidentStations() {
        if (this._incidentStationLayer) {
            this._incidentStationLayer.remove();
            this._incidentStationLayer = null;
        }
        this.clearVehicleAnimations();
    },

    _vehicleAnimFrameId: null,
    _vehicleAnimMarkers: [],

    clearVehicleAnimations() {
        if (this._vehicleAnimFrameId) {
            cancelAnimationFrame(this._vehicleAnimFrameId);
            this._vehicleAnimFrameId = null;
        }
        this._vehicleAnimMarkers.forEach(m => m.remove());
        this._vehicleAnimMarkers = [];
    },

    animateVehiclePositions(vehicles, inc) {
        this.clearVehicleAnimations();
        if (!inc || !inc.lat || !inc.lng || !this.map) return;

        const map = this.map;
        const destLat = inc.lat, destLng = inc.lng;
        const markers = [];
        const routePromises = [];

        vehicles.forEach((v, idx) => {
            if (!v.station_lat || !v.station_lng || !v.first_seen_at || !v.drive_time) return;
            if (v.position_source === 'live') return;

            const color = PIN_COLORS[v.discipline] || '#718096';
            const stagger = idx * 5;
            const alertedAt = new Date(v.first_seen_at).getTime() / 1000;
            const dispatchDelay = (v.dispatch_delay_seconds || 420) + stagger;
            const driveTime = v.drive_time;
            const arrivalTime = alertedAt + dispatchDelay + driveTime;

            const dotSize = 12;
            const waitingHtml = `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 0 ${color};animation:veh-pulse 1.5s ease-in-out infinite;"></div>`;
            const movingHtml = `<div style="width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5);"></div>`;
            const html = waitingHtml;
            const marker = L.marker([v.station_lat, v.station_lng], {
                icon: L.divIcon({ className: '', html, iconSize: [dotSize, dotSize], iconAnchor: [dotSize / 2, dotSize / 2] }),
                zIndexOffset: 3000,
            });
            const label = `${v.vehicle_id || '?'} — ${v.description || v.vehicle_type || ''}`;
            marker.bindTooltip(this.esc(label), { direction: 'top', offset: [0, -8] });

            const waitingIcon = L.divIcon({ className: '', html: waitingHtml, iconSize: [dotSize, dotSize], iconAnchor: [dotSize / 2, dotSize / 2] });
            const movingIcon = L.divIcon({ className: '', html: movingHtml, iconSize: [dotSize, dotSize], iconAnchor: [dotSize / 2, dotSize / 2] });
            const entry = { marker, alertedAt, dispatchDelay, driveTime, arrivalTime, added: false, moving: false, waitingIcon, movingIcon, route: null, segLengths: null, totalLength: 0 };
            markers.push(entry);

            // Fetch route
            if (v.discipline === 'Lifeliner') {
                entry.route = [[v.station_lat, v.station_lng], [destLat, destLng]];
                this._calcSegments(entry);
            } else {
                const coords = `${v.station_lng},${v.station_lat};${destLng},${destLat}`;
                routePromises.push(
                    fetch(`/api/v1/osrm/route?coords=${encodeURIComponent(coords)}`)
                        .then(r => r.json())
                        .then(data => {
                            if (data?.routes?.[0]?.geometry?.coordinates) {
                                entry.route = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
                                this._calcSegments(entry);
                            }
                        })
                        .catch(() => {})
                );
            }
        });

        this._vehicleAnimMarkers = markers.map(e => e.marker);

        const self = this;
        Promise.all(routePromises).then(() => {
            function tick() {
                const now = Date.now() / 1000;
                let anyActive = false;

                markers.forEach(e => {
                    if (!e.route || !e.segLengths) return;

                    if (now >= e.arrivalTime) {
                        if (e.added) { e.marker.remove(); e.added = false; }
                        return;
                    }

                    anyActive = true;

                    if (now < e.alertedAt + e.dispatchDelay) {
                        if (!e.added) { e.marker.setLatLng(e.route[0]).setIcon(e.waitingIcon).addTo(map); e.added = true; }
                        return;
                    }

                    // Switch naar moving icon bij uitruk
                    if (!e.moving) { e.marker.setIcon(e.movingIcon); e.moving = true; }

                    const elapsed = now - e.alertedAt - e.dispatchDelay;
                    const fraction = Math.min(1, elapsed / e.driveTime);
                    const targetDist = fraction * e.totalLength;

                    let traveled = 0;
                    for (let i = 0; i < e.segLengths.length; i++) {
                        if (traveled + e.segLengths[i] >= targetDist) {
                            const segFrac = (targetDist - traveled) / e.segLengths[i];
                            const p1 = e.route[i], p2 = e.route[i + 1];
                            const lat = p1[0] + (p2[0] - p1[0]) * segFrac;
                            const lng = p1[1] + (p2[1] - p1[1]) * segFrac;
                            if (!e.added) { e.marker.addTo(map); e.added = true; }
                            e.marker.setLatLng([lat, lng]);
                            break;
                        }
                        traveled += e.segLengths[i];
                    }
                });

                if (anyActive) {
                    self._vehicleAnimFrameId = requestAnimationFrame(tick);
                }
            }
            tick();
        });
    },

    _calcSegments(entry) {
        if (!entry.route || entry.route.length < 2) return;
        const segs = [];
        let total = 0;
        for (let i = 1; i < entry.route.length; i++) {
            const d = this.map.distance(L.latLng(entry.route[i - 1]), L.latLng(entry.route[i]));
            segs.push(d);
            total += d;
        }
        entry.segLengths = segs;
        entry.totalLength = total;
    },

    renderDetailHeader(inc) {
        const title = document.getElementById('dpTitle');
        const sub = document.getElementById('dpSub');
        const link = document.getElementById('dpLink');
        if (title) title.textContent = inc.incident_type || (inc.disciplines || ['Melding'])[0];
        if (sub) sub.textContent = (inc.location || inc.city || '\u2013') + ' \u00B7 ' + this.formatTime(inc.first_message_at);
        if (link) link.href = this.incidentUrl(inc);

        // Follow knop (subtiel, naast externe link)
        const existingFollow = document.getElementById('dpFollowBtn');
        if (existingFollow) existingFollow.remove();
        if (this.config.isAuth && link && inc.status !== 'closed') {
            const bellSvg = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>';
            const fbtn = document.createElement('button');
            fbtn.id = 'dpFollowBtn';
            fbtn.title = 'Volg dit incident';
            fbtn.style.cssText = 'background:none;border:none;cursor:pointer;color:var(--tx3);padding:2px;margin-left:4px;opacity:.6;';
            fbtn.innerHTML = bellSvg;
            fbtn.addEventListener('mouseenter', () => fbtn.style.opacity = '1');
            fbtn.addEventListener('mouseleave', () => { if (fbtn.dataset.following !== '1') fbtn.style.opacity = '.6'; });

            const setFollowState = (active) => {
                fbtn.dataset.following = active ? '1' : '0';
                fbtn.style.color = active ? '#3498db' : 'var(--tx3)';
                fbtn.style.opacity = active ? '1' : '.6';
                fbtn.title = active ? 'Volgend (klik om te stoppen)' : 'Volg dit incident';
            };

            fetch('/api/v1/incidents/' + inc.id + '/follow', { credentials: 'same-origin' })
                .then(r => { if (!r.ok) throw r; return r.json(); })
                .then(d => { if (d.data?.is_following) setFollowState(true); })
                .catch(() => {});

            fbtn.addEventListener('click', () => {
                const isFollowing = fbtn.dataset.following === '1';
                fetch('/api/v1/incidents/' + inc.id + '/follow', {
                    method: isFollowing ? 'DELETE' : 'POST',
                    headers: apiHeaders(true),
                    credentials: 'same-origin',
                }).then(r => r.json()).then(d => {
                    if (d.data) setFollowState(d.data.is_following);
                });
            });
            link.parentNode.insertBefore(fbtn, link.nextSibling);
        }

        // Deployment tracking knop in header (subtiel, naast externe link)
        const existingBtn = document.getElementById('dpDeployBtn');
        if (existingBtn) existingBtn.remove();
        if (this.config.isAuth && link) {
            const plusSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';
            const checkSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2ecc71" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>';

            const btn = document.createElement('button');
            btn.id = 'dpDeployBtn';
            btn.title = 'Inzet bijhouden';
            btn.style.cssText = 'background:none;border:none;cursor:pointer;color:var(--tx3);padding:2px;margin-left:4px;opacity:.6;';
            btn.innerHTML = plusSvg;
            btn.addEventListener('mouseenter', () => { if (!btn.disabled) btn.style.opacity = '1'; });
            btn.addEventListener('mouseleave', () => { if (!btn.disabled) btn.style.opacity = '.6'; });

            // Check of er al een deployment bestaat
            fetch('/api/v1/deployments?per_page=1&incident_id=' + inc.id, { credentials: 'same-origin' })
                .then(r => r.json())
                .then(d => {
                    const existing = (d.data || []).find(dep => dep.incident_id === inc.id || dep.incident_id === String(inc.id));
                    if (existing) {
                        btn.innerHTML = checkSvg;
                        btn.title = 'Inzet al bijgehouden';
                        btn.style.opacity = '1';
                        btn.disabled = true;
                    }
                }).catch(() => {});

            btn.addEventListener('click', () => {
                if (btn.disabled) return;
                fetch('/api/v1/deployments', {
                    method: 'POST',
                    headers: apiHeaders(true),
                    credentials: 'same-origin',
                    body: JSON.stringify({ incident_id: inc.id }),
                }).then(r => r.json()).then(d => {
                    btn.innerHTML = checkSvg;
                    btn.title = d.errors ? d.errors[0].message : 'Inzet toegevoegd';
                    btn.style.opacity = '1';
                    btn.disabled = true;
                });
            });
            link.parentNode.insertBefore(btn, link.nextSibling);
        }
    },

    renderTab(inc, tab) {
        const body = document.getElementById('dpBody');
        if (!body) return;
        document.querySelectorAll('.dp-tab').forEach(t => t.classList.toggle('on', t.dataset.tab === tab));
        this.activeTab = tab;

        if (tab === 'info') this.renderTabInfo(inc, body);
        else if (tab === 'eenheden') this.renderTabEenheden(inc, body);
        else if (tab === 'media') this.renderTabMedia(inc, body);
        else if (tab === 'kladblok') this.renderTabKladblok(inc, body);
    },

    renderTabInfo(inc, body) {
        // Escalation bar
        const lvl = inc.escalation_level || 0;
        let escBar = '<div class="sl">Escalatieniveau</div><div class="esc-bar">';
        for (let i = 1; i <= 9; i++) {
            const cls = i <= lvl ? (i <= 3 ? 'l1' : i <= 6 ? 'l2' : 'l3') : '';
            escBar += `<div class="es ${cls}"></div>`;
        }
        const ESC_LABELS = {1:'Klein incident',2:'Middel incident',3:'Groot incident',4:'Zeer groot incident',5:'GRIP 1',6:'GRIP 2',7:'GRIP 3',8:'GRIP 4',9:'GRIP 5'};
        const ESC_CAT_LABELS = {BR:'brand',HV:'hulpverlening',WO:'waterongeval',IBGS:'incident'};
        function escLabel(level, cat) {
            if (level >= 5) return ESC_LABELS[level] || 'GRIP';
            const c = (cat||'').toUpperCase();
            const cl = ESC_CAT_LABELS[c] || '';
            if (!cl) return ESC_LABELS[level] || '';
            if (cl === 'brand') return level === 2 ? 'Middelbrand' : (level >= 4 ? 'Zeer grote brand' : level >= 3 ? 'Grote brand' : 'Kleine brand');
            const sz = level >= 4 ? 'Zeer grote' : level >= 3 ? 'Grote' : 'Middel';
            return sz + ' ' + cl;
        }
        const lbl = escLabel(lvl, inc.escalation_category) || ESC_LABELS[lvl] || (lvl > 0 ? `Level ${lvl}` : 'Onbekend');
        escBar += `<span class="esc-lbl">${lbl}</span></div>`;

        // Body
        let html = `<div class="sl">Melding</div>`;
        html += `<div class="info-row" style="white-space:normal;line-height:1.4">${this.esc(inc.body || '\u2013')}</div>`;

        // Status badges
        let statusHtml = '';
        if (inc.is_administrative) statusHtml += '<span class="badge b-over">Administratief</span> ';
        if (inc.is_cancelled) statusHtml += '<span class="badge b-grip2">Ingetrokken</span> ';
        if (inc.is_test) statusHtml += '<span class="badge b-over">Test</span> ';
        if (inc.status === 'closed') statusHtml += '<span class="badge b-over">Gesloten</span> ';
        if (statusHtml) html += `<div style="margin-bottom:8px">${statusHtml}</div>`;

        // Location
        const accLabel = ACC_LABELS[inc.location_accuracy] || 'Onbekend';
        html += `<div class="sl">Locatie <span class="sl-sub">${this.esc(accLabel)}</span></div>`;
        const streetDisplay = (inc.location && inc.location.indexOf(',') !== -1)
            ? inc.location.split(',')[0].trim()
            : inc.street;
        if (streetDisplay) html += `<div class="info-row"><b>Straat</b>${this.esc(streetDisplay)}</div>`;
        if (inc.city) html += `<div class="info-row"><b>Gemeente</b>${this.esc(inc.city)}</div>`;
        else if (inc.location) html += `<div class="info-row"><b>Locatie</b>${this.esc(inc.location)}</div>`;
        if (inc.region_name) html += `<div class="info-row"><b>Regio</b>${this.esc(inc.region_name)}</div>`;
        if (this.fLocLat !== null && inc.lat && inc.lng) {
            const dist = this.haversineKm(this.fLocLat, this.fLocLng, inc.lat, inc.lng);
            html += `<div class="info-row"><b>Afstand</b>${dist.toFixed(1)} km van jouw locatie</div>`;
        }

        // Weather
        if (inc._weather) {
            const w = inc._weather;
            const temp = parseFloat(w.temperature_c);
            const wind = parseFloat(w.wind_speed_kmh);
            const gust = parseFloat(w.wind_gust_kmh);
            const vis = parseFloat(w.visibility_km);
            const precip = parseFloat(w.precipitation_mm);
            const humid = parseInt(w.humidity_pct);

            let weatherHtml = `<div class="wx-bar">`;
            weatherHtml += `<span class="wx-item">\u{1F321} ${temp}\u00B0C</span>`;
            weatherHtml += `<span class="wx-item">\u{1F4A8} ${wind} km/u ${this.esc(w.wind_dir)}${gust > wind + 10 ? ` (${gust})` : ''}</span>`;
            if (precip > 0) weatherHtml += `<span class="wx-item">\u{1F327} ${precip} mm</span>`;
            weatherHtml += `<span class="wx-item">\u{1F441} ${vis} km</span>`;
            weatherHtml += `<span class="wx-item">\u{1F4A7} ${humid}%</span>`;
            weatherHtml += `</div>`;

            // Waarschuwingen
            let warnings = [];
            if (wind >= 50) warnings.push('Sterke wind');
            if (gust >= 70) warnings.push('Zware windstoten');
            if (vis < 1) warnings.push('Beperkt zicht');
            if (temp < 0) warnings.push('Mogelijk gladheid');
            if (warnings.length > 0) {
                weatherHtml += `<div class="wx-warn">\u26A0 ${warnings.join(' \u00B7 ')}</div>`;
            }

            html += `<div class="sl">Weer <span class="sl-sub">${this.esc(w.condition_text)}</span></div>`;
            html += weatherHtml;
        }

        // Messages
        const ccOn = this._ccMode !== 'off' ? ' on' : '';
        const ccLabelsMap = { off: 'Capcodes', names: 'Namen', nums: 'Nummers' };
        const ccToggle = `<button class="tl-cc-toggle${ccOn}" id="tlCcToggle" title="Capcodes tonen/verbergen">${ccLabelsMap[this._ccMode]}</button>`;
        const ccWrapClass = this._ccMode !== 'off' ? 'show-cc-' + this._ccMode : '';
        if (inc._messages) {
            html += `<div class="sl" style="display:flex;align-items:center;justify-content:space-between;">${inc._messages.length} bericht${inc._messages.length !== 1 ? 'en' : ''} ${ccToggle}</div>`;
            html += `<div id="tlWrap" class="${ccWrapClass}">${this.buildMessagesHtml(inc._messages, inc._vehicles || inc.vehicles)}</div>`;
        } else {
            html += `<div class="sl" style="display:flex;align-items:center;justify-content:space-between;" id="msgTitle">Berichten laden... ${ccToggle}</div>`;
            html += `<div id="msgContainer" class="${ccWrapClass}"></div>`;
        }

        body.innerHTML = escBar + html;
    },

    renderTabEenheden(inc, body) {
        const vehicles = inc._vehicles || inc.vehicles || [];

        if (!this.isPro()) {
            body.innerHTML = `<div class="lock">
                <div class="lock-t">Inloggen vereist</div>
                <div class="lock-s">Log in om voertuigen (${inc.unit_count || 0}) en capcodes te bekijken</div>
                <a href="/login" class="upg">Inloggen</a>
            </div>`;
            return;
        }

        let vhtml = `<div class="sl">Voertuigen${vehicles.length ? ` <span class="sl-badge">${vehicles.length}</span>` : ''}</div>`;
        if (vehicles.length) {
            vhtml += '<div class="vl">';
            vehicles.forEach(v => {
                const dc = { Brandweer: 'vs-bw', Ambulance: 'vs-amb', Politie: 'vs-pol', Lifeliner: 'vs-life', KNRM: 'vs-knrm', Reddingsbrigade: 'vs-knrm', Veiligheidsregio: 'vs-vr', Meldkamer: 'vs-vr' };
                const cls = dc[v.discipline] || 'vs-bw';
                const fmtId = this.formatVehicleId(v.vehicle_id, v.discipline);
                const desc = v.description || v.vehicle_id || '\u2013';

                // Detail rows for expanded view
                let details = '';
                if (v.vehicle_type) details += `<div class="vi-dr"><span class="vi-dl">Type</span><span>${this.esc(v.vehicle_type)}</span></div>`;
                if (v.station_name) details += `<div class="vi-dr"><span class="vi-dl">Kazerne</span><span>${this.esc(v.station_name)}</span></div>`;
                if (v.region_name) details += `<div class="vi-dr"><span class="vi-dl">Regio</span><span>${this.esc(v.region_name)}</span></div>`;
                if (v.brand) details += `<div class="vi-dr"><span class="vi-dl">Merk</span><span>${this.esc(v.brand)}</span></div>`;
                if (v.alert_time) {
                    const at = new Date(v.alert_time);
                    details += `<div class="vi-dr"><span class="vi-dl">Alarmering</span><span>${String(at.getHours()).padStart(2,'0')}:${String(at.getMinutes()).padStart(2,'0')}</span></div>`;
                }
                if (v.drive_time != null) {
                    const mins = Math.floor(v.drive_time / 60);
                    const secs = v.drive_time % 60;
                    details += `<div class="vi-dr"><span class="vi-dl">Rijtijd</span><span>${mins} min ${secs}s</span></div>`;
                }
                if (v.alert_time && v.drive_time != null) {
                    const dispDelay = v.dispatch_delay_seconds || 420;
                    const etaMs = new Date(v.alert_time).getTime() + (dispDelay + v.drive_time) * 1000;
                    const eta = new Date(etaMs);
                    details += `<div class="vi-dr"><span class="vi-dl">Aankomst (est.)</span><span>${String(eta.getHours()).padStart(2,'0')}:${String(eta.getMinutes()).padStart(2,'0')}</span></div>`;
                }
                if (v.vehicle_id) details += `<div class="vi-dr"><span class="vi-dl"></span><a href="/voertuig/${encodeURIComponent(v.vehicle_id)}" class="vi-link" target="_blank">Bekijk voertuig &rarr;</a></div>`;

                vhtml += `<div class="vi" data-expandable>
                    <span class="vs ${cls}">${DISC_LETTER[v.discipline] || (v.discipline || '').substring(0, 1)}</span>
                    <div class="vi-main">
                        <div class="vi-hd">
                            <span class="vc">${this.esc(desc)}</span>
                            <svg class="vi-chev" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                        </div>
                        <div class="vt2">${fmtId ? `<span class="vi-id">${this.esc(fmtId)}</span>` : ''}${parseInt(v.photo_count) > 0 ? '<svg class="vi-cam" viewBox="0 0 16 16" width="12" height="12" title="Heeft foto\'s"><path d="M2 4h2l1-1.5h6L12 4h2a1 1 0 011 1v7a1 1 0 01-1 1H2a1 1 0 01-1-1V5a1 1 0 011-1z" fill="none" stroke="currentColor" stroke-width="1.2"/><circle cx="8" cy="8" r="2.5" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>' : ''}${this.esc(v.vehicle_type || '\u2013')}${v.station_name ? ' \u00B7 ' + this.esc(v.station_name) : ''}</div>
                        ${details ? `<div class="vi-details">${details}</div>` : ''}
                    </div>
                </div>`;
            });
            vhtml += '</div>';
        } else {
            vhtml += '<div class="empty-state" style="padding:12px">Geen voertuigen bekend</div>';
        }

        // Posten sectie — groepeer voertuigen per station
        const stationMap = new Map();
        vehicles.forEach(v => {
            if (!v.station_name || !v.station_id) return;
            const key = (v.region_id || '0') + ':' + v.station_id;
            if (!stationMap.has(key)) {
                stationMap.set(key, {
                    name: v.station_name,
                    region_name: v.region_name || '',
                    region_slug: v.region_slug || '',
                    station_slug: v.station_slug || '',
                    discipline: v.discipline || '',
                    count: 0,
                });
            }
            stationMap.get(key).count++;
        });

        if (stationMap.size > 0) {
            vhtml += `<div class="sl" style="margin-top:12px">Posten <span class="sl-badge">${stationMap.size}</span></div>`;
            vhtml += '<div class="vl">';
            for (const [, st] of stationMap) {
                const dc = { Brandweer: 'vs-bw', Ambulance: 'vs-amb', Politie: 'vs-pol', Lifeliner: 'vs-life', KNRM: 'vs-knrm', Reddingsbrigade: 'vs-knrm', Veiligheidsregio: 'vs-vr', Meldkamer: 'vs-vr' };
                const cls = dc[st.discipline] || 'vs-bw';
                const href = st.region_slug && st.station_slug ? `/kazerne/${encodeURIComponent(st.region_slug)}/${encodeURIComponent(st.station_slug)}` : '';
                vhtml += `<div class="vi vi-station">
                    <span class="vs ${cls}">${(st.discipline || '').substring(0, 1) || 'P'}</span>
                    <div class="vi-main">
                        <div class="vi-hd">
                            ${href ? `<a href="${href}" class="vc" target="_blank" style="text-decoration:none;color:inherit">${this.esc(st.name)}</a>` : `<span class="vc">${this.esc(st.name)}</span>`}
                            <span class="sl-badge">${st.count}</span>
                        </div>
                        <div class="vt2">${this.esc(st.region_name)}</div>
                    </div>
                </div>`;
            }
            vhtml += '</div>';
        }

        // Dichtstbijzijnde voertuigen knop
        if (inc.lat && inc.lng) {
            vhtml += `<button class="nv-btn" id="nearestVehiclesBtn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 000 20M2 12h20"/></svg>
                Dichtstbijzijnde voertuigen
            </button>`;
        }

        body.innerHTML = vhtml;

        // Expand/collapse
        body.querySelectorAll('.vi[data-expandable]').forEach(el => {
            el.querySelector('.vi-hd')?.addEventListener('click', () => el.classList.toggle('expanded'));
        });

        // Nearest vehicles overlay
        document.getElementById('nearestVehiclesBtn')?.addEventListener('click', () => {
            this.openNearestVehicles(inc);
        });
    },

    async openNearestVehicles(inc) {
        let overlay = document.getElementById('nvOverlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'nvOverlay';
            overlay.className = 'nv-overlay';
            document.body.appendChild(overlay);
        }
        overlay.innerHTML = `<div class="nv-panel">
            <div class="nv-header">
                <h3>Dichtstbijzijnde voertuigen</h3>
                <button class="nv-close" id="nvClose">&times;</button>
            </div>
            <div class="nv-filters" id="nvFilters"></div>
            <div class="nv-body" id="nvBody"><div class="nv-loading"><div class="nv-spinner"></div>Voertuigen ophalen...</div></div>
        </div>`;
        overlay.classList.add('open');
        document.getElementById('nvClose').addEventListener('click', () => overlay.classList.remove('open'));
        overlay.addEventListener('click', e => { if (e.target === overlay) overlay.classList.remove('open'); });

        try {
            // Collect deployed vehicle IDs to exclude
            const deployed = (inc._vehicles || inc.vehicles || []).map(v => (v.vehicle_id || '').replace(/-/g, '')).filter(Boolean);
            const excludeParam = deployed.length ? `&exclude=${deployed.join(',')}` : '';
            const res = await fetch(`/api/v1/vehicles/nearest-by-type?lat=${inc.lat}&lng=${inc.lng}${excludeParam}`, { credentials: 'same-origin' });
            const json = await res.json();
            if (!res.ok || !json.data || !Array.isArray(json.data) || json.data.length === 0) {
                document.getElementById('nvBody').innerHTML = `<div class="nv-empty">${this.esc(json.errors?.[0]?.message || 'Geen voertuigen gevonden')}</div>`;
                return;
            }

            const items = json.data;

            // API levert al drive_time, drive_distance_km, dispatch_delay_seconds en total_response_seconds
            // Sorteer op totale responstijd (uitruk + rijtijd), fallback op afstand
            items.sort((a, b) => (a.total_response_seconds ?? Infinity) - (b.total_response_seconds ?? Infinity) || (a.drive_distance_km ?? a.distance_km) - (b.drive_distance_km ?? b.distance_km));

            // Build filter pills from vehicle types
            const types = [...new Set(items.map(v => v.vehicle_type))];
            this._nvItems = items;
            this._nvFilter = null;

            const filtersEl = document.getElementById('nvFilters');
            let filterHtml = '<button class="nv-pill on" data-type="">Alles</button>';
            types.forEach(t => {
                filterHtml += `<button class="nv-pill" data-type="${this.esc(t)}">${this.esc(t)}</button>`;
            });
            filtersEl.innerHTML = filterHtml;
            filtersEl.addEventListener('click', e => {
                const pill = e.target.closest('.nv-pill');
                if (!pill) return;
                filtersEl.querySelectorAll('.nv-pill').forEach(p => p.classList.remove('on'));
                pill.classList.add('on');
                this._nvFilter = pill.dataset.type || null;
                this._renderNvList();
            });

            this._renderNvList();
        } catch (e) {
            document.getElementById('nvBody').innerHTML = '<div class="nv-empty">Kon gegevens niet laden</div>';
        }
    },

    _renderNvList() {
        const body = document.getElementById('nvBody');
        if (!body || !this._nvItems) return;

        let items;
        if (this._nvFilter) {
            // Show all (up to 4) for the selected type
            items = this._nvItems.filter(v => v.vehicle_type === this._nvFilter);
        } else {
            // Show only the nearest per type
            const seen = new Set();
            items = this._nvItems.filter(v => {
                if (seen.has(v.vehicle_type)) return false;
                seen.add(v.vehicle_type);
                return true;
            });
        }

        if (items.length === 0) {
            body.innerHTML = '<div class="nv-empty">Geen voertuigen van dit type in de buurt</div>';
            return;
        }

        const dc = { Brandweer: 'vs-bw', Ambulance: 'vs-amb', Politie: 'vs-pol', Lifeliner: 'vs-life', KNRM: 'vs-knrm' };
        let html = '';
        items.forEach((v, i) => {
            const cls = dc[v.discipline] || 'vs-bw';
            const km = v.drive_distance_km ?? v.distance_km;
            function fmtMs(sec) { const m = Math.floor(sec / 60); const s = sec % 60; return s > 0 ? m + ':' + String(s).padStart(2, '0') : m + ':00'; }
            const driveStr = v.drive_time != null ? fmtMs(v.drive_time) : null;
            const delay = v.dispatch_delay_seconds || 270;
            const delayStr = fmtMs(delay);
            const isPro = v.is_professional || delay < 120;
            const totalSec = v.total_response_seconds;
            const totalStr = totalSec != null ? fmtMs(totalSec) : null;
            const proLabel = isPro ? 'Beroeps' : 'Vrijw.';
            html += `<div class="nv-row${i === 0 && this._nvFilter ? ' nv-row-first' : ''}">
                <span class="vs ${cls}">${(v.discipline || '')[0] || '?'}</span>
                <div class="nv-info">
                    <span class="nv-type">${this.esc(v.vehicle_type)}</span>
                    <span class="nv-desc">${this.esc(v.description || v.vehicle_id)} &middot; ${this.esc(v.station_name)} <span style="opacity:.6;font-size:11px;">(${proLabel})</span></span>
                </div>
                <div class="nv-dist">
                    <span class="nv-km">${km} km</span>
                    ${driveStr ? `<span class="nv-min" style="opacity:.6;font-size:11px;" title="Rijtijd ${driveStr} + uitruk ${delayStr}">${driveStr}+${delayStr}</span>` : ''}
                    ${totalStr ? `<span class="nv-min" title="Totale responstijd">&#127937; ${totalStr}</span>` : ''}
                </div>
            </div>`;
        });
        body.innerHTML = html;
    },

    renderTabMedia(inc, body) {
        let html = '';

        // Goedgekeurde media
        const media = inc._media || [];
        const photos = media.filter(m => m.type === 'photo');
        const links = media.filter(m => m.type === 'link');

        if (photos.length > 0) {
            html += '<div class="sl">Foto\'s</div>';
            html += '<div class="media-photos">';
            photos.forEach((p, idx) => {
                html += `<div class="media-photo-thumb" data-idx="${idx}" data-photos='${this.esc(JSON.stringify(photos))}'>`;
                html += `<img src="${this.esc(p.url)}" alt="${this.esc(p.caption || 'Foto')}" loading="lazy">`;
                const capParts = [p.caption, p.submitter_name ? '\u00A9 ' + p.submitter_name : ''].filter(Boolean);
                if (capParts.length) html += `<span class="media-photo-cap">${this.esc(capParts.join(' \u2014 '))}</span>`;
                html += '</div>';
            });
            html += '</div>';
        }

        if (links.length > 0) {
            html += '<div class="sl">Links</div>';
            links.forEach(l => {
                if (!/^https?:\/\//i.test(l.url || '')) return;
                let domain = '';
                try { domain = new URL(l.url).hostname.replace(/^www\./, ''); } catch {}
                html += `<a href="${this.esc(l.url)}" target="_blank" rel="noopener noreferrer" class="media-link-card">`;
                if (l.og_image_url) {
                    html += `<img src="${this.esc(l.og_image_url)}" alt="" class="media-link-og" loading="lazy">`;
                }
                html += '<div class="media-link-info">';
                html += `<span class="media-link-title">${this.esc(l.caption || domain || l.url)}</span>`;
                html += `<span class="media-link-domain">${this.esc(domain)}${l.submitter_name ? ' \u00B7 \u00A9 ' + this.esc(l.submitter_name) : ''}</span>`;
                html += '</div></a>';
            });
        }

        if (media.length === 0) {
            html += '<div class="empty-state" style="padding:16px;text-align:center;font-size:12px;color:var(--tx2)">Nog geen media toegevoegd</div>';
        }

        // Formulier om media toe te voegen
        html += '<div class="sl" style="margin-top:20px">Toevoegen</div>';
        html += `<div class="media-submit-form" id="mediaSubmitForm">
            <div class="media-submit-tabs">
                <button class="media-submit-tab on" data-mtype="link">Link</button>
                <button class="media-submit-tab" data-mtype="photo">Foto</button>
            </div>
            <div id="mediaFormLink" class="media-form-section">
                <input type="url" id="mediaLinkUrl" class="media-input" placeholder="https://..." autocomplete="off">
                <input type="text" id="mediaLinkCaption" class="media-input" placeholder="Beschrijving (optioneel)" maxlength="500">
                <input type="text" id="mediaLinkName" class="media-input" placeholder="Je naam (voor credits)" maxlength="100">
                <button class="media-submit-btn" id="mediaSubmitLink">Verstuur link</button>
            </div>
            <div id="mediaFormPhoto" class="media-form-section" style="display:none">
                <input type="file" id="mediaPhotoFile" accept="image/jpeg,image/png,image/webp,image/gif" style="display:none">
                <div class="media-file-label" id="mediaFileLabel" role="button">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    <span id="mediaFileLabelText">Kies een foto (max 10 MB)</span>
                </div>
                <input type="text" id="mediaPhotoCaption" class="media-input" placeholder="Beschrijving (optioneel)" maxlength="500">
                <input type="text" id="mediaPhotoName" class="media-input" placeholder="Je naam (voor credits)" maxlength="100">
                <button class="media-submit-btn" id="mediaSubmitPhoto" disabled>Upload foto</button>
            </div>
            <div id="mediaSubmitStatus" class="media-submit-status" style="display:none"></div>
        </div>`;

        body.innerHTML = html;

        // Tab switching
        body.querySelectorAll('.media-submit-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                body.querySelectorAll('.media-submit-tab').forEach(t => t.classList.toggle('on', t === tab));
                document.getElementById('mediaFormLink').style.display = tab.dataset.mtype === 'link' ? '' : 'none';
                document.getElementById('mediaFormPhoto').style.display = tab.dataset.mtype === 'photo' ? '' : 'none';
            });
        });

        // File input
        const fileInput = document.getElementById('mediaPhotoFile');
        const fileLabel = document.getElementById('mediaFileLabel');
        const fileLabelText = document.getElementById('mediaFileLabelText');
        const photoBtn = document.getElementById('mediaSubmitPhoto');
        if (fileInput && fileLabel) {
            fileLabel.addEventListener('click', () => fileInput.click());
            fileInput.addEventListener('change', () => {
                if (fileInput.files.length > 0) {
                    if (fileLabelText) fileLabelText.textContent = fileInput.files[0].name;
                    photoBtn.disabled = false;
                }
            });
        }

        // Submit link
        document.getElementById('mediaSubmitLink')?.addEventListener('click', () => this.submitMedia(inc.id, 'link'));
        // Submit photo
        document.getElementById('mediaSubmitPhoto')?.addEventListener('click', () => this.submitMedia(inc.id, 'photo'));

        // Lightbox click handler
        body.querySelectorAll('.media-photo-thumb').forEach(thumb => {
            thumb.addEventListener('click', () => {
                const photos = JSON.parse(thumb.dataset.photos);
                const idx = parseInt(thumb.dataset.idx, 10);
                this.openLightbox(photos, idx);
            });
        });
    },

    async submitMedia(incidentId, type) {
        const statusEl = document.getElementById('mediaSubmitStatus');
        const showStatus = (msg, isError) => {
            if (!statusEl) return;
            statusEl.style.display = '';
            statusEl.className = 'media-submit-status' + (isError ? ' error' : ' success');
            statusEl.textContent = msg;
        };

        try {
            let res;
            if (type === 'link') {
                const url = document.getElementById('mediaLinkUrl')?.value?.trim();
                const caption = document.getElementById('mediaLinkCaption')?.value?.trim();
                const name = document.getElementById('mediaLinkName')?.value?.trim();
                if (!url) { showStatus('Vul een URL in', true); return; }
                res = await fetch(`/api/v1/incidents/${incidentId}/media`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url, caption: caption || null, name: name || null }),
                });
            } else {
                const fileInput = document.getElementById('mediaPhotoFile');
                const caption = document.getElementById('mediaPhotoCaption')?.value?.trim();
                const name = document.getElementById('mediaPhotoName')?.value?.trim();
                if (!fileInput?.files?.length) { showStatus('Kies een foto', true); return; }
                const fd = new FormData();
                fd.append('photo', fileInput.files[0]);
                if (caption) fd.append('caption', caption);
                if (name) fd.append('name', name);
                res = await fetch(`/api/v1/incidents/${incidentId}/media`, {
                    method: 'POST',
                    body: fd,
                });
            }

            const json = await res.json();
            if (!res.ok) {
                showStatus(json.errors?.[0]?.message || 'Fout bij versturen', true);
                return;
            }

            showStatus('Ingediend! Wordt na goedkeuring zichtbaar.', false);
            // Reset form
            if (type === 'link') {
                const urlInput = document.getElementById('mediaLinkUrl');
                const capInput = document.getElementById('mediaLinkCaption');
                if (urlInput) urlInput.value = '';
                if (capInput) capInput.value = '';
            } else {
                const fileInput = document.getElementById('mediaPhotoFile');
                const capInput = document.getElementById('mediaPhotoCaption');
                if (fileInput) fileInput.value = '';
                if (capInput) capInput.value = '';
                document.getElementById('mediaSubmitPhoto').disabled = true;
                const lbl = document.getElementById('mediaFileLabelText');
                if (lbl) lbl.textContent = 'Kies een foto (max 10 MB)';
            }
        } catch (e) {
            showStatus('Netwerkfout: ' + e.message, true);
        }
    },

    handleMediaEvent(data) {
        const incidentId = data.incident_id;
        if (!incidentId) return;
        const inc = this.incidents.get(incidentId);
        if (!inc) return;

        // Update cached media + counts
        if (data.media) {
            inc._media = data.media;
            const photos = data.media.filter(m => m.type === 'photo').length;
            const links = data.media.filter(m => m.type === 'link').length;
            inc.media_counts = { photos, links };
            inc._dirty = true;
        }

        this.updateMediaBadge(incidentId);

        // Re-render if viewing this incident's media tab
        if (this.selected === incidentId && this.activeTab === 'media') {
            const body = document.getElementById('dpBody');
            if (body) this.renderTabMedia(inc, body);
        }
    },

    async fetchIncidentMedia(id) {
        try {
            const res = await fetch(`/api/v1/incidents/${id}/media`);
            const json = await res.json();
            if (!json.data || this.selected !== id) return;
            const inc = this.incidents.get(id);
            if (inc) {
                inc._media = json.data;
                const photos = json.data.filter(m => m.type === 'photo').length;
                const links = json.data.filter(m => m.type === 'link').length;
                inc.media_counts = { photos, links };
                inc._dirty = true;
                this.updateMediaBadge(id);
                if (this.activeTab === 'media') {
                    const body = document.getElementById('dpBody');
                    if (body) this.renderTabMedia(inc, body);
                }
            }
        } catch (e) {
            console.error('Failed to load incident media:', e);
        }
    },

    updateMediaBadge(incidentId) {
        const inc = this.incidents.get(incidentId);
        if (!inc) return;
        const count = (inc._media || []).length;

        // Update tab badge if this incident is selected
        if (this.selected === incidentId) {
            const mediaTab = document.querySelector('.dp-tab[data-tab="media"]');
            if (mediaTab) {
                mediaTab.innerHTML = count > 0
                    ? `Media <span class="tab-badge">${count}</span>`
                    : 'Media';
            }
        }

        // Card update niet nodig — buildCardHtml toont media_counts via emoji's
        // Media counts worden bijgewerkt via handleMediaEvent → _dirty → renderList
    },

    // ========================================
    // Lightbox
    // ========================================

    openLightbox(photos, startIdx) {
        this._lbPhotos = photos;
        this._lbIdx = startIdx;

        // Create lightbox if not exists
        let lb = document.getElementById('lightbox');
        if (!lb) {
            lb = document.createElement('div');
            lb.id = 'lightbox';
            lb.className = 'lb-overlay';
            lb.innerHTML = `
                <div class="lb-close">&times;</div>
                <div class="lb-nav lb-prev">&lsaquo;</div>
                <div class="lb-nav lb-next">&rsaquo;</div>
                <div class="lb-content">
                    <img class="lb-img" id="lbImg" src="" alt="">
                    <div class="lb-caption" id="lbCaption"></div>
                    <div class="lb-counter" id="lbCounter"></div>
                </div>
            `;
            document.body.appendChild(lb);

            lb.querySelector('.lb-close').addEventListener('click', () => this.closeLightbox());
            lb.querySelector('.lb-prev').addEventListener('click', (e) => { e.stopPropagation(); this.lbNav(-1); });
            lb.querySelector('.lb-next').addEventListener('click', (e) => { e.stopPropagation(); this.lbNav(1); });
            lb.addEventListener('click', (e) => { if (e.target === lb) this.closeLightbox(); });

            document.addEventListener('keydown', (e) => {
                if (!document.getElementById('lightbox')?.classList.contains('open')) return;
                if (e.key === 'Escape') this.closeLightbox();
                else if (e.key === 'ArrowLeft') this.lbNav(-1);
                else if (e.key === 'ArrowRight') this.lbNav(1);
            });
        }

        this.lbShow();
        lb.classList.add('open');
        document.body.style.overflow = 'hidden';
    },

    closeLightbox() {
        const lb = document.getElementById('lightbox');
        if (lb) lb.classList.remove('open');
        document.body.style.overflow = '';
    },

    lbNav(dir) {
        if (!this._lbPhotos) return;
        this._lbIdx = (this._lbIdx + dir + this._lbPhotos.length) % this._lbPhotos.length;
        this.lbShow();
    },

    lbShow() {
        const photo = this._lbPhotos[this._lbIdx];
        const img = document.getElementById('lbImg');
        const caption = document.getElementById('lbCaption');
        const counter = document.getElementById('lbCounter');
        if (img) img.src = photo.url;
        if (caption) {
            const parts = [photo.caption, photo.submitter_name ? '\u00A9 ' + photo.submitter_name : ''].filter(Boolean);
            caption.textContent = parts.join(' \u2014 ');
            caption.style.display = parts.length ? '' : 'none';
        }
        if (counter) {
            counter.textContent = this._lbPhotos.length > 1
                ? `${this._lbIdx + 1} / ${this._lbPhotos.length}`
                : '';
        }
    },

    renderTabKladblok(inc, body) {
        const notes = inc._notes || [];
        if (notes.length === 0) {
            body.innerHTML = '<div class="empty-state" style="padding:16px;font-size:12px;color:var(--tx2)">Geen kladblokberichten</div>';
            return;
        }

        let html = '<div class="kb-list">';
        notes.forEach(n => {
            const t = n.created_at ? new Date(n.created_at.replace(' ', 'T')) : null;
            const time = t && !isNaN(t.getTime())
                ? ('0' + t.getHours()).slice(-2) + ':' + ('0' + t.getMinutes()).slice(-2) + ':' + ('0' + t.getSeconds()).slice(-2)
                : '';
            html += `<div class="kb-note">
                <time class="kb-time">${time}</time>
                <span class="kb-body">${this.esc(n.body)}</span>
            </div>`;
        });
        html += '</div>';
        body.innerHTML = html;
    },

    handleNoteEvent(data) {
        const incidentId = parseInt(data.incident_id, 10);
        const note = data.note;
        if (!incidentId || !note) return;

        // Store on incident if loaded
        const inc = this.incidents.get(incidentId);
        if (inc) {
            if (!inc._notes) inc._notes = [];
            if (!inc._notes.some(n => n.id === note.id)) {
                inc._notes.push(note);
            }
        }

        // Update tab if this incident is currently selected
        if (this.selected === incidentId) {
            const kladblokTab = document.querySelector('.dp-tab[data-tab="kladblok"]');
            if (kladblokTab) {
                const count = inc?._notes?.length || 1;
                kladblokTab.style.display = '';
                kladblokTab.innerHTML = `Kladblok <span class="tab-badge">${count}</span>`;
            }
            // Re-render if on kladblok tab
            if (this.activeTab === 'kladblok' && inc) {
                this.renderTab(inc, 'kladblok');
            }
        }
    },

    handleWeatherEvent(data) {
        const incidentId = parseInt(data.incident_id, 10);
        if (!incidentId || !data.weather) return;

        const inc = this.incidents.get(incidentId);
        if (inc) {
            inc._weather = data.weather;
            // Re-render if viewing this incident's info tab
            if (this.selected === incidentId && this.activeTab === 'info') {
                this.renderTab(inc, 'info');
            }
        }
    },

    async fetchIncidentDetail(id) {
        try {
            const res = await fetch(`/api/v1/incidents/${id}`);
            const json = await res.json();
            if (!json.data || this.selected !== id) return;

            // Store weather data
            const inc0 = this.incidents.get(id);
            if (inc0 && json.data.weather) {
                inc0._weather = json.data.weather;
                if (this.activeTab === 'info') this.renderTab(inc0, 'info');
            }

            // Store vehicles for eenheden tab
            if (json.data.vehicles) {
                const inc = this.incidents.get(id);
                if (inc) inc._vehicles = json.data.vehicles;

                // Update eenheden tab badge
                const eenhedenTab = document.querySelector('.dp-tab[data-tab="eenheden"]');
                if (eenhedenTab) {
                    const count = json.data.vehicles.length;
                    eenhedenTab.innerHTML = count > 0 ? `Eenheden <span class="tab-badge">${count}</span>` : 'Eenheden';
                }

                // Re-render if on eenheden tab
                if (this.activeTab === 'eenheden' && inc) {
                    this.renderTab(inc, 'eenheden');
                }

                // Show station markers on the map
                this.showIncidentStations(json.data.vehicles);

                // Animate estimated vehicle positions
                if (inc && inc.status === 'active') {
                    this.animateVehiclePositions(json.data.vehicles, inc);
                }
            }

            // Store messages and render if on info tab
            if (json.data.messages) {
                const inc = this.incidents.get(id);
                if (inc) inc._messages = json.data.messages;
                if (this.activeTab === 'info') {
                    this.renderMessages(json.data.messages, inc ? (inc._vehicles || inc.vehicles) : []);
                }
            }

            // Store notes and update kladblok tab
            if (json.data.notes) {
                const inc = this.incidents.get(id);
                if (inc) inc._notes = json.data.notes;
                const kladblokTab = document.querySelector('.dp-tab[data-tab="kladblok"]');
                if (kladblokTab) {
                    const count = json.data.notes.length;
                    kladblokTab.style.display = count > 0 ? '' : 'none';
                    if (count > 0) kladblokTab.innerHTML = `Kladblok <span class="tab-badge">${count}</span>`;
                }
                if (this.activeTab === 'kladblok' && inc) {
                    this.renderTab(inc, 'kladblok');
                }
            } else {
                // No notes: hide tab
                const kladblokTab = document.querySelector('.dp-tab[data-tab="kladblok"]');
                if (kladblokTab) kladblokTab.style.display = 'none';
            }
        } catch (e) {
            console.error('Failed to load incident detail:', e);
            const title = document.getElementById('msgTitle');
            if (title) title.textContent = 'Kon berichten niet laden';
        }
    },

    renderMessages(messages, vehicles) {
        const title = document.getElementById('msgTitle');
        const container = document.getElementById('msgContainer');
        if (!title || !container) return;
        const ccOn = this._ccMode !== 'off' ? ' on' : '';
        const ccLabel = { off: 'Capcodes', names: 'Namen', nums: 'Nummers' }[this._ccMode];
        title.innerHTML = `${messages.length} bericht${messages.length !== 1 ? 'en' : ''} <button class="tl-cc-toggle${ccOn}" id="tlCcToggle" title="Capcodes tonen/verbergen">${ccLabel}</button>`;
        container.classList.remove('show-cc-names', 'show-cc-nums');
        if (this._ccMode !== 'off') container.classList.add('show-cc-' + this._ccMode);
        container.innerHTML = this.buildMessagesHtml(messages, vehicles);
    },

    _detectEscalation(body) {
        const m = (body || '').match(/\((?:Middel\.?\s*(?:brand|BR)?|Grote?\s*(?:brand|BR)?|Grt?\.?\s*(?:brand|BR)?|Zeer\s+(?:grote?|gr\.?|grt\.?)\s*(?:brand|BR)?|GRIP\s*\d*|Fataal)\)/i);
        if (!m) return null;
        const lbl = m[0].replace(/[()]/g, '');
        if (/zeer/i.test(lbl)) return { level: 4, label: 'Zeer grote brand', type: 'up' };
        if (/grot/i.test(lbl)) return { level: 3, label: 'Grote brand', type: 'up' };
        if (/middel/i.test(lbl)) return { level: 2, label: 'Middelbrand', type: 'up' };
        if (/grip/i.test(lbl)) return { level: 5, label: lbl.toUpperCase(), type: 'grip' };
        if (/fataal/i.test(lbl)) return { level: 6, label: 'Fataal incident', type: 'grip' };
        return null;
    },

    _linkifyVehicleIds(escapedBody, vehicleMap) {
        if (!vehicleMap || Object.keys(vehicleMap).length === 0) return escapedBody;
        // Match raw digits (201532) and formatted IDs (20-1532, 19-126)
        return escapedBody.replace(/\b(\d{2}-\d{3,5})\b|\b(\d{5,7})\b/g, (match, fmtMatch, rawMatch) => {
            const raw = fmtMatch ? fmtMatch.replace(/-/g, '') : rawMatch;
            const entry = vehicleMap[raw];
            if (!entry) return match; // Niet linken als het geen bekend voertuig is
            const fmt = entry.fmtId || fmtMatch || (raw.length >= 5 ? raw.substring(0, 2) + '-' + raw.substring(2) : raw);
            return `<a href="/voertuig/${encodeURIComponent(fmt)}" class="tl-vlink" title="${this.esc(entry.desc)}">${this.esc(fmt)}</a>`;
        });
    },

    buildMessagesHtml(messages, vehicles) {
        // Build vehicle ID → description map for linkifying
        // API returns formatted IDs (e.g. "17-3561"), but message text has raw digits ("173561")
        // Store both the raw (digits-only) key and keep the formatted ID for linking
        const vehicleMap = {};
        (vehicles || []).forEach(v => {
            if (v.vehicle_id) {
                const raw = v.vehicle_id.replace(/-/g, '');
                vehicleMap[raw] = { desc: v.description || v.vehicle_id, fmtId: v.vehicle_id };
            }
        });

        let html = '<div class="tl"><div class="tl-ln"></div>';
        let prevEscLevel = 0;
        messages.forEach((msg, i) => {
            const discipline = msg.discipline || '';
            const time = this.formatTime(msg.transmitted_at);
            const dotClass = discipline === 'Brandweer' ? 'r' : discipline === 'Ambulance' ? 'o' : discipline === 'Politie' ? 'b' : 'g';

            // Escalation detection
            const esc = this._detectEscalation(msg.body);
            if (esc && esc.level > prevEscLevel) {
                const prefix = i === 0 ? '' : 'Opgeschaald naar ';
                const cls = esc.type === 'grip' ? 'tl-esc-grip' : 'tl-esc-up';
                html += `<div class="tl-esc ${cls}">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                    ${this.esc(prefix + esc.label)}
                </div>`;
                prevEscLevel = esc.level;
            } else if (esc && esc.level < prevEscLevel) {
                html += `<div class="tl-esc tl-esc-down">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="transform:rotate(180deg)"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                    ${this.esc('Afgeschaald naar ' + esc.label)}
                </div>`;
                prevEscLevel = esc.level;
            } else if (esc) {
                prevEscLevel = esc.level;
            }

            const discColorMap = {
                'Brandweer': '#d32f2f',
                'Ambulance': '#f9a825',
                'Politie': '#1565c0',
                'KNRM': '#e65100',
                'Lifeliner': '#6a1b9a',
            };
            const discColor = discColorMap[discipline] || 'var(--tx2)';
            const prioHtml = msg.priority !== null && msg.priority !== undefined ? ' P' + msg.priority : '';
            const stationHtml = msg.station_label ? ' \u00B7 ' + this.esc(msg.station_label) : '';

            let unitsHtml = '';
            if (msg.units && msg.units.length > 0) {
                const ccNames = msg.units.filter(u => u.description).map(u => this.esc(u.description)).join(', ');
                const ccNums = msg.units.map(u => this.esc(String(u.capcode))).join(', ');
                unitsHtml = `<div class="tl-units"><span class="cc-names">${ccNames}</span><span class="cc-nums">${ccNums}</span></div>`;
            }

            html += `<div class="te">
                <div class="td ${dotClass}"></div>
                <div class="tc">
                    <div class="ts2"><strong>${time}</strong> <span style="color:${discColor}">${this.esc(discipline)}</span>${prioHtml}${stationHtml}</div>
                    <div class="tt">${this._linkifyVehicleIds(this.esc(msg.body || ''), vehicleMap)}</div>
                    ${unitsHtml}
                </div>
            </div>`;
        });
        html += '</div>';
        return html;
    },

    // ========================================
    // Event Toasts
    // ========================================

    _playAlertSound() {
        try {
            if (!this._soundCtx) this._soundCtx = new (window.AudioContext || window.webkitAudioContext)();
            const ctx = this._soundCtx;
            if (ctx.state === 'suspended') ctx.resume();
            // Twee korte tonen: 880Hz + 1046Hz (A5 + C6)
            [880, 1046].forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0.15, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15 + i * 0.12);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + i * 0.12);
                osc.stop(ctx.currentTime + 0.15 + i * 0.12);
            });
        } catch (_) {}
    },

    // Prioriteitsnummer uit een code als "P1"/"A2"/"B1" → 1/2/3 (of null)
    _prioNumber(code) {
        const m = (code || '').match(/(\d)/);
        return m ? parseInt(m[1], 10) : null;
    },

    // Kies de mooist klinkende Nederlandse stem. De browser pakt anders de eerste
    // beste (vaak de oude, robotachtige lokale systeemstem) terwijl er meestal
    // veel natuurlijkere stemmen beschikbaar zijn (Google/cloud "natural").
    _ttsVoice() {
        if (this._ttsVoiceCache !== undefined) return this._ttsVoiceCache;
        try {
            const voices = window.speechSynthesis.getVoices() || [];
            const nl = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('nl'));
            if (nl.length === 0) {
                // Stemmen mogelijk nog niet geladen → niet cachen, later opnieuw proberen.
                return null;
            }
            // Hoger = mooier. Kwaliteitsmarkers in de stemnaam + cloud-stemmen wegen
            // het zwaarst; nl-NL boven nl-BE (Vlaams) zodat het Nederlands klinkt.
            const score = (v) => {
                const name = (v.name || '').toLowerCase();
                let s = 0;
                if (/natural|neural|enhanced|premium|wavenet|google/.test(name)) s += 100;
                if (v.localService === false) s += 40;          // cloud-stem, doorgaans hoger kwaliteit
                if ((v.lang || '').toLowerCase() === 'nl-nl') s += 10; // NL boven BE
                if (v.default) s += 1;
                return s;
            };
            const best = nl.slice().sort((a, b) => score(b) - score(a))[0] || null;
            this._ttsVoiceCache = best;
            return best;
        } catch (_) { return null; }
    },

    _speak(text) {
        try {
            const synth = window.speechSynthesis;
            if (!synth || !text) return;
            // Voorkom een achterstand aan zinnen bij piekdrukte
            if (this._ttsQueueCount >= 3) return;
            const u = new SpeechSynthesisUtterance(text);
            u.lang = 'nl-NL';
            u.rate = 0.97;  // iets kalmer = rustiger en duidelijker
            u.pitch = 1.0;
            const v = this._ttsVoice();
            if (v) u.voice = v;
            this._ttsQueueCount++;
            u.onend = u.onerror = () => { this._ttsQueueCount = Math.max(0, this._ttsQueueCount - 1); };
            synth.speak(u);
        } catch (_) {}
    },

    // Lees een nieuwe melding voor als die past binnen de gekozen diensten + prio's
    _maybeSpeakIncident(data) {
        // Respecteer de actieve kaartfilter: alleen voorlezen wat ook zichtbaar is
        const inc = this.incidents.get(data.id);
        if (inc && !this.passesFilter(inc)) return;

        const disc = data.disciplines || [];
        if (!disc.some(d => this.fTtsDisc.has(d))) return;
        const prioNum = this._prioNumber(data.priority_code);
        if (prioNum === null || !this.fTtsPrio.has(prioNum)) return;

        // Volledige (letterlijke) P2000-tekst voorlezen ...
        const body = (data.body || '').trim();
        if (this.fTtsFull && body) {
            this._speak(body);
            return;
        }

        // ... of de nette samenvatting van de backend (TtsFormatter: zonder capcodes/
        // ritnummers, met prio/escalatie/locatie uitgeschreven). Fallback als die ontbreekt.
        const clean = (data.tts_text || '').replace(/\.$/, '').trim();
        this._speak(clean || this._ttsFallback(data, prioNum, disc));
    },

    // Minimale samenvatting voor het zeldzame geval dat de backend geen tts_text levert
    _ttsFallback(data, prioNum, disc) {
        const parts = [];
        if (prioNum) parts.push(`Prioriteit ${prioNum}`);
        if (disc[0]) parts.push(disc[0]);
        if (data.incident_type) parts.push(data.incident_type);
        const city = data.city || data.region_name;
        if (city) parts.push(city);
        return parts.join(', ');
    },

    showEventToast(text) {
        const log = document.getElementById('evLog');
        if (!log) return;
        const el = document.createElement('div');
        el.className = 'ev-item';
        el.textContent = text;
        log.prepend(el);
        setTimeout(() => el.remove(), 3100);
        if (log.children.length > 4) log.lastElementChild?.remove();
    },

    // ========================================
    // Mobile
    // ========================================

    initMobileTabs() {
        document.querySelectorAll('.mobile-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const t = tab.dataset.tab;
                document.querySelectorAll('.mobile-tab').forEach(b => b.classList.toggle('active', b.dataset.tab === t));
                const sb = document.querySelector('.sb');
                const fp = document.querySelector('.fp');

                // Hide all panels first
                if (sb) sb.classList.remove('mobile-visible');
                if (fp) fp.classList.remove('open');
                const morePanel = document.getElementById('mobileMore');
                if (morePanel) morePanel.style.display = 'none';

                if (t === 'feed') {
                    if (sb) sb.classList.add('mobile-visible');
                } else if (t === 'filters') {
                    if (fp) fp.classList.add('open');
                } else if (t === 'more') {
                    // Show mobile more overlay
                    const morePanel = document.getElementById('mobileMore');
                    if (morePanel) morePanel.style.display = '';
                } else {
                    // Map tab — vanuit de tekstmodus terug naar een weergave mét kaart
                    if (this.viewMode === 'text') this.setViewMode('split');
                    this.closeDetail();
                    setTimeout(() => this.map?.invalidateSize(), 50);
                }
            });
        });

        // Close filter panel via Apply/Reset should switch back to map tab
        const fpApply = document.getElementById('fpApply');
        const fpReset = document.getElementById('fpReset');
        const switchToMap = () => {
            document.querySelectorAll('.mobile-tab').forEach(b => b.classList.toggle('active', b.dataset.tab === 'map'));
        };
        if (fpApply) fpApply.addEventListener('click', switchToMap);
        if (fpReset) fpReset.addEventListener('click', switchToMap);
    },

    // ========================================
    // Classification helpers
    // ========================================

    updateFilterState() {
        this.filtersApplied = (this.fDisc.size > 0 || this.fEsc.size > 0 || this.fPrio.size > 0 || this.fRegion || this.fLocLat !== null || this.fHideTest);
    },

    applyFilters() {
        this.updateFilterState();
        this.saveFilters();
        this.renderAll();
        this.renderActiveFilters();
        this.applyMarkerVisibility();
    },

    sinceParam() {
        const hours = this.tlActive ? this.tlMaxHours : 2;
        const d = new Date(Date.now() - hours * 60 * 60 * 1000);
        // DB stores local time (CET), so generate local time string
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    formatCoords() {
        return `${this.fLocLat.toFixed(4)}, ${this.fLocLng.toFixed(4)}`;
    },

    // Unified escalation lookup — single source of truth for grip/escalation classification
    escInfo(inc) {
        const g = inc.grip || 0, e = inc.escalation_level || 0;
        if (g >= 5) return { level: 'grip5', card: 'grip2', color: '#922b21', size: 26 };
        if (g >= 4) return { level: 'grip4', card: 'grip2', color: '#922b21', size: 25 };
        if (g >= 3) return { level: 'grip3', card: 'grip2', color: '#922b21', size: 24 };
        if (g >= 2) return { level: 'grip2', card: 'grip2', color: '#e74c3c', size: 21 };
        if (g >= 1) return { level: 'grip1', card: 'grip1', color: '#e74c3c', size: 18 };
        if (e >= 4) return { level: 'zeer-groot', card: 'esc', color: '#b33025', size: 16 };
        if (e >= 3) return { level: 'groot', card: 'esc', color: '#c0700a', size: 14 };
        if (e >= 2) return { level: 'middel', card: null, color: '#888', size: 12 };
        return { level: 'klein', card: null, color: null, size: 10 };
    },

    cardClass(inc) {
        const info = this.escInfo(inc);
        if (info.card) return info.card;
        const d = (inc.disciplines || [])[0] || '';
        if (d === 'Brandweer') return 'bw';
        if (d === 'Ambulance') return 'amb';
        if (d === 'Politie') return 'pol';
        if (d === 'Lifeliner' || d === 'KNRM' || d === 'Reddingsbrigade') return 'life';
        return 'vr';
    },

    pinColor(inc) {
        const info = this.escInfo(inc);
        if (info.color) return info.color;
        const d = (inc.disciplines || [])[0] || '';
        return PIN_COLORS[d] || '#8b8fa3';
    },

    pinSize(inc) {
        const base = this.escInfo(inc).size;
        // When icons enabled, scale up so icons are visible
        if (this._pinIcons) return Math.max(20, base + 10);
        return base;
    },

    escLevel(inc) {
        return this.escInfo(inc).level;
    },

    prioBadgeClass(code) {
        if (!code) return 'b-over';
        const c = code.toUpperCase();
        if (c.startsWith('P')) return 'b-bw';
        if (c.startsWith('A')) return 'b-amb';
        if (c.startsWith('B')) return 'b-pol';
        return 'b-over';
    },

    // ========================================
    // Map bounds filter
    // ========================================

    initBoundsFilter() {
        const btn = document.getElementById('boundsToggle');
        if (!btn) return;

        // Restore saved state
        this.mapBoundsFilter = localStorage.getItem('mapBoundsFilter') === '1';
        if (this.mapBoundsFilter) {
            btn.classList.add('active');
            this.renderActiveFilters();
        }

        btn.addEventListener('click', () => {
            this.mapBoundsFilter = !this.mapBoundsFilter;
            btn.classList.toggle('active', this.mapBoundsFilter);
            savePref('mapBoundsFilter', this.mapBoundsFilter ? '1' : '0');
            this.renderAll();
            this.renderActiveFilters();
            this.applyMarkerVisibility();
        });
    },

    // ========================================
    // Timeline
    // ========================================

    initTimeline() {
        const toggle = document.getElementById('tlToggle');
        const bar = document.getElementById('timelineBar');
        const slider = document.getElementById('tlSlider');
        const label = document.getElementById('tlLabel');
        const playBtn = document.getElementById('tlPlay');
        const closeBtn = document.getElementById('tlClose');
        if (!toggle || !bar || !slider || !label || !playBtn || !closeBtn) return;

        toggle.addEventListener('click', () => {
            this.tlActive = !this.tlActive;
            bar.style.display = this.tlActive ? 'flex' : 'none';
            toggle.classList.toggle('active', this.tlActive);
            if (!this.tlActive) {
                this.stopTimeline();
                slider.value = '100';
                label.textContent = 'Nu';
            }
            this.refreshAllIncidents();
        });

        let _sliderTimer = null;
        slider.addEventListener('input', () => {
            this.updateTimelineLabel();
            clearTimeout(_sliderTimer);
            _sliderTimer = setTimeout(() => {
                this.scheduleRender();
                this.applyMarkerVisibility();
            }, 60);
        });

        playBtn.addEventListener('click', () => {
            if (this.tlPlaying) {
                this.stopTimeline();
            } else {
                this.startTimeline();
            }
        });

        closeBtn.addEventListener('click', () => {
            this.tlActive = false;
            bar.style.display = 'none';
            toggle.classList.remove('active');
            this.stopTimeline();
            slider.value = '100';
            label.textContent = 'Nu';
            this.refreshAllIncidents();
        });
    },

    updateTimelineLabel() {
        const slider = document.getElementById('tlSlider');
        const label = document.getElementById('tlLabel');
        if (!slider || !label) return;
        const val = parseInt(slider.value, 10);
        if (val >= 100) {
            label.textContent = 'Nu';
        } else {
            const hoursAgo = this.tlMaxHours * (1 - val / 100);
            const d = new Date(Date.now() - hoursAgo * 3600000);
            label.textContent = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        }
    },

    getTimelineCutoff() {
        if (!this.tlActive) return null;
        const slider = document.getElementById('tlSlider');
        if (!slider) return null;
        const val = parseInt(slider.value, 10);
        if (val >= 100) return null;
        const hoursAgo = this.tlMaxHours * (1 - val / 100);
        return new Date(Date.now() - hoursAgo * 3600000);
    },

    startTimeline() {
        const slider = document.getElementById('tlSlider');
        const playBtn = document.getElementById('tlPlay');
        if (!slider || !playBtn) return;
        this.tlPlaying = true;
        playBtn.innerHTML = '&#9646;&#9646;';
        if (parseInt(slider.value, 10) >= 100) slider.value = '0';
        this.tlInterval = setInterval(() => {
            let val = parseInt(slider.value, 10);
            if (val >= 100) {
                this.stopTimeline();
                return;
            }
            slider.value = String(val + 1);
            this.updateTimelineLabel();
            this.scheduleRender();
            this.applyMarkerVisibility();
        }, 300);
    },

    stopTimeline() {
        const playBtn = document.getElementById('tlPlay');
        this.tlPlaying = false;
        if (playBtn) playBtn.innerHTML = '&#9654;';
        if (this.tlInterval) {
            clearInterval(this.tlInterval);
            this.tlInterval = null;
        }
    },

    // ========================================
    // Merge mode (admin only)
    // ========================================

    initMergeMode() {
        if (!this.config.isAdmin) return;

        const hd = document.querySelector('.sb-hd');
        if (!hd) return;

        // Add merge toggle button in sidebar header
        const btn = document.createElement('button');
        btn.className = 'merge-btn';
        btn.id = 'mergeToggle';
        btn.textContent = 'Samenvoegen';
        btn.title = 'Selecteer incidenten om samen te voegen';
        hd.appendChild(btn);

        // Add merge action bar (hidden by default)
        const bar = document.createElement('div');
        bar.className = 'merge-bar';
        bar.id = 'mergeBar';
        bar.innerHTML = `
            <span class="merge-count" id="mergeCount">0 geselecteerd</span>
            <button class="merge-confirm" id="mergeConfirm" disabled>Samenvoegen</button>
            <button class="merge-cancel" id="mergeCancel">Annuleren</button>
        `;
        hd.parentNode.insertBefore(bar, hd.nextSibling);

        btn.addEventListener('click', () => this.toggleMergeMode());
        document.getElementById('mergeConfirm').addEventListener('click', () => this.executeMerge());
        document.getElementById('mergeCancel').addEventListener('click', () => this.toggleMergeMode(false));
    },

    onCardClick(id) {
        if (this.mergeMode) {
            this.toggleMergeSelection(id);
        } else {
            this.selectIncident(id);
        }
    },

    toggleMergeMode(enable) {
        this.mergeMode = enable !== undefined ? enable : !this.mergeMode;
        this.mergeSelected.clear();

        const bar = document.getElementById('mergeBar');
        const btn = document.getElementById('mergeToggle');
        const sb = document.querySelector('.sb');

        if (bar) bar.classList.toggle('active', this.mergeMode);
        if (btn) btn.classList.toggle('active', this.mergeMode);
        if (sb) sb.classList.toggle('merge-mode', this.mergeMode);

        this.updateMergeCount();
        this.scheduleRender();
    },

    toggleMergeSelection(id) {
        if (this.mergeSelected.has(id)) {
            this.mergeSelected.delete(id);
        } else {
            this.mergeSelected.add(id);
        }
        this.updateMergeCount();

        // Update just this card's class
        const card = this.cards.get(id);
        const inc = this.incidents.get(id);
        if (card && inc) {
            card.className = this.buildCardClass(inc, null);
        }
    },

    updateMergeCount() {
        const count = this.mergeSelected.size;
        const el = document.getElementById('mergeCount');
        const btn = document.getElementById('mergeConfirm');
        if (el) el.textContent = count + ' geselecteerd';
        if (btn) btn.disabled = count < 2;
    },

    async executeMerge() {
        const ids = [...this.mergeSelected];
        if (ids.length < 2) return;

        const confirmMsg = `${ids.length} incidenten samenvoegen? Dit kan niet ongedaan worden.`;
        if (!confirm(confirmMsg)) return;

        const btn = document.getElementById('mergeConfirm');
        if (btn) { btn.disabled = true; btn.textContent = 'Bezig...'; }

        try {
            let resp = await fetch('/api/v1/admin/incidents/merge', {
                method: 'POST',
                headers: apiHeaders(true),
                body: JSON.stringify({ incident_ids: ids }),
            });
            let json = await resp.json();

            // Incidenten >24u uit elkaar: vraag extra bevestiging
            if (resp.status === 422 && (json.errors?.[0]?.message || '').includes('uur uit elkaar')) {
                if (!confirm(json.errors[0].message + '\n\nWeet je het zeker?')) return;
                resp = await fetch('/api/v1/admin/incidents/merge', {
                    method: 'POST',
                    headers: apiHeaders(true),
                    body: JSON.stringify({ incident_ids: ids, force: true }),
                });
                json = await resp.json();
            }

            if (!resp.ok) {
                const msg = json.errors?.[0]?.message || 'Onbekende fout';
                alert('Fout: ' + msg);
                return;
            }

            const targetId = json.data?.target_id;
            const merged = json.data?.merged_count || 0;

            // Remove merged source incidents from local state
            ids.forEach(id => {
                if (id !== targetId) {
                    this.incidents.delete(id);
                    this.cards.delete(id);
                    const marker = this.markers.get(id);
                    if (marker) { marker.remove(); this.markers.delete(id); }
                }
            });

            // Exit merge mode and refresh
            this.toggleMergeMode(false);
            this.loadInitialIncidents();

            const errList = json.data?.errors || [];
            if (errList.length > 0) {
                alert(`${merged} samengevoegd in #${targetId}\n\nFouten:\n${errList.join('\n')}`);
            } else {
                alert(`${merged} incident(en) samengevoegd in #${targetId}`);
            }
        } catch (e) {
            alert('Netwerkfout: ' + e.message);
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = 'Samenvoegen'; }
        }
    },

    isPro() {
        return this.config.isAuth;
    },

    // ========================================
    // Helpers
    // ========================================

    formatTime(dateStr) {
        if (!dateStr) return '';
        // DB stores CET (local time), parse without Z suffix
        const d = new Date(dateStr.replace(' ', 'T'));
        if (isNaN(d.getTime())) return '';
        const now = new Date();
        const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        if (d.toDateString() === now.toDateString()) return time;
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) + ' ' + time;
    },

    // Escapet ook quotes: veel output belandt in attributen (alt, href, data-*), niet alleen in tekst.
    esc(str) {
        return String(str ?? '').replace(/[&<>"'`]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' })[ch]);
    },

    incidentUrl(inc) {
        const parts = [inc.incident_type, inc.street, inc.city].filter(Boolean);
        let raw = parts.length ? parts.join(' ') : (inc.body || 'incident').substring(0, 60);
        let slug = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');
        if (slug.length > 80) slug = slug.substring(0, slug.lastIndexOf('-', 80) || 80);
        return '/incident/' + inc.id + '/' + (slug || 'incident');
    },

    formatVehicleId(id, discipline) {
        if (!id) return '';
        // API en WebSocket leveren het nummer al geformatteerd ("21-3831")
        if (/^\d{2}-\d+$/.test(id)) return id;
        if (!/^\d+$/.test(id)) return '';
        // Brandweer/Veiligheidsregio: 6 digits → RR-PPTN (21-3831)
        // Ambulance: 5 digits → RR-TNN (17-151)
        if (id.length === 6) return id.substring(0, 2) + '-' + id.substring(2);
        if (id.length === 5) return id.substring(0, 2) + '-' + id.substring(2);
        return id;
    },

    haversineKm(lat1, lng1, lat2, lng2) {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    // ========================================
    // Feeds
    // ========================================

    initFeeds() {
        // Feed switcher dropdown
        const btn = document.getElementById('feedCurrent');
        const menu = document.getElementById('feedMenu');
        if (btn && menu) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                menu.classList.toggle('open');
            });
            document.addEventListener('click', () => menu.classList.remove('open'));
            menu.addEventListener('click', (e) => e.stopPropagation());
        }

        // "Alle meldingen" item
        menu?.querySelector('.feed-menu-item')?.addEventListener('click', () => {
            this.switchFeed(null);
            menu.classList.remove('open');
        });

        // "+ Nieuwe feed" button
        document.getElementById('feedMenuAdd')?.addEventListener('click', () => {
            menu?.classList.remove('open');
            if (!this.config.isAuth) {
                window.location.href = '/login';
                return;
            }
            if (!this.config.planFeatures.includes('feeds')) {
                alert('Feeds zijn beschikbaar vanaf het Basis abonnement. Ga naar /prijzen om te upgraden.');
                return;
            }
            this.openWizard();
        });

        // Wizard controls
        document.getElementById('wizClose')?.addEventListener('click', () => this.closeWizard());
        document.getElementById('wizOverlay')?.addEventListener('click', (e) => {
            if (e.target.id === 'wizOverlay') this.closeWizard();
        });
        document.getElementById('wizBack')?.addEventListener('click', () => this.wizPrev());
        document.getElementById('wizNext')?.addEventListener('click', () => this.wizNext());

        // Follow type selection
        document.getElementById('wizTypes')?.addEventListener('click', (e) => {
            const type = e.target.closest('.wiz-type');
            if (!type) return;
            document.querySelectorAll('.wiz-type').forEach(t => t.classList.remove('sel'));
            type.classList.add('sel');
            this.wizData.follow_type = type.dataset.ft;
        });

        // Filter chips in step 3
        document.getElementById('wizFilterDisc')?.addEventListener('click', (e) => {
            const chip = e.target.closest('.chip');
            if (!chip) return;
            chip.classList.toggle('on');
        });
        document.getElementById('wizFilterPrio')?.addEventListener('click', (e) => {
            const chip = e.target.closest('.chip');
            if (!chip) return;
            chip.classList.toggle('on');
        });

        // Escape key closes wizard
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById('wizOverlay')?.classList.contains('open')) {
                this.closeWizard();
            }
        });

        // Feed settings toggle
        document.getElementById('feedSettingsBtn')?.addEventListener('click', () => {
            const bar = document.getElementById('feedDetailBar');
            if (bar) bar.style.display = bar.style.display === 'none' ? '' : 'none';
        });

        // Add follow to existing feed
        document.getElementById('feedAddFollow')?.addEventListener('click', () => {
            if (!this.activeFeedId) return;
            this.wizData = { _addToFeed: this.activeFeedId };
            this.wizStep = 1;
            document.getElementById('wizOverlay')?.classList.add('open');
            document.querySelectorAll('.wiz-type').forEach(t => t.classList.remove('sel'));
            document.querySelectorAll('#wizFilterDisc .chip, #wizFilterPrio .chip').forEach(c => c.classList.remove('on'));
            document.getElementById('wizTargetBody').innerHTML = '';
            document.getElementById('wizTitle').textContent = 'Follow toevoegen';
            this.wizUpdateUI();
        });

        // Delete feed
        document.getElementById('feedDelete')?.addEventListener('click', async () => {
            if (!this.activeFeedId) return;
            if (!confirm('Weet je zeker dat je deze feed wilt verwijderen?')) return;
            try {
                const res = await fetch(`/api/v1/feeds/${this.activeFeedId}`, { method: 'DELETE', headers: apiHeaders(true) });
                if (res.ok) {
                    this.switchFeed(null);
                    await this.loadFeeds();
                    this.showEventToast('Feed verwijderd');
                }
            } catch (e) { alert('Fout: ' + e.message); }
        });

        // Load feeds if authenticated
        if (this.config.isAuth && this.config.planFeatures.includes('feeds')) {
            this.loadFeeds();
        }
    },

    async loadFeeds() {
        try {
            const res = await fetch('/api/v1/feeds');
            const json = await res.json();
            this.feeds = json.data || [];
            this.renderFeedMenu();

            // Auto-select feed from URL param or localStorage
            if (!this.activeFeedId) {
                const urlFeed = new URLSearchParams(window.location.search).get('feed');
                if (urlFeed === '0') {
                    // Explicit "all incidents" — clear saved feed
                    this.switchFeed(null);
                } else {
                    const savedFeed = localStorage.getItem('activeFeedId');
                    const targetId = urlFeed ? parseInt(urlFeed) : (savedFeed ? parseInt(savedFeed) : null);
                    if (targetId && this.feeds.some(f => f.id === targetId && !f.locked)) {
                        this.switchFeed(targetId);
                    }
                }
            }
        } catch (e) {
            console.error('Failed to load feeds:', e);
        }
    },

    renderFeedMenu() {
        const list = document.getElementById('feedMenuList');
        if (!list) return;
        list.innerHTML = '';
        this.feeds.forEach(f => {
            const div = document.createElement('div');
            div.className = 'feed-menu-item' + (this.activeFeedId === f.id ? ' active' : '');
            div.dataset.feed = f.id;
            div.innerHTML = `<span>${this.esc(f.name)}</span>` + (f.locked ? ' <span title="Vergrendeld">🔒</span>' : '');
            div.addEventListener('click', () => {
                // Boven de plan-limiet na verlopen abonnement: alleen verwijderbaar via /feeds
                if (f.locked) {
                    if (confirm('Deze feed is vergrendeld: je huidige plan staat minder feeds toe. Verwijder feeds tot je binnen je limiet zit, of upgrade. Naar je feeds?')) {
                        window.location.href = '/feeds';
                    }
                    return;
                }
                this.switchFeed(f.id);
                document.getElementById('feedMenu')?.classList.remove('open');
            });
            list.appendChild(div);
        });
        if (this.feeds.length > 0) {
            // Ensure separator is visible
            const seps = document.querySelectorAll('.feed-menu-sep');
            seps.forEach(s => s.style.display = 'block');
        }
    },

    switchFeed(feedId) {
        this.activeFeedId = feedId;

        // Persist selection
        if (feedId) {
            savePref('activeFeedId', String(feedId));
        } else {
            savePref('activeFeedId', '');
        }

        // Clean ?feed= from URL without reload
        const url = new URL(window.location);
        if (url.searchParams.has('feed')) {
            url.searchParams.delete('feed');
            history.replaceState(null, '', url.pathname + (url.search || ''));
        }

        // Update active states in menu
        document.querySelectorAll('.feed-menu-item').forEach(el => {
            el.classList.toggle('active', (el.dataset.feed || '') === String(feedId || ''));
        });

        // Update label
        const lbl = document.getElementById('feedCurrentLbl');
        if (feedId) {
            const feed = this.feeds.find(f => f.id === feedId);
            if (lbl && feed) lbl.textContent = feed.name;
        } else {
            if (lbl) lbl.textContent = 'Alle meldingen';
        }

        // Show/hide settings button
        const settingsBtn = document.getElementById('feedSettingsBtn');
        const detailBar = document.getElementById('feedDetailBar');
        if (settingsBtn) settingsBtn.style.display = feedId ? '' : 'none';
        if (detailBar) detailBar.style.display = 'none';

        if (feedId) {
            this.loadFeedIncidents(feedId);
            this.loadFeedDetails(feedId);
        } else {
            this.feedIncidents = [];
            this.feedFollows = [];
            this.renderAll();
            this.applyMarkerVisibility();
        }
    },

    async loadFeedDetails(feedId) {
        try {
            const res = await fetch(`/api/v1/feeds/${feedId}`);
            const json = await res.json();
            if (!json.data?.follows) return;
            this.feedFollows = json.data.follows;

            const list = document.getElementById('feedFollowsList');
            if (!list) return;
            list.innerHTML = '';

            const TYPE_LABELS = {
                region: 'Regio', station: 'Kazerne', discipline: 'Discipline',
                geo: 'Locatie', vehicle: 'Voertuig', capcode: 'Capcode',
                incident_type: 'Type', escalation: 'Opschaling',
            };

            json.data.follows.forEach(f => {
                const chip = document.createElement('span');
                chip.className = 'feed-follow-chip';
                let label = TYPE_LABELS[f.follow_type] || f.follow_type;
                let detail = '';
                if (f.follow_type === 'region') {
                    const r = this.regionData.find(r => r.region_id === f.region_id);
                    detail = r ? r.name : `#${f.region_id}`;
                } else if (f.follow_type === 'station') {
                    detail = `#${f.station_id}`;
                } else if (f.follow_type === 'discipline') {
                    detail = f.discipline;
                } else if (f.follow_type === 'geo') {
                    detail = `${parseFloat(f.geo_lat).toFixed(2)}, ${parseFloat(f.geo_lng).toFixed(2)} — ${f.geo_radius_km}km`;
                } else if (f.follow_type === 'vehicle') {
                    detail = f.vehicle_id;
                } else if (f.follow_type === 'capcode') {
                    detail = `#${f.capcode_id}`;
                } else if (f.follow_type === 'incident_type') {
                    detail = f.incident_type_code;
                } else if (f.follow_type === 'escalation') {
                    detail = `${f.escalation_min}+`;
                }
                chip.innerHTML = `<b>${this.esc(label)}</b> ${this.esc(detail)} <button class="feed-follow-rm" data-fid="${f.id}">&times;</button>`;
                chip.querySelector('.feed-follow-rm').addEventListener('click', async (e) => {
                    e.stopPropagation();
                    if (!confirm('Follow verwijderen?')) return;
                    try {
                        await fetch(`/api/v1/feeds/${feedId}/follows/${f.id}`, { method: 'DELETE', headers: apiHeaders(true) });
                        this.loadFeedDetails(feedId);
                        this.loadFeedIncidents(feedId);
                    } catch (err) { /* ignore */ }
                });
                list.appendChild(chip);
            });
        } catch (e) { /* ignore */ }
    },

    incidentMatchesFeed(inc) {
        if (this.feedFollows.length === 0) return false;
        return this.feedFollows.some(f => {
            let typeMatch = false;
            switch (f.follow_type) {
                case 'region':
                    typeMatch = inc.region_id === f.region_id;
                    break;
                case 'station':
                    typeMatch = (inc.region_id === f.region_id && inc.station_id === f.station_id)
                        || (inc.vehicles || []).some(v => v.station_id === f.station_id && v.region_id === f.region_id);
                    break;
                case 'discipline':
                    typeMatch = (inc.disciplines || []).includes(f.discipline);
                    break;
                case 'geo':
                    if (!inc.lat || !inc.lng || !f.geo_lat || !f.geo_lng) break;
                    typeMatch = this.haversineKm(f.geo_lat, f.geo_lng, inc.lat, inc.lng) <= f.geo_radius_km;
                    break;
                case 'vehicle':
                    typeMatch = (inc.vehicles || []).some(v => v.vehicle_id === f.vehicle_id);
                    break;
                case 'incident_type':
                    typeMatch = inc.incident_type_code === f.incident_type_code;
                    break;
                case 'escalation':
                    typeMatch = (inc.escalation_level || 0) >= (f.escalation_min || 0);
                    break;
                case 'capcode':
                    typeMatch = (inc.capcodes || []).includes(f.capcode_id);
                    break;
            }
            if (!typeMatch) return false;
            // Per-follow AND filters (must match discipline + priority if set)
            if (f.filter_disciplines && f.filter_disciplines.length > 0) {
                if (!(inc.disciplines || []).some(d => f.filter_disciplines.includes(d))) return false;
            }
            if (f.filter_priorities && f.filter_priorities.length > 0) {
                if (!f.filter_priorities.includes(inc.priority)) return false;
            }
            return true;
        });
    },

    scheduleFeedRefresh() {
        if (this._feedRefreshTimer) return;
        this._feedRefreshTimer = setTimeout(() => {
            this._feedRefreshTimer = null;
            if (this.activeFeedId) {
                this.loadFeedIncidents(this.activeFeedId);
            }
        }, 5000);
    },

    async loadFeedIncidents(feedId) {
        try {
            const res = await fetch(`/api/v1/feeds/${feedId}/incidents?per_page=500`);
            const json = await res.json();
            if (!json.data) return;

            // Store feed incident IDs for filtering
            this.feedIncidents = json.data.map(i => i.id);

            // Add/update incidents in our main data store
            json.data.forEach(inc => {
                this.addOrUpdateIncident(inc, false);
            });

            this.renderAll();
            this.applyMarkerVisibility();
        } catch (e) {
            console.error('Failed to load feed incidents:', e);
        }
    },

    // ========================================
    // Wizard
    // ========================================

    openWizard() {
        this.wizStep = 0;
        this.wizData = {};
        document.getElementById('wizOverlay')?.classList.add('open');
        document.getElementById('wizName').value = '';

        // Reset UI
        document.querySelectorAll('.wiz-type').forEach(t => t.classList.remove('sel'));
        document.querySelectorAll('#wizFilterDisc .chip, #wizFilterPrio .chip').forEach(c => c.classList.remove('on'));
        document.getElementById('wizTargetBody').innerHTML = '';
        document.getElementById('wizSummary').innerHTML = '';

        this.wizUpdateUI();
        setTimeout(() => document.getElementById('wizName')?.focus(), 100);
    },

    closeWizard() {
        document.getElementById('wizOverlay')?.classList.remove('open');
    },

    wizUpdateUI() {
        const step = this.wizStep;
        const addMode = !!this.wizData._addToFeed;

        document.querySelectorAll('.wiz-step').forEach(s => {
            s.classList.toggle('on', parseInt(s.dataset.step) === step);
        });
        document.querySelectorAll('.wiz-dot').forEach(d => {
            const s = parseInt(d.dataset.s);
            if (addMode && s === 0) {
                d.classList.add('done');
                d.classList.remove('on');
            } else {
                d.classList.toggle('on', s === step);
                d.classList.toggle('done', s < step);
            }
        });

        const back = document.getElementById('wizBack');
        const next = document.getElementById('wizNext');
        const minStep = addMode ? 1 : 0;
        if (back) back.style.display = step > minStep ? '' : 'none';
        if (next) {
            next.textContent = step === 3 ? (addMode ? 'Follow toevoegen' : 'Feed aanmaken') : 'Volgende';
        }

        // Step-specific titles
        const titles = addMode
            ? ['', 'Follow toevoegen', 'Configureer', 'Filters & opslaan']
            : ['Nieuwe feed', 'Kies follow type', 'Configureer', 'Filters & opslaan'];
        const titleEl = document.getElementById('wizTitle');
        if (titleEl) titleEl.textContent = titles[step] || 'Feed wizard';
    },

    wizPrev() {
        if (this.wizStep > 0) {
            this.wizStep--;
            this.wizUpdateUI();
        }
    },

    async wizNext() {
        const step = this.wizStep;

        if (step === 0) {
            // Validate name
            const name = document.getElementById('wizName')?.value?.trim();
            if (!name) {
                document.getElementById('wizName')?.focus();
                return;
            }
            this.wizData.name = name;
            this.wizStep = 1;
            this.wizUpdateUI();
        } else if (step === 1 && this.wizData._addToFeed) {
            // Adding follow to existing feed — skip step 0 was already done
            if (!this.wizData.follow_type) return;
            this.wizStep = 2;
            this.wizBuildTarget();
            this.wizUpdateUI();
        } else if (step === 1) {
            // Validate follow type selected
            if (!this.wizData.follow_type) return;
            this.wizStep = 2;
            this.wizBuildTarget();
            this.wizUpdateUI();
        } else if (step === 2) {
            // Validate target config
            if (!this.wizValidateTarget()) return;
            this.wizStep = 3;
            this.wizBuildSummary();
            this.wizUpdateUI();
        } else if (step === 3) {
            // Create feed + follow
            await this.wizSubmit();
        }
    },

    wizBuildTarget() {
        const body = document.getElementById('wizTargetBody');
        const title = document.getElementById('wizTargetTitle');
        const desc = document.getElementById('wizTargetDesc');
        if (!body) return;
        body.innerHTML = '';

        const ft = this.wizData.follow_type;
        const TARGETS = {
            region: { title: 'Kies een veiligheidsregio', desc: 'Alle meldingen binnen deze regio.' },
            station: { title: 'Zoek een kazerne', desc: 'Meldingen waarbij deze kazerne betrokken is.' },
            discipline: { title: 'Kies een discipline', desc: 'Alle meldingen van deze discipline.' },
            geo: { title: 'Stel locatie + radius in', desc: 'Meldingen binnen een straal van een punt.' },
            vehicle: { title: 'Zoek een voertuig', desc: 'Alle meldingen waarbij dit voertuig is ingezet.' },
            capcode: { title: 'Zoek een capcode', desc: 'Meldingen op deze P2000 capcode.' },
            incident_type: { title: 'Voer een meldingssoort in', desc: 'bijv. "brand", "reanimatie", "afhijsen".' },
            escalation: { title: 'Minimale opschaling', desc: 'Alleen meldingen vanaf dit opschalingniveau.' },
        };

        const t = TARGETS[ft] || {};
        if (title) title.textContent = t.title || 'Configureer';
        if (desc) desc.textContent = t.desc || '';

        if (ft === 'region') {
            body.innerHTML = '<select class="wiz-input" id="wizRegionSel"><option value="">Selecteer regio...</option></select>';
            const sel = document.getElementById('wizRegionSel');
            this.regionData.forEach(r => {
                const opt = document.createElement('option');
                opt.value = r.region_id;
                opt.textContent = r.name;
                sel.appendChild(opt);
            });
        } else if (ft === 'station') {
            body.innerHTML = `
                <div class="wiz-ac-wrap">
                    <input type="text" class="wiz-input" id="wizStationSearch" placeholder="Zoek op kazerne naam...">
                    <div class="wiz-ac-list" id="wizStationAC"></div>
                </div>
                <div id="wizStationSelected" style="margin-top:8px"></div>`;
            this.wizInitStationAC();
        } else if (ft === 'discipline') {
            body.innerHTML = `
                <div class="wiz-disc-grid">
                    <span class="wiz-disc bw" data-d="Brandweer">Brandweer</span>
                    <span class="wiz-disc amb" data-d="Ambulance">Ambulance</span>
                    <span class="wiz-disc pol" data-d="Politie">Politie</span>
                </div>`;
            body.querySelector('.wiz-disc-grid').addEventListener('click', (e) => {
                const d = e.target.closest('.wiz-disc');
                if (!d) return;
                document.querySelectorAll('.wiz-disc').forEach(x => x.classList.remove('sel'));
                d.classList.add('sel');
                this.wizData.discipline = d.dataset.d;
            });
        } else if (ft === 'geo') {
            body.innerHTML = `
                <div class="wiz-geo-row">
                    <div style="flex:1">
                        <span class="wiz-label">Breedtegraad</span>
                        <input type="number" step="0.0001" class="wiz-input" id="wizGeoLat" placeholder="bijv. 52.0907">
                    </div>
                    <div style="flex:1">
                        <span class="wiz-label">Lengtegraad</span>
                        <input type="number" step="0.0001" class="wiz-input" id="wizGeoLng" placeholder="bijv. 5.1214">
                    </div>
                </div>
                <button class="wiz-btn-back" id="wizGeoGps" style="display:inline-flex;margin-bottom:10px">Gebruik GPS locatie</button>
                <div>
                    <span class="wiz-label">Radius</span>
                    <div class="wiz-radius-row">
                        <input type="range" id="wizGeoRadius" min="1" max="100" value="15">
                        <span class="wiz-radius-val" id="wizGeoRadiusVal">15 km</span>
                    </div>
                </div>`;
            document.getElementById('wizGeoRadius')?.addEventListener('input', (e) => {
                document.getElementById('wizGeoRadiusVal').textContent = e.target.value + ' km';
            });
            document.getElementById('wizGeoGps')?.addEventListener('click', () => {
                if (!navigator.geolocation) { alert('GPS niet beschikbaar'); return; }
                navigator.geolocation.getCurrentPosition(pos => {
                    document.getElementById('wizGeoLat').value = pos.coords.latitude.toFixed(4);
                    document.getElementById('wizGeoLng').value = pos.coords.longitude.toFixed(4);
                }, () => alert('GPS niet toegestaan'));
            });
        } else if (ft === 'vehicle') {
            body.innerHTML = `
                <div class="wiz-ac-wrap">
                    <input type="text" class="wiz-input" id="wizVehicleSearch" placeholder="Zoek op voertuig ID (bijv. 0901691)...">
                    <div class="wiz-ac-list" id="wizVehicleAC"></div>
                </div>
                <div id="wizVehicleSelected" style="margin-top:8px"></div>`;
            this.wizInitVehicleAC();
        } else if (ft === 'capcode') {
            body.innerHTML = `
                <div class="wiz-ac-wrap">
                    <input type="text" class="wiz-input" id="wizCapcodeSearch" placeholder="Zoek op capcode of omschrijving...">
                    <div class="wiz-ac-list" id="wizCapcodeAC"></div>
                </div>
                <div id="wizCapcodeSelected" style="margin-top:8px"></div>`;
            this.wizInitCapcodeAC();
        } else if (ft === 'incident_type') {
            body.innerHTML = `<input type="text" class="wiz-input" id="wizIncTypeCode" placeholder="bijv. Brand, Reanimatie, Liftopsluiting">`;
        } else if (ft === 'escalation') {
            body.innerHTML = `
                <div class="wiz-esc-grid">
                    <span class="wiz-esc" data-e="1">Klein (1+)</span>
                    <span class="wiz-esc" data-e="2">Middel (2+)</span>
                    <span class="wiz-esc" data-e="3">Groot (3+)</span>
                    <span class="wiz-esc" data-e="4">Zeer groot (4+)</span>
                    <span class="wiz-esc" data-e="5">GRIP 1 (5+)</span>
                    <span class="wiz-esc" data-e="6">GRIP 2 (6+)</span>
                    <span class="wiz-esc" data-e="7">GRIP 3 (7+)</span>
                    <span class="wiz-esc" data-e="8">GRIP 4 (8+)</span>
                    <span class="wiz-esc" data-e="9">GRIP 5 (9+)</span>
                </div>`;
            body.querySelector('.wiz-esc-grid').addEventListener('click', (e) => {
                const el = e.target.closest('.wiz-esc');
                if (!el) return;
                document.querySelectorAll('.wiz-esc').forEach(x => x.classList.remove('sel'));
                el.classList.add('sel');
                this.wizData.escalation_min = parseInt(el.dataset.e);
            });
        }
    },

    wizInitStationAC() {
        const input = document.getElementById('wizStationSearch');
        const list = document.getElementById('wizStationAC');
        if (!input || !list) return;
        let timer = null;

        input.addEventListener('input', () => {
            clearTimeout(timer);
            const q = input.value.trim();
            if (q.length < 2) { list.classList.remove('open'); return; }
            timer = setTimeout(async () => {
                try {
                    const res = await fetch(`/api/v1/autocomplete/stations?q=${encodeURIComponent(q)}&limit=8`);
                    const json = await res.json();
                    const items = json.data || [];
                    if (items.length === 0) { list.classList.remove('open'); return; }
                    list.innerHTML = items.map(s =>
                        `<div class="wiz-ac-item" data-rid="${s.region_id}" data-sid="${s.station_id}">
                            <span>${this.esc(s.label || '')}</span>
                            <span class="wiz-ac-item-sub">${this.esc(s.discipline || '')}</span>
                        </div>`
                    ).join('');
                    positionAcList(input, list);
                    list.classList.add('open');

                    list.querySelectorAll('.wiz-ac-item').forEach(el => {
                        el.addEventListener('click', () => {
                            this.wizData.region_id = parseInt(el.dataset.rid);
                            this.wizData.station_id = parseInt(el.dataset.sid);
                            const name = el.querySelector('span').textContent;
                            input.value = name;
                            list.classList.remove('open');
                            document.getElementById('wizStationSelected').innerHTML =
                                `<div style="font-size:12px;color:var(--tx2)">Geselecteerd: <b style="color:var(--tx)">${this.esc(name)}</b></div>`;
                        });
                    });
                } catch (e) { list.classList.remove('open'); }
            }, 250);
        });

        input.addEventListener('blur', () => setTimeout(() => list.classList.remove('open'), 200));
    },

    wizInitVehicleAC() {
        const input = document.getElementById('wizVehicleSearch');
        const list = document.getElementById('wizVehicleAC');
        if (!input || !list) return;
        let timer = null;

        input.addEventListener('input', () => {
            clearTimeout(timer);
            const q = input.value.trim();
            if (q.length < 2) { list.classList.remove('open'); return; }
            timer = setTimeout(async () => {
                try {
                    const res = await fetch(`/api/v1/autocomplete/vehicles?q=${encodeURIComponent(q)}&limit=8`);
                    const json = await res.json();
                    const items = json.data || [];
                    if (items.length === 0) { list.classList.remove('open'); return; }
                    list.innerHTML = items.map(v =>
                        `<div class="wiz-ac-item" data-vid="${this.esc(v.value)}">
                            <span>${this.esc(v.label || v.value)}</span>
                            <span class="wiz-ac-item-sub">${this.esc(v.discipline || '')}</span>
                        </div>`
                    ).join('');
                    positionAcList(input, list);
                    list.classList.add('open');

                    list.querySelectorAll('.wiz-ac-item').forEach(el => {
                        el.addEventListener('click', () => {
                            this.wizData.vehicle_id = el.dataset.vid;
                            input.value = el.dataset.vid;
                            list.classList.remove('open');
                            const sub = el.querySelector('.wiz-ac-item-sub')?.textContent || '';
                            document.getElementById('wizVehicleSelected').innerHTML =
                                `<div style="font-size:12px;color:var(--tx2)">Geselecteerd: <b style="color:var(--tx)">${this.esc(el.dataset.vid)}</b> ${this.esc(sub)}</div>`;
                        });
                    });
                } catch (e) { list.classList.remove('open'); }
            }, 250);
        });

        input.addEventListener('blur', () => setTimeout(() => list.classList.remove('open'), 200));
    },

    wizInitCapcodeAC() {
        const input = document.getElementById('wizCapcodeSearch');
        const list = document.getElementById('wizCapcodeAC');
        if (!input || !list) return;
        let timer = null;

        input.addEventListener('input', () => {
            clearTimeout(timer);
            const q = input.value.trim();
            if (q.length < 2) { list.classList.remove('open'); return; }
            timer = setTimeout(async () => {
                try {
                    const res = await fetch(`/api/v1/autocomplete/capcodes?q=${encodeURIComponent(q)}&limit=8`);
                    const json = await res.json();
                    const items = json.data || [];
                    if (items.length === 0) { list.classList.remove('open'); return; }
                    list.innerHTML = items.map(c =>
                        `<div class="wiz-ac-item" data-cid="${this.esc(c.value)}">
                            <span>${this.esc(c.label || c.value)}</span>
                            <span class="wiz-ac-item-sub">${this.esc(c.discipline || '')}</span>
                        </div>`
                    ).join('');
                    positionAcList(input, list);
                    list.classList.add('open');

                    list.querySelectorAll('.wiz-ac-item').forEach(el => {
                        el.addEventListener('click', () => {
                            this.wizData.capcode_id = parseInt(el.dataset.cid);
                            input.value = el.querySelector('span').textContent;
                            list.classList.remove('open');
                            document.getElementById('wizCapcodeSelected').innerHTML =
                                `<div style="font-size:12px;color:var(--tx2)">Geselecteerd: <b style="color:var(--tx)">${this.esc(el.querySelector('span').textContent)}</b></div>`;
                        });
                    });
                } catch (e) { list.classList.remove('open'); }
            }, 250);
        });

        input.addEventListener('blur', () => setTimeout(() => list.classList.remove('open'), 200));
    },

    wizValidateTarget() {
        const ft = this.wizData.follow_type;
        if (ft === 'region') {
            const val = document.getElementById('wizRegionSel')?.value;
            if (!val) return false;
            this.wizData.region_id = parseInt(val);
            return true;
        }
        if (ft === 'station') return !!(this.wizData.region_id && this.wizData.station_id);
        if (ft === 'discipline') return !!this.wizData.discipline;
        if (ft === 'geo') {
            const lat = parseFloat(document.getElementById('wizGeoLat')?.value);
            const lng = parseFloat(document.getElementById('wizGeoLng')?.value);
            const r = parseInt(document.getElementById('wizGeoRadius')?.value);
            if (isNaN(lat) || isNaN(lng) || isNaN(r)) return false;
            this.wizData.geo_lat = lat;
            this.wizData.geo_lng = lng;
            this.wizData.geo_radius_km = r;
            return true;
        }
        if (ft === 'vehicle') return !!this.wizData.vehicle_id;
        if (ft === 'capcode') return !!this.wizData.capcode_id;
        if (ft === 'incident_type') {
            const val = document.getElementById('wizIncTypeCode')?.value?.trim();
            if (!val) return false;
            this.wizData.incident_type_code = val;
            return true;
        }
        if (ft === 'escalation') return !!this.wizData.escalation_min;
        return false;
    },

    wizBuildSummary() {
        const s = document.getElementById('wizSummary');
        if (!s) return;
        const ft = this.wizData.follow_type;
        const TYPE_LABELS = {
            region: 'Veiligheidsregio',
            station: 'Kazerne',
            discipline: 'Discipline',
            geo: 'Locatie + radius',
            vehicle: 'Voertuig',
            capcode: 'Capcode',
            incident_type: 'Meldingssoort',
            escalation: 'Opschaling',
        };

        let detail = '';
        if (ft === 'region') {
            const r = this.regionData.find(r => r.region_id === this.wizData.region_id);
            detail = r ? r.name : `Regio #${this.wizData.region_id}`;
        } else if (ft === 'station') {
            detail = document.getElementById('wizStationSearch')?.value || `Station #${this.wizData.station_id}`;
        } else if (ft === 'discipline') {
            detail = this.wizData.discipline;
        } else if (ft === 'geo') {
            detail = `${this.wizData.geo_lat.toFixed(4)}, ${this.wizData.geo_lng.toFixed(4)} — ${this.wizData.geo_radius_km} km`;
        } else if (ft === 'vehicle') {
            detail = this.wizData.vehicle_id;
        } else if (ft === 'capcode') {
            detail = document.getElementById('wizCapcodeSearch')?.value || `Capcode #${this.wizData.capcode_id}`;
        } else if (ft === 'incident_type') {
            detail = this.wizData.incident_type_code;
        } else if (ft === 'escalation') {
            const ESC = { 1: 'Klein incident', 2: 'Middel incident', 3: 'Groot incident', 4: 'Zeer groot incident', 5: 'GRIP 1', 6: 'GRIP 2', 7: 'GRIP 3', 8: 'GRIP 4', 9: 'GRIP 5' };
            detail = (ESC[this.wizData.escalation_min] || '') + ' en hoger';
        }

        s.innerHTML = `
            <b>Feed:</b> ${this.esc(this.wizData.name)}<br>
            <b>Volgt:</b> ${TYPE_LABELS[ft] || ft} — ${this.esc(detail)}
        `;
    },

    async wizSubmit() {
        const btn = document.getElementById('wizNext');
        if (btn) { btn.disabled = true; btn.textContent = 'Aanmaken...'; }
        const addToExisting = this.wizData._addToFeed;

        try {
            let feedId;
            if (addToExisting) {
                feedId = addToExisting;
            } else {
                // Step 1: Create feed
                const feedRes = await fetch('/api/v1/feeds', {
                    method: 'POST',
                    headers: apiHeaders(true),
                    body: JSON.stringify({ name: this.wizData.name }),
                });
                const feedJson = await feedRes.json();
                if (!feedRes.ok) {
                    alert(feedJson.error?.[0]?.message || feedJson.error || 'Fout bij aanmaken feed');
                    return;
                }
                feedId = feedJson.data.id;
            }

            // Step 2: Add follow
            const followBody = { follow_type: this.wizData.follow_type };

            // Copy relevant fields
            const ft = this.wizData.follow_type;
            if (ft === 'region') followBody.region_id = this.wizData.region_id;
            if (ft === 'station') { followBody.region_id = this.wizData.region_id; followBody.station_id = this.wizData.station_id; }
            if (ft === 'discipline') followBody.discipline = this.wizData.discipline;
            if (ft === 'geo') { followBody.geo_lat = this.wizData.geo_lat; followBody.geo_lng = this.wizData.geo_lng; followBody.geo_radius_km = this.wizData.geo_radius_km; }
            if (ft === 'vehicle') followBody.vehicle_id = this.wizData.vehicle_id;
            if (ft === 'capcode') followBody.capcode_id = this.wizData.capcode_id;
            if (ft === 'incident_type') followBody.incident_type_code = this.wizData.incident_type_code;
            if (ft === 'escalation') followBody.escalation_min = this.wizData.escalation_min;

            // Optional filters
            const filterDiscs = [...document.querySelectorAll('#wizFilterDisc .chip.on')].map(c => c.dataset.d);
            const filterPrios = [...document.querySelectorAll('#wizFilterPrio .chip.on')].map(c => parseInt(c.dataset.p));
            if (filterDiscs.length > 0) followBody.filter_disciplines = filterDiscs;
            if (filterPrios.length > 0) followBody.filter_priorities = filterPrios;

            const followRes = await fetch(`/api/v1/feeds/${feedId}/follows`, {
                method: 'POST',
                headers: apiHeaders(true),
                body: JSON.stringify(followBody),
            });
            if (!followRes.ok) {
                const fj = await followRes.json();
                alert(fj.error?.[0]?.message || fj.error || 'Fout bij toevoegen follow');
                return;
            }

            // Success
            this.closeWizard();
            await this.loadFeeds();
            this.switchFeed(feedId);
            this.showEventToast(addToExisting ? 'Follow toegevoegd!' : 'Feed aangemaakt!');

        } catch (e) {
            alert('Netwerkfout: ' + e.message);
        } finally {
            if (btn) { btn.disabled = false; btn.textContent = 'Feed aanmaken'; }
        }
    },

    // ========================================
    // Helicopter tracking
    // ========================================

    async loadHelicopterLayer() {
        if (!this._heliLayer) {
            this._heliLayer = L.layerGroup().addTo(this.map);
        }
        try {
            const res = await fetch('/api/v1/helicopters/positions');
            const json = await res.json();
            const positions = json.data || [];
            positions.forEach(pos => this._updateHeliMarker(pos));
            this._startHeliStaleSweep();
        } catch (e) {
            console.error('Helikopter posities laden mislukt:', e);
        }
    },

    removeHelicopterLayer() {
        if (this._heliSweepTimer) {
            clearInterval(this._heliSweepTimer);
            this._heliSweepTimer = null;
        }
        if (this._heliLayer) {
            this._heliLayer.remove();
            this._heliLayer = null;
        }
        this._heliMarkers.clear();
    },

    // ========================================
    // NDW situaties (verkeer)
    // ========================================

    _ndwIconForType(type) {
        // Brandweer-rood = ongeluk; oranje = pech/file; geel = wegwerkzaamheden
        const config = {
            'Accident':                            { color: '#dc2626', label: 'Ongeluk' },
            'VehicleObstruction':                  { color: '#f97316', label: 'Pech' },
            'AbnormalTraffic':                     { color: '#f97316', label: 'File' },
            'GeneralObstruction':                  { color: '#dc2626', label: 'Obstructie' },
            'RoadOrCarriagewayOrLaneManagement':   { color: '#eab308', label: 'Wegwerkzaamheden' },
        };
        return config[type] || { color: '#6b7280', label: type };
    },

    async _loadNdwSituations(def, entry) {
        try {
            const res = await fetch(def.source_url || '/api/v1/ndw/situations');
            if (!res.ok) return;
            const json = await res.json();
            const records = Array.isArray(json.data) ? json.data : [];

            // Volledige refresh: oude markers vervangen door nieuwe set
            const group = entry.layerInstance;
            if (!group) return;
            group.clearLayers();

            for (const r of records) {
                if (typeof r.lat !== 'number' || typeof r.lng !== 'number') continue;
                const cfg = this._ndwIconForType(r.type);
                const icon = L.divIcon({
                    html: `<div style="width:14px;height:14px;border-radius:50%;background:${cfg.color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4)"></div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 9],
                    className: 'ndw-marker',
                });
                const popup = `<div style="min-width:180px"><strong>${this.esc(cfg.label)}</strong>${r.road ? ' &middot; <span style="color:#666">' + this.esc(r.road) + '</span>' : ''}${r.description ? '<div style="font-size:11px;color:#666;margin-top:4px">' + this.esc(r.description) + '</div>' : ''}<div style="font-size:10px;color:#999;margin-top:4px">NDW &middot; ${this.esc(r.severity || '')}</div></div>`;
                L.marker([r.lat, r.lng], { icon, interactive: true })
                    .bindPopup(popup, { maxWidth: 280 })
                    .addTo(group);
            }
        } catch (e) {
            console.error('NDW situations laden mislukt:', e);
        }
    },

    // Volgorde waarin metrics gestapeld zijn (boven → onder).
    _weatherMetricOrder: ['temperature', 'wind', 'clouds', 'precipitation'],

    // Hoeveel pixels tussen rij-centers in de stack.
    _weatherRowSpacing: 16,

    /**
     * Geef voor elke zichtbare weather_labels laag (in metric-volgorde) zijn
     * positie-index en het totaal. Zo kan de stack zonder gaten gecentreerd
     * rond het stadspunt geplaatst worden, ongeacht welke combinatie aan staat.
     */
    _visibleWeatherMetrics() {
        if (!this.layerRegistry) return [];
        const visible = [];
        for (const m of this._weatherMetricOrder) {
            for (const entry of this.layerRegistry.state.values()) {
                if (entry.def.type !== 'weather_labels') continue;
                if (!entry.isVisible) continue;
                if ((entry.def.meta?.metric || 'temperature') === m) {
                    visible.push(m);
                    break;
                }
            }
        }
        return visible;
    },

    _isPrimaryWeatherLabel(myMetric) {
        const visible = this._visibleWeatherMetrics();
        return visible.length > 0 && visible[0] === myMetric;
    },

    _weatherLabelOffsetY(myMetric) {
        const visible = this._visibleWeatherMetrics();
        const total = visible.length;
        const idx = visible.indexOf(myMetric);
        if (total === 0 || idx < 0) return 0;
        // Stack gecentreerd: idx 0 zit (total-1)/2 stappen boven center.
        return (idx - (total - 1) / 2) * this._weatherRowSpacing;
    },

    _refreshOtherWeatherLabels(excludeSlug) {
        if (!this.layerRegistry) return;
        for (const [slug, entry] of this.layerRegistry.state) {
            if (slug === excludeSlug) continue;
            if (entry.def.type !== 'weather_labels') continue;
            if (!entry.isVisible) continue;
            this._loadWeatherLabels(entry.def, entry);
        }
    },

    async _loadWeatherLabels(def, entry) {
        try {
            const res = await fetch(def.source_url || '/api/v1/weather/cities');
            if (!res.ok) return;
            const json = await res.json();
            const cities = Array.isArray(json.data) ? json.data : [];

            const group = entry.layerInstance;
            if (!group) return;
            group.clearLayers();

            const metric = (def.meta && typeof def.meta.metric === 'string') ? def.meta.metric : 'temperature';
            const showName = this._isPrimaryWeatherLabel(metric);
            const offsetY = this._weatherLabelOffsetY(metric);

            for (const c of cities) {
                if (typeof c.lat !== 'number' || typeof c.lng !== 'number') continue;
                const html = this._weatherLabelHtml(c, metric, showName, offsetY);
                if (!html) continue;
                const icon = L.divIcon({
                    html,
                    iconSize: null,
                    iconAnchor: [0, 0],
                    className: 'wt-marker',
                });
                L.marker([c.lat, c.lng], { icon, interactive: false, keyboard: false })
                    .addTo(group);
            }
        } catch (e) {
            console.error('Weather labels laden mislukt:', e);
        }
    },

    _weatherLabelHtml(c, metric, showName, offsetY) {
        const namePart = showName ? `<span class="wt-name">${this.esc(c.name)}</span>` : '';
        const styleAttr = ` style="transform: translate(-50%, calc(-50% + ${offsetY}px))"`;
        if (metric === 'temperature') {
            if (typeof c.temperature_c !== 'number') return '';
            const cls = this._tempColorClass(c.temperature_c);
            return `<div class="wt-label ${cls}"${styleAttr}><span class="wt-temp">${Math.round(c.temperature_c)}°</span>${namePart}</div>`;
        }
        if (metric === 'wind') {
            if (typeof c.wind_kmh !== 'number') return '';
            const cls = this._windColorClass(c.wind_kmh);
            const deg = (typeof c.wind_deg === 'number') ? c.wind_deg : 0;
            return `<div class="wt-label ${cls}"${styleAttr}><span class="wt-wind-arrow" style="transform:rotate(${deg}deg)">↓</span><span class="wt-temp">${Math.round(c.wind_kmh)} km/u</span>${namePart}</div>`;
        }
        if (metric === 'clouds') {
            if (typeof c.clouds_pct !== 'number') return '';
            const cls = this._cloudsColorClass(c.clouds_pct);
            return `<div class="wt-label ${cls}"${styleAttr}><span class="wt-temp">${Math.round(c.clouds_pct)}% wolken</span>${namePart}</div>`;
        }
        if (metric === 'precipitation') {
            if (typeof c.precipitation_mm !== 'number') return '';
            const cls = this._precipColorClass(c.precipitation_mm);
            const val = c.precipitation_mm > 0 ? c.precipitation_mm.toFixed(1) : '0';
            return `<div class="wt-label ${cls}"${styleAttr}><span class="wt-temp">${val} mm/u</span>${namePart}</div>`;
        }
        return '';
    },

    _tempColorClass(t) {
        if (t <= -5) return 'wt-cold-extreme';
        if (t <= 0) return 'wt-cold';
        if (t <= 8) return 'wt-cool';
        if (t <= 16) return 'wt-mild';
        if (t <= 24) return 'wt-warm';
        if (t <= 30) return 'wt-hot';
        return 'wt-hot-extreme';
    },

    // Beaufort-achtige indeling: <12=zwak, <30=matig, <50=krachtig, <75=storm, ≥75=zware storm
    _windColorClass(kmh) {
        if (kmh < 12) return 'wt-mild';
        if (kmh < 30) return 'wt-cool';
        if (kmh < 50) return 'wt-warm';
        if (kmh < 75) return 'wt-hot';
        return 'wt-hot-extreme';
    },

    _cloudsColorClass(pct) {
        if (pct < 20) return 'wt-mild';
        if (pct < 50) return 'wt-cool';
        if (pct < 80) return 'wt-cold';
        return 'wt-cold-extreme';
    },

    _precipColorClass(mm) {
        if (mm <= 0) return 'wt-mild';
        if (mm < 0.5) return 'wt-cool';
        if (mm < 2.5) return 'wt-cold';
        if (mm < 7.5) return 'wt-cold-extreme';
        return 'wt-hot-extreme';
    },

    handleHeliEvent(data, type) {
        if (!this._heliVisible) return;
        if (type === 'heli.position') {
            if (!this._heliLayer) {
                this._heliLayer = L.layerGroup().addTo(this.map);
            }
            this._updateHeliMarker(data);
        } else if (type === 'heli.gone') {
            const vid = data.vehicle_id;
            const marker = this._heliMarkers.get(vid);
            if (marker) {
                if (this._heliLayer) this._heliLayer.removeLayer(marker);
                this._heliMarkers.delete(vid);
            }
        }
    },

    _startHeliStaleSweep() {
        if (this._heliSweepTimer) return;
        this._heliSweepTimer = setInterval(() => {
            if (!this._heliLayer || !this._heliMarkers.size) return;
            const maxAge = 180; // 3 minuten
            const now = Math.floor(Date.now() / 1000);
            for (const [vid, marker] of this._heliMarkers) {
                const lc = marker._heliLastContact;
                if (lc && (now - lc) > maxAge) {
                    if (this._heliLayer) this._heliLayer.removeLayer(marker);
                    this._heliMarkers.delete(vid);
                }
            }
        }, 30_000); // elke 30s checken
    },

    _updateHeliMarker(pos) {
        const vid = String(pos.vehicle_id);
        const lat = pos.latitude || pos.lat;
        const lng = pos.longitude || pos.lng;
        if (!lat || !lng) return;

        // Filter stale posities client-side (>3 min)
        const lc = pos.last_contact;
        if (lc && (Math.floor(Date.now() / 1000) - lc) > 180) return;

        // Cache vehicle info van initiële API load voor WebSocket updates
        if (!this._heliInfo) this._heliInfo = new Map();
        // De live uitgezonden callsign is gezaghebbend voor de Lifeliner-naam: leid 'm
        // daar direct uit af zodat de getoonde naam ALTIJD matcht met de Callsign. De
        // backend kan via bron-races (lokale readsb vs adsb.fi zonder callsign) op
        // dezelfde vehicle_id een verouderde DB-naam zetten — die mag het label niet
        // bepalen wanneer we een live callsign hebben.
        const csM = pos.callsign ? /^LIFELN?(\d+)$/i.exec(String(pos.callsign).trim()) : null;
        const csName = csM ? 'Lifeliner ' + csM[1] : null;
        if (csName) {
            this._heliInfo.set(vid, { name: csName, discipline: pos.discipline || 'Lifeliner' });
        } else if (pos.vehicle_name && !this._heliInfo.has(vid)) {
            this._heliInfo.set(vid, { name: pos.vehicle_name, discipline: pos.discipline });
        }
        const cached = this._heliInfo.get(vid);

        const heading = pos.heading || 0;
        const alt = pos.altitude_m != null ? Math.round(pos.altitude_m) + 'm' : '';
        const speed = pos.ground_speed_kt != null ? Math.round(pos.ground_speed_kt) + 'kt' : '';
        const name = csName || cached?.name || pos.vehicle_name || vid;
        const disc = pos.discipline || cached?.discipline || 'Lifeliner';
        const color = PIN_COLORS[disc] || '#e74c3c';

        let marker = this._heliMarkers.get(vid);
        if (marker) {
            // Bestaande marker: update positie + heading via DOM (geen setIcon)
            marker.setLatLng([lat, lng]);
            const el = marker.getElement();
            if (el) {
                // Smooth positie-transitie activeren na eerste frame
                if (!el.classList.contains('heli-smooth')) {
                    requestAnimationFrame(() => el.classList.add('heli-smooth'));
                }
                const iconEl = el.querySelector('.heli-icon');
                if (iconEl) iconEl.style.transform = `rotate(${heading}deg)`;
                const dotEl = el.querySelector('.heli-dot');
                if (dotEl) { dotEl.style.background = color; dotEl.style.color = color; }
                // Label bijwerken (bv. zodra de live callsign de naam corrigeert) —
                // werd voorheen alleen bij aanmaken gezet, waardoor het label oud bleef.
                const labelEl = el.querySelector('.heli-label');
                if (labelEl && labelEl.textContent !== name) labelEl.textContent = name;
            }
        } else {
            const icon = L.divIcon({
                className: 'heli-marker',
                html: `<div class="heli-dot" style="background:${color};color:${color}">` +
                      `<div class="heli-icon" style="transform:rotate(${heading}deg)">` +
                      `<svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M3 3l3.5 1L12 2l5.5 2L21 3v2l-3 .5V8h3v2h-3v1l3 4v2l-4-2v2l1 3h-2l-1.5-2h-5L8 21H6l1-3V16l-4 2v-2l3-4V11H3V9h3V5.5L3 5V3zm5 3v5h8V6h-2v3h-1V6h-2v3h-1V6H8z"/></svg>` +
                      `</div></div><div class="heli-label">${this.esc(name)}</div>`,
                iconSize: [40, 36],
                iconAnchor: [20, 18],
            });
            marker = L.marker([lat, lng], { icon, interactive: true, zIndexOffset: 1000 });
            this._heliMarkers.set(vid, marker);
            if (this._heliLayer) this._heliLayer.addLayer(marker);
        }
        marker._heliLastContact = lc || Math.floor(Date.now() / 1000);

        // Popup
        let popup = `<b>${this.esc(name)}</b>`;
        if (alt) popup += `<br>Hoogte: ${alt}`;
        if (speed) popup += `<br>Snelheid: ${speed}`;
        if (pos.callsign) popup += `<br>Callsign: ${this.esc(pos.callsign)}`;
        marker.bindPopup(popup);
    },
};

// Tekstweergave (kaart/split/tekst + ruwe berichtenstroom) leeft in een eigen
// module maar deelt de state van App.
Object.assign(App, TextView);

document.addEventListener('DOMContentLoaded', () => App.init());
